import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Expense,
  ExpenseSection,
  Debt,
  ShoppingListItem,
  Income,
} from "../../lib/types";
import { expenseUtils } from "../../lib";
import { TaxProfile } from "../../lib/taxCalculatorUtils";
import CreatedViaBadge from "../common/CreatedViaBadge";
import {
  transactionService,
  Transaction,
} from "../../services/api/transactionService";
import { linkingService } from "../../services/api/linkingService";
import { shoppingListAPI } from "../../services/api/api";
import { settingsService } from "../../services/settings/settingsService";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SnoozeIcon from "@mui/icons-material/Snooze";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  useTheme,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  TextField,
  Skeleton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ShareIcon from "@mui/icons-material/Share";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import BlockIcon from "@mui/icons-material/Block";
import UndoIcon from "@mui/icons-material/Undo";
import EventIcon from "@mui/icons-material/Event";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SavingsIcon from "@mui/icons-material/Savings";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PushPinIcon from "@mui/icons-material/PushPin";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import LinkIcon from "@mui/icons-material/Link";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import CancelIcon from "@mui/icons-material/Cancel";
import LinkTransactionDialog from "./LinkTransactionDialog";
import { addDays, format } from "date-fns";
import { getTodayLocal, toMidnightUTC } from "../../lib/dateUtils";
import {
  getFutureExpenseSeriesOccurrences,
  getExpenseSeriesOccurrences,
  getExpenseSeriesRootId,
  isRecurringSeriesExpense,
} from "../../lib/expenseSeriesUtils";

type ViewMode = "table" | "card";
type GroupingMode = "time" | "recurrence";
type RecurrenceGroup = "monthly" | "quarterly" | "annually" | "one-time";
type SortColumn =
  | "invoicedDate"
  | "dueDate"
  | "name"
  | "vendor"
  | "paymentAccount"
  | "groupName"
  | "internalCategory"
  | "recurrencePattern"
  | "amount"
  | "pastDueDays"
  | "suspensionDays";
type SortDirection = "asc" | "desc";

interface ExpenseListProps {
  expenses: Record<ExpenseSection, Expense[]>;
  projectionSources?: Expense[]; // Optional: base expenses used for projecting future months
  searchQuery?: string;
  sectionTotals: { section: ExpenseSection; total: number }[];
  isLoading?: boolean; // Optional: show skeletons while data is loading
  onEditExpense: (expense: Expense) => void;
  onDeleteExpense: (expense: Expense) => Promise<void> | void;
  onStopRecurringAfterDate?: (expense: Expense) => Promise<void> | void;
  onMergeExpenseIntoSeries?: (
    expense: Expense,
    seriesRoot: Expense
  ) => Promise<void> | void;
  onDuplicateExpense?: (expense: Expense) => void;
  onShareExpense?: (expense: Expense) => void;
  onSkipExpense?: (expense: Expense) => void;
  onPostponeExpense?: (
    expenseId: string,
    until: string | null | "clear"
  ) => void;
  onPostponeDebt?: (debtId: string, until: string | null | "clear") => void;
  onRescheduleExpense?: (
    expense: Expense,
    newDate: Date,
    scope?: "single" | "all_future"
  ) => Promise<void> | void;
  onResetOccurrences?: (expense: Expense) => Promise<void> | void;
  onMarkAsPaid: (
    expense: Expense,
    paymentDate: Date,
    paymentAccount: string,
    linkedTransactionId?: string
  ) => Promise<boolean | void> | boolean | void;
  onExpenseSync?: (expense: Expense) => void;
  onLinkAsRecurring?: (expenseIds: string[]) => void;
  onMergeExpenses?: (expenseIds: string[]) => void;
  onLinkTransaction?: (
    expense: Expense,
    transactionId: string | null
  ) => Promise<void> | void;
  pinnedExpenseIds?: Set<string>;
  onTogglePinExpense?: (expense: Expense) => void;
  debts?: Debt[]; // Optional debts for dynamic amount calculation
  income?: Income[];
  taxProfiles?: TaxProfile[];
  viewMode?: ViewMode; // Optional controlled view mode
  onViewModeChange?: (mode: ViewMode) => void; // Optional callback for view mode changes
  allExpanded?: boolean; // Optional: expand all accordions
  expandedSection?: ExpenseSection | null; // Optional: externally controlled expanded section
  onSectionChange?: (section: ExpenseSection | null) => void; // Optional: callback when section changes
  groupingMode?: GroupingMode; // Optional: grouping mode (time or recurrence)
  highlightedExpenseId?: string | null; // Optional: ID of expense to highlight with pulse animation
  onPausedIdsChange?: (pausedIds: Set<string>) => void; // Optional: callback when paused expected IDs change
  readOnly?: boolean; // Optional: hide all action buttons (for shared views)
}

// Function to get dynamic section titles - Updated for UTC timezone fix
const getSectionTitles = (): Partial<Record<ExpenseSection, string>> => ({
  received: "Received",
  overdue: "Overdue",
  today: "Today",
  tomorrow: "Tomorrow",
  thisWeek: "Within 7 Days",
  next10Days: "Within 10 Days",
  thisMonth: `This Month (${expenseUtils.getCurrentMonthName()})`,
  nextMonth: `Next Month (${expenseUtils.getNextMonthName()})`,
  expected: "Within 30 Days",
});

// Helper to get future month name from offset (1 = next month, 2 = month after, etc.)
const formatMonthYearUTC = (date: Date): string => {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  }).format(date);
};

const getFutureMonthName = (monthOffset: number): string => {
  // getTodayLocal() returns a midnight-UTC Date representing "today" in the user's timezone.
  // Use UTC month arithmetic to avoid local timezone/DST shifting the displayed month.
  const base = getTodayLocal();
  const target = new Date(
    Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + monthOffset, 1)
  );
  return formatMonthYearUTC(target);
};

const isSameUtcDay = (date: Date, target: Date): boolean =>
  date.getUTCFullYear() === target.getUTCFullYear() &&
  date.getUTCMonth() === target.getUTCMonth() &&
  date.getUTCDate() === target.getUTCDate();

const toUtcDateOnly = (date: Date): Date =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );

const hexToRgba = (hex: string, alpha: number) => {
  const sanitized = hex.replace("#", "");
  const full =
    sanitized.length === 3
      ? sanitized
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : sanitized;
  const int = parseInt(full, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const sectionColors: Partial<Record<ExpenseSection, string>> = {
  received: "#9CA3AF", // Muted gray
  overdue: "#E53E3E",
  today: "#DD6B20",
  tomorrow: "#D69E2E",
  thisWeek: "#3182CE",
  next10Days: "#718096",
  thisMonth: "#38A169",
  nextMonth: "#6B46C1",
  expected: "#B83280",
};

const COLLAPSIBLE_SECTIONS: ExpenseSection[] = [
  "overdue",
  "today",
  "tomorrow",
  "thisWeek",
  "next10Days",
  "thisMonth",
  "nextMonth",
];

const getNeutralDateStyle = (isDarkMode: boolean) => ({
  backgroundColor: "transparent",
  color: isDarkMode ? "#a0aec0" : "#4a5568",
});

const getExpectedDueBadgeStyle = (
  groupKey: string | null,
  isDarkMode: boolean
): { backgroundColor: string; color: string } => {
  switch (groupKey) {
    case "overdue":
      return { backgroundColor: "#E53E3E", color: "#FFFFFF" };
    case "today":
      return { backgroundColor: "#DD6B20", color: "#FFFFFF" };
    case "tomorrow":
      return { backgroundColor: "#D69E2E", color: "#1A202C" };
    case "next7":
      return { backgroundColor: "#3182CE", color: "#FFFFFF" };
    case "next10":
      return { backgroundColor: "#718096", color: "#FFFFFF" };
    case "remainder":
    case "paused":
    default:
      return {
        backgroundColor: isDarkMode ? "#2D3748" : "#E2E8F0",
        color: isDarkMode ? "#E2E8F0" : "#2D3748",
      };
  }
};

const getDueDateStyle = (
  expense: Expense,
  styleSection: ExpenseSection,
  expectedGroupKey: string | null,
  isDarkMode: boolean
): { backgroundColor: string; color: string } => {
  if (styleSection === "overdue") {
    return getExpectedDueBadgeStyle("overdue", isDarkMode);
  }
  if (styleSection === "expected") {
    return getExpectedDueBadgeStyle(expectedGroupKey, isDarkMode);
  }
  return getNeutralDateStyle(isDarkMode);
};

// Get the number of days an expense is overdue (timezone-aware)
const getDaysOverdue = (dueDate?: Date): number => {
  if (!dueDate) return 0;

  const today = getTodayLocal();
  const due = toMidnightUTC(dueDate);
  const diffMs = today.getTime() - due.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

const getDaysUntilDue = (dueDate?: Date): number | null => {
  if (!dueDate) return null;
  const todayUTC = getTodayLocal();
  const dueUTC = toMidnightUTC(dueDate);
  const diffMs = dueUTC.getTime() - todayUTC.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

const getSuspensionDateStyle = (
  expense: Expense,
  isDarkMode: boolean
): { backgroundColor: string; color: string } => {
  if (
    expense.suspensionPeriodValue === undefined ||
    !expense.suspensionPeriodUnit
  ) {
    return getNeutralDateStyle(isDarkMode);
  }

  const daysUntilSuspension = expenseUtils.getDaysUntilSuspension(
    expense.dueDate,
    expense.suspensionPeriodValue,
    expense.suspensionPeriodUnit
  );

  if (daysUntilSuspension === null) {
    return getNeutralDateStyle(isDarkMode);
  }

  const days = Math.max(0, daysUntilSuspension);

  if (days <= 3) {
    return { backgroundColor: "#E53E3E", color: "#FFFFFF" };
  }
  if (days <= 10) {
    return { backgroundColor: "#9B2C2C", color: "#FFFFFF" };
  }
  if (days <= 21) {
    return { backgroundColor: "#F56565", color: "#742A2A" };
  }
  if (days <= 30) {
    return { backgroundColor: "#FEB2B2", color: "#9B2C2C" };
  }
  if (days <= 45) {
    return { backgroundColor: "#FED7D7", color: "#9B2C2C" };
  }
  if (days <= 60) {
    return { backgroundColor: "#FEE2E2", color: "#9B2C2C" };
  }
  if (days <= 90) {
    return { backgroundColor: "#FEF2F2", color: "#9B2C2C" };
  }

  return getNeutralDateStyle(isDarkMode);
};

export const getExpenseSuspensionDays = (expense: Expense): number | null =>
  expenseUtils.getDaysUntilSuspension(
    expense.dueDate,
    expense.suspensionPeriodValue,
    expense.suspensionPeriodUnit
  );

export const isExpenseSuspended = (expense: Expense): boolean => {
  const daysUntilSuspension = getExpenseSuspensionDays(expense);
  return daysUntilSuspension !== null && daysUntilSuspension < 0;
};

export const getSuspensionBadgeLabel = (
  daysUntilSuspension: number | null
): string | number => {
  if (daysUntilSuspension === null) return "";
  return daysUntilSuspension;
};

// Format date as "Jan. 1st, 2026"
const formatDueDate = (date: Date): string => {
  const months = [
    "Jan.",
    "Feb.",
    "Mar.",
    "Apr.",
    "May",
    "Jun.",
    "Jul.",
    "Aug.",
    "Sep.",
    "Oct.",
    "Nov.",
    "Dec.",
  ];
  const month = months[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();

  // Get ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
  const getOrdinalSuffix = (n: number): string => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };

  return `${month} ${day}${getOrdinalSuffix(day)}, ${year}`;
};

const formatCompactDate = (date: Date): string => {
  const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${date.getUTCDate()}`.padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

const ExpenseList: React.FC<ExpenseListProps> = ({
  expenses,
  projectionSources,
  searchQuery = "",
  sectionTotals,
  onEditExpense,
  onDeleteExpense,
  onPostponeExpense,
  onPostponeDebt,
  onStopRecurringAfterDate,
  onMergeExpenseIntoSeries,
  onDuplicateExpense,
  onShareExpense,
  onSkipExpense,
  onRescheduleExpense,
  onResetOccurrences,
  onMarkAsPaid,
  onExpenseSync: _onExpenseSync,
  onLinkAsRecurring,
  onMergeExpenses,
  onLinkTransaction,
  debts = [],
  income = [],
  taxProfiles = [],
  viewMode: controlledViewMode,
  allExpanded = false,
  expandedSection,
  onSectionChange,
  groupingMode = "time",
  highlightedExpenseId = null,
  pinnedExpenseIds,
  onTogglePinExpense,
  onPausedIdsChange,
  readOnly = false,
  isLoading = false,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  // Theme-aware palette for the hand-rolled "Link Transaction" dropdown in the
  // Mark-as-Paid modal, so option rows stay legible in both light and dark mode.
  const txnPicker = {
    optionBg: isDarkMode ? "#1a1a1a" : "#ffffff",
    optionAiBg: isDarkMode ? "#12263a" : "#f0f8ff",
    optionHoverBg: isDarkMode ? "#2d2d2d" : "#f5f5f5",
    optionAiHoverBg: isDarkMode ? "#1c3a55" : "#e6f3ff",
    text: theme.palette.text.primary,
    secondary: theme.palette.text.secondary,
    accent: isDarkMode ? "#4da6ff" : "#0066cc",
    accentBg: isDarkMode ? "rgba(77,166,255,0.15)" : "#e6f3ff",
    divider: theme.palette.divider,
  };
  const sectionTitles = getSectionTitles();

  // View mode state - use controlled prop if provided, otherwise use internal state
  const [internalViewMode] = useState<ViewMode>(() => {
    const saved = sessionStorage.getItem("expenses_view_mode");
    return (saved as ViewMode) || "table";
  });

  // Use controlled viewMode if provided, otherwise use internal state
  const viewMode = controlledViewMode ?? internalViewMode;

  // State for additional future months (beyond nextMonth)
  const [futureMonthsCount, setFutureMonthsCount] = useState<number>(0);
  // Postpone popover (matches the Recurring Expenses modal).
  const [postponeAnchor, setPostponeAnchor] = useState<HTMLElement | null>(
    null
  );
  const [postponeTarget, setPostponeTarget] = useState<{
    type: "debt" | "expense";
    id: string;
  } | null>(null);
  const [postponeDate, setPostponeDate] = useState("");
  const applyPostpone = (until: string | null | "clear") => {
    if (postponeTarget) {
      if (postponeTarget.type === "debt" && onPostponeDebt)
        onPostponeDebt(postponeTarget.id, until);
      else if (postponeTarget.type === "expense" && onPostponeExpense)
        onPostponeExpense(postponeTarget.id, until);
    }
    setPostponeAnchor(null);
  };
  const openPostpone = (e: React.MouseEvent<HTMLElement>, expense: Expense) => {
    setPostponeAnchor(e.currentTarget);
    setPostponeTarget(
      expense.linkedDebtId
        ? { type: "debt", id: expense.linkedDebtId }
        : { type: "expense", id: expense.id }
    );
    setPostponeDate("");
  };
  const prevFutureMonthsCountRef = useRef<number>(0);

  const renderLoadingCountBadge = useCallback(
    (color: string) => (
      <span style={badgeStyle(color)}>
        <Skeleton
          variant="rounded"
          width={18}
          height={14}
          sx={{ display: "inline-block" }}
        />
      </span>
    ),
    []
  );

  const renderLoadingTotal = useCallback(
    () => (
      <Skeleton variant="text" width={96} sx={{ display: "inline-block" }} />
    ),
    []
  );

  const persistFutureMonthsCount = useCallback(
    async (count: number) => {
      if (readOnly) return;
      try {
        await settingsService.saveUserSettings({
          expenses_future_months_count: count,
        });
      } catch (error) {
        console.error("[ExpenseList] Failed to persist future months count", {
          count,
          error,
        });
      }
    },
    [readOnly]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await settingsService.getUserSettings();
        const savedCount = Number(settings.expenses_future_months_count || 0);

        // Legacy per-origin storage (migrate to DB once, then clear).
        const legacyDefault = Number(
          localStorage.getItem("expenses_future_months_default") || 0
        );
        const legacySession = Number(
          sessionStorage.getItem("expenses_future_months_count") || 0
        );
        const legacyCount = Math.max(legacyDefault, legacySession);

        const initial = Math.max(savedCount, legacyCount);
        if (!cancelled) {
          setFutureMonthsCount(initial);
        }

        if (legacyCount > 0) {
          localStorage.removeItem("expenses_future_months_default");
          sessionStorage.removeItem("expenses_future_months_count");
        }

        if (legacyCount > savedCount) {
          await persistFutureMonthsCount(legacyCount);
        }
      } catch (error) {
        console.error("[ExpenseList] Failed to load user settings", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [persistFutureMonthsCount]);

  // State for collapsed sections - start all collapsed, persist in sessionStorage
  const [collapsedSections, setCollapsedSections] = useState<
    Set<ExpenseSection>
  >(() => {
    // Try to restore from sessionStorage
    const saved = sessionStorage.getItem("expenses_collapsed_sections");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return new Set(parsed as ExpenseSection[]);
      } catch {
        // If parse fails, default to all collapsed
      }
    }
    // Default: all sections collapsed
    return new Set(COLLAPSIBLE_SECTIONS);
  });

  // Future month accordions should start collapsed by default.
  // When new months are added, collapse only the newly-added sections (do not override user toggles).
  useEffect(() => {
    const prev = prevFutureMonthsCountRef.current;
    if (futureMonthsCount > prev) {
      setCollapsedSections((current) => {
        const next = new Set(current);
        // futureMonthsCount is "extra months beyond next month".
        // Accordion keys use month offsets: 2 = month after next, 3 = 2 months after next, etc.
        for (
          let extraIndex = prev + 1;
          extraIndex <= futureMonthsCount;
          extraIndex += 1
        ) {
          next.add(`future-${extraIndex + 1}` as unknown as ExpenseSection);
        }
        sessionStorage.setItem(
          "expenses_collapsed_sections",
          JSON.stringify([...next])
        );
        return next;
      });
    }
    prevFutureMonthsCountRef.current = futureMonthsCount;
  }, [futureMonthsCount]);

  // State for paid modal
  const [showPaidModal, setShowPaidModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [paymentDate, setPaymentDate] = useState<string>("");
  const [paymentTime, setPaymentTime] = useState<string>("");
  const [paymentAccount, setPaymentAccount] = useState<string>("");

  // State for transaction dropdown
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<string>("");
  const [transactionSearch, setTransactionSearch] = useState<string>("");
  const [showTransactionDropdown, setShowTransactionDropdown] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // State for collapsed expense groups within sections
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem("expenses_collapsed_groups");
    if (saved) {
      try {
        return new Set(JSON.parse(saved) as string[]);
      } catch {
        // Default to all collapsed
      }
    }
    return new Set<string>();
  });

  // Shopping list accordion state
  const [expandedShoppingExpenses, setExpandedShoppingExpenses] = useState<
    Set<string>
  >(new Set());
  const [shoppingListItemsCache, setShoppingListItemsCache] = useState<
    Record<string, ShoppingListItem[]>
  >({});
  const [shoppingListLoading, setShoppingListLoading] = useState<Set<string>>(
    new Set()
  );
  const [shoppingItemActionLoadingIds, setShoppingItemActionLoadingIds] =
    useState<Set<string>>(new Set());
  const [
    shoppingItemDeleteAnimatingOutIds,
    setShoppingItemDeleteAnimatingOutIds,
  ] = useState<Set<string>>(new Set());
  const [shoppingItemHiddenIds, setShoppingItemHiddenIds] = useState<
    Set<string>
  >(new Set());

  // Shopping list modal state
  const [shoppingModalExpense, setShoppingModalExpense] =
    useState<Expense | null>(null);
  const [shoppingModalShowPaid, setShoppingModalShowPaid] = useState(false);
  const [shoppingModalShowSkipped, setShoppingModalShowSkipped] =
    useState(false);
  const [shoppingModalDraftItem, setShoppingModalDraftItem] = useState<{
    title: string;
    vendor: string;
    link: string;
    price: string;
  }>({ title: "", vendor: "", link: "", price: "" });
  const [occurrencesModalExpense, setOccurrencesModalExpense] =
    useState<Expense | null>(null);
  const [stopRecurringExpenseId, setStopRecurringExpenseId] = useState<
    string | null
  >(null);
  const [mergeSeriesExpense, setMergeSeriesExpense] = useState<Expense | null>(
    null
  );
  const [mergeSeriesQuery, setMergeSeriesQuery] = useState("");
  const [mergeSeriesTargetId, setMergeSeriesTargetId] = useState<string | null>(
    null
  );

  const toggleShoppingAccordion = useCallback(
    async (expense: Expense) => {
      const expenseId = expense.id;
      const listId = expense.linkedShoppingListId;
      if (!listId) return;

      setExpandedShoppingExpenses((prev) => {
        const next = new Set(prev);
        if (next.has(expenseId)) {
          next.delete(expenseId);
        } else {
          next.add(expenseId);
          // Fetch items if not cached
          if (!shoppingListItemsCache[listId]) {
            setShoppingListLoading((prev2) => new Set(prev2).add(listId));
            shoppingListAPI
              .getById(listId)
              .then((list) => {
                const items = list?.items || [];
                console.debug(
                  "[ExpenseList] Loaded shopping list items:",
                  items.length
                );
                setShoppingListItemsCache((prev2) => ({
                  ...prev2,
                  [listId]: items,
                }));
                setShoppingListLoading((prev2) => {
                  const next2 = new Set(prev2);
                  next2.delete(listId);
                  return next2;
                });
              })
              .catch((err) => {
                console.error(
                  "[ExpenseList] Failed to load shopping list:",
                  err
                );
                setShoppingListLoading((prev2) => {
                  const next2 = new Set(prev2);
                  next2.delete(listId);
                  return next2;
                });
              });
          }
        }
        return next;
      });
    },
    [shoppingListItemsCache]
  );

  const openShoppingModal = useCallback(
    (expense: Expense) => {
      const listId = expense.linkedShoppingListId;
      if (!listId) return;
      setShoppingModalExpense(expense);
      setShoppingModalShowPaid(false);
      setShoppingModalShowSkipped(false);
      setShoppingModalDraftItem({ title: "", vendor: "", link: "", price: "" });
      // Fetch items if not cached
      if (!shoppingListItemsCache[listId]) {
        setShoppingListLoading((prev) => new Set(prev).add(listId));
        shoppingListAPI
          .getById(listId)
          .then((list) => {
            const items = list?.items || [];
            console.debug(
              "[ExpenseList] Modal: loaded shopping list items:",
              items.length
            );
            setShoppingListItemsCache((prev) => ({ ...prev, [listId]: items }));
            setShoppingListLoading((prev) => {
              const next = new Set(prev);
              next.delete(listId);
              return next;
            });
          })
          .catch((err) => {
            console.error(
              "[ExpenseList] Modal: failed to load shopping list:",
              err
            );
            setShoppingListLoading((prev) => {
              const next = new Set(prev);
              next.delete(listId);
              return next;
            });
          });
      }
    },
    [shoppingListItemsCache]
  );

  const visibleExpensePool = useMemo(() => {
    const deduped = new Map<string, Expense>();
    Object.values(expenses)
      .flat()
      .forEach((expense) => {
        deduped.set(expense.id, expense);
      });
    (projectionSources || []).forEach((expense) => {
      deduped.set(expense.id, expense);
    });
    return Array.from(deduped.values());
  }, [expenses, projectionSources]);

  // Recurring-series badge: for every visible row that belongs to a recurring
  // series, collect the row ids of its PAST/PRESENT siblings (due date <= end of
  // today, local). Future-dated instances are excluded from both the count and
  // the highlight set. The grouping key is the series root id
  // (`originalId || id`) used elsewhere in the codebase.
  const seriesPastPresentIds = useMemo(() => {
    const today = getTodayLocal(); // midnight-UTC representing "today" locally
    const map = new Map<string, string[]>();
    visibleExpensePool.forEach((expense) => {
      if (!isRecurringSeriesExpense(expense)) return;
      if (!expense.dueDate) return;
      // "today or before": normalize the due date to a UTC day and compare to today.
      if (toUtcDateOnly(expense.dueDate).getTime() > today.getTime()) return;
      const rootId = getExpenseSeriesRootId(expense);
      const list = map.get(rootId) || [];
      list.push(expense.id);
      map.set(rootId, list);
    });
    return map;
  }, [visibleExpensePool]);

  // Set of row ids currently highlighted via a recurring-series badge click.
  // Held for 10s, then a CSS transition slow-fades the highlight out.
  const [seriesHighlightIds, setSeriesHighlightIds] = useState<Set<string>>(
    () => new Set()
  );
  const seriesHighlightTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (seriesHighlightTimeoutRef.current !== null) {
        window.clearTimeout(seriesHighlightTimeoutRef.current);
      }
    };
  }, []);

  const handleSeriesBadgeClick = useCallback(
    (rootId: string) => {
      const ids = seriesPastPresentIds.get(rootId) || [];
      if (ids.length === 0) return;
      if (seriesHighlightTimeoutRef.current !== null) {
        window.clearTimeout(seriesHighlightTimeoutRef.current);
      }
      setSeriesHighlightIds(new Set(ids));
      // Hold the highlight for 10s, then clear it. Removing the
      // `expense-series-highlighted` class triggers the slow CSS fade-out.
      seriesHighlightTimeoutRef.current = window.setTimeout(() => {
        setSeriesHighlightIds(new Set());
        seriesHighlightTimeoutRef.current = null;
      }, 10000);
    },
    [seriesPastPresentIds]
  );

  const occurrenceSeries = useMemo(() => {
    if (!occurrencesModalExpense) return [];
    return getExpenseSeriesOccurrences(
      visibleExpensePool,
      occurrencesModalExpense
    );
  }, [occurrencesModalExpense, visibleExpensePool]);

  useEffect(() => {
    if (!occurrencesModalExpense) return;
    console.debug("[ExpenseList] Open occurrences modal", {
      expenseId: occurrencesModalExpense.id,
      totalOccurrences: occurrenceSeries.length,
    });
  }, [occurrencesModalExpense, occurrenceSeries.length]);

  const handleStopRecurringAfterDate = useCallback(
    async (expense: Expense) => {
      if (!onStopRecurringAfterDate) return;
      if (stopRecurringExpenseId === expense.id) return;
      console.debug("[ExpenseList] Stop recurring after date clicked", {
        expenseId: expense.id,
        dueDate: expense.dueDate,
      });
      setStopRecurringExpenseId(expense.id);
      try {
        await onStopRecurringAfterDate(expense);
        setOccurrencesModalExpense((current) =>
          current?.id === expense.id ? null : current
        );
      } catch (error) {
        console.error("[ExpenseList] Stop recurring after date failed", {
          expenseId: expense.id,
          error,
        });
        alert("Failed to stop recurring expense after this date.");
      } finally {
        setStopRecurringExpenseId((current) =>
          current === expense.id ? null : current
        );
      }
    },
    [onStopRecurringAfterDate, stopRecurringExpenseId]
  );

  const handleMergeIntoSeries = useCallback(
    async (seriesRoot: Expense) => {
      if (!mergeSeriesExpense || !onMergeExpenseIntoSeries) return;
      if (mergeSeriesTargetId === seriesRoot.id) return;
      console.debug("[ExpenseList] Merge one-time expense into series", {
        expenseId: mergeSeriesExpense.id,
        seriesRootId: seriesRoot.id,
      });
      setMergeSeriesTargetId(seriesRoot.id);
      try {
        await onMergeExpenseIntoSeries(mergeSeriesExpense, seriesRoot);
        setMergeSeriesExpense(null);
        setMergeSeriesQuery("");
      } catch (error) {
        console.error("[ExpenseList] Merge expense into series failed", {
          expenseId: mergeSeriesExpense.id,
          seriesRootId: seriesRoot.id,
          error,
        });
        alert("Failed to merge expense into recurring series.");
      } finally {
        setMergeSeriesTargetId((current) =>
          current === seriesRoot.id ? null : current
        );
      }
    },
    [mergeSeriesExpense, mergeSeriesTargetId, onMergeExpenseIntoSeries]
  );

  // Effect to handle allExpanded prop
  React.useEffect(() => {
    if (allExpanded) {
      // Expand all: clear the collapsed sections set
      setCollapsedSections(new Set());
    } else {
      // When allExpanded is turned off, collapse all sections
      setCollapsedSections(new Set(COLLAPSIBLE_SECTIONS));
    }
  }, [allExpanded]);

  // Effect to handle expandedSection prop (from card clicks)
  React.useEffect(() => {
    if (expandedSection !== undefined) {
      setCollapsedSections((prev) => {
        // Preserve future month collapse state (future-2, future-3, ...).
        const preservedFuture = [...prev].filter((key) =>
          String(key).startsWith("future-")
        ) as ExpenseSection[];

        // Create a set with main sections collapsed.
        const nextCollapsed = new Set<ExpenseSection>([
          ...COLLAPSIBLE_SECTIONS,
          ...preservedFuture,
        ]);

        // If a section is specified, expand only that main section.
        if (expandedSection !== null) {
          nextCollapsed.delete(expandedSection);
          if (expandedSection !== "expected") {
            nextCollapsed.add("expected");
          }
        }
        return nextCollapsed;
      });
    }
  }, [expandedSection]);

  // Sort state - default to suspension days ascending (soonest suspension first)
  const [sortColumn, setSortColumn] = useState<SortColumn>("suspensionDays");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Handle column header click for sorting
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New column, default to ascending (except amount which defaults to descending)
      setSortColumn(column);
      setSortDirection(column === "amount" ? "desc" : "asc");
    }
  };

  // Bulk selection state
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<Set<string>>(
    new Set()
  );
  const [lastSelectedExpenseId, setLastSelectedExpenseId] = useState<
    string | null
  >(null);
  // Which row's action menu is currently slid open (click-toggled via the
  // per-row kebab icon). Only one row's actions are open at a time.
  const [actionsOpenId, setActionsOpenId] = useState<string | null>(null);

  // Close the open action menu when clicking anywhere outside of an action
  // strip or its toggle button.
  useEffect(() => {
    if (!actionsOpenId) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.closest(".expense-row-actions") ||
          target.closest(".expense-row-actions-toggle"))
      ) {
        return;
      }
      setActionsOpenId(null);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [actionsOpenId]);
  const [showBulkDuplicateModal, setShowBulkDuplicateModal] = useState(false);
  const [bulkDuplicateMonth, setBulkDuplicateMonth] = useState<number>(() => {
    const now = new Date();
    return now.getMonth() + 1; // 1-12
  });
  const [bulkDuplicateYear, setBulkDuplicateYear] = useState<number>(() => {
    return new Date().getFullYear();
  });
  const [bulkDuplicateRecurring, setBulkDuplicateRecurring] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [skipStrikethroughIds, setSkipStrikethroughIds] = useState<Set<string>>(
    new Set()
  );
  const [skipAnimatingOutIds, setSkipAnimatingOutIds] = useState<Set<string>>(
    new Set()
  );
  const [skipHiddenIds, setSkipHiddenIds] = useState<Set<string>>(new Set());
  const skipTimeoutsRef = useRef<Record<string, number>>({});
  const [paidAnimatingOutIds, setPaidAnimatingOutIds] = useState<Set<string>>(
    new Set()
  );
  const [paidHiddenIds, setPaidHiddenIds] = useState<Set<string>>(new Set());
  // Rows currently persisting a Mark-as-Paid save. While saving, the row
  // renders struck-through, faded, and "breathing" (pulse) optimistically.
  const [paidSavingIds, setPaidSavingIds] = useState<Set<string>>(new Set());
  const paidTimeoutsRef = useRef<Record<string, number>>({});
  const [deleteLoadingIds, setDeleteLoadingIds] = useState<Set<string>>(
    new Set()
  );
  const [resetLoadingIds, setResetLoadingIds] = useState<Set<string>>(
    new Set()
  );
  // Linked-transaction picker modal (opened from the "Linked" column).
  const [linkTxnExpense, setLinkTxnExpense] = useState<Expense | null>(null);
  // After linking a transaction whose amount is less than the expense
  // amount, prompt the user before marking the expense as paid.
  const [linkPaidPrompt, setLinkPaidPrompt] = useState<{
    expense: Expense;
    transactionId: string;
    txnAmount: number;
    txnDate?: string;
    diff: number;
  } | null>(null);
  const [deleteAnimatingOutIds, setDeleteAnimatingOutIds] = useState<
    Set<string>
  >(new Set());
  const [deleteHiddenIds, setDeleteHiddenIds] = useState<Set<string>>(
    new Set()
  );
  const deleteTimeoutsRef = useRef<Record<string, number>>({});
  const expectedStatusLoggedRef = useRef<Set<string>>(new Set());
  const [rescheduleExpense, setRescheduleExpense] = useState<Expense | null>(
    null
  );
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleScope, setRescheduleScope] = useState<
    "single" | "all_future"
  >("single");
  const [rescheduleError, setRescheduleError] = useState("");
  const [isRescheduling, setIsRescheduling] = useState(false);

  useEffect(() => {
    const timeouts = skipTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
    };
  }, []);

  useEffect(() => {
    const timeouts = paidTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
    };
  }, []);

  useEffect(() => {
    const timeouts = deleteTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
    };
  }, []);

  const scheduleDeleteTimeout = (key: string, ms: number, fn: () => void) => {
    const timeouts = deleteTimeoutsRef.current;
    if (timeouts[key]) {
      window.clearTimeout(timeouts[key]);
    }
    const timeoutId = window.setTimeout(() => {
      delete timeouts[key];
      fn();
    }, ms);
    timeouts[key] = timeoutId;
  };

  const handleDeleteClick = async (expense: Expense) => {
    if (!expense?.id) return;
    if (deleteLoadingIds.has(expense.id)) return;

    console.log("[ExpenseList] Delete clicked", {
      id: expense.id,
      name: expense.name,
    });

    setDeleteLoadingIds((prev) => new Set(prev).add(expense.id));

    try {
      await onDeleteExpense(expense);

      setDeleteAnimatingOutIds((prev) => new Set(prev).add(expense.id));
      scheduleDeleteTimeout(`delete-out-${expense.id}`, 220, () => {
        setDeleteHiddenIds((prev) => new Set(prev).add(expense.id));
        setSelectedExpenseIds((prev) => {
          const next = new Set(prev);
          next.delete(expense.id);
          return next;
        });
        setDeleteLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(expense.id);
          return next;
        });
        setDeleteAnimatingOutIds((prev) => {
          const next = new Set(prev);
          next.delete(expense.id);
          return next;
        });
      });
    } catch (error) {
      if (error instanceof Error && error.message === "DELETE_CANCELLED") {
        setDeleteLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(expense.id);
          return next;
        });
        setDeleteAnimatingOutIds((prev) => {
          const next = new Set(prev);
          next.delete(expense.id);
          return next;
        });
        return;
      }
      console.error("[ExpenseList] Delete failed", { id: expense.id, error });
      setDeleteLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
      setDeleteAnimatingOutIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
      alert("Failed to delete expense. Check console for details.");
    }
  };

  const handleResetClick = async (expense: Expense) => {
    if (!expense?.id) return;
    if (!onResetOccurrences) return;
    if (resetLoadingIds.has(expense.id)) return;

    console.log("[ExpenseList] Reset occurrences clicked", {
      id: expense.id,
      name: expense.name,
      originalId: expense.originalId,
      isRecurring: expense.isRecurring,
      isRecurringInstance: expense.isRecurringInstance,
    });

    setResetLoadingIds((prev) => new Set(prev).add(expense.id));
    try {
      await onResetOccurrences(expense);
    } catch (error) {
      console.error("[ExpenseList] Reset occurrences failed", {
        expenseId: expense.id,
        error,
      });
      alert("Failed to reset occurrences. Check console for details.");
    } finally {
      setResetLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
    }
  };

  const getEffectiveAmount = useCallback(
    (expense: Expense): number => {
      let base: number;
      if (expense.linkedShoppingListId) {
        const cachedItems =
          shoppingListItemsCache[expense.linkedShoppingListId];
        base = cachedItems
          ? expenseUtils.getRemainingShoppingListTotal(cachedItems)
          : expenseUtils.getEffectiveExpenseAmount(expense, debts, {
              income,
              taxProfiles,
            });
      } else {
        base = expenseUtils.getEffectiveExpenseAmount(expense, debts, {
          income,
          taxProfiles,
        });
      }
      // Subtract recorded partial payments so the row shows the balance
      // remaining. Paid rows already drop out of the unpaid totals.
      if (!expense.isPaid && expense.paymentsTotal) {
        return Math.max(0, base - Number(expense.paymentsTotal));
      }
      return base;
    },
    [debts, income, shoppingListItemsCache, taxProfiles]
  );

  const getDisplayName = useCallback(
    (expense: Expense): string => {
      // A debt-linked expense IS the debt: show the creditor as the title.
      if (expense.linkedDebtId && debts) {
        const linkedDebt = debts.find((d) => d.id === expense.linkedDebtId);
        const creditor = linkedDebt?.creditor || linkedDebt?.account;
        if (creditor) return creditor;
      }
      const normalizedName = (() => {
        const shouldNormalize =
          Boolean(expense.isRecurringInstance) ||
          Boolean(expense.isRecurring) ||
          Boolean(expense.originalId);
        if (!shouldNormalize) return expense.name;
        const stripped = expense.name
          // Historical suffix we don't want in UI: "Payment 11"
          .replace(/\s+payment\s+\d+$/i, "")
          // Generic numeric suffix: " 11"
          .replace(/\s+\d+$/, "")
          .trim();
        return stripped || expense.name;
      })();
      const calculationType = expense.calculationType || "fixed";
      if (calculationType !== "fixed") {
        return `% ${normalizedName}`;
      }
      return normalizedName;
    },
    [debts]
  );

  const normalizedSearchQuery = useMemo(
    () => searchQuery.trim().toLowerCase(),
    [searchQuery]
  );

  const isSearchActive = normalizedSearchQuery.length > 0;

  const matchesSearchQuery = useCallback(
    (expense: Expense) => {
      if (!normalizedSearchQuery) return true;
      return [
        getDisplayName(expense),
        expense.name,
        expense.vendor,
        expense.internalCategory,
        expense.recurrencePattern,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearchQuery)
        );
    },
    [getDisplayName, normalizedSearchQuery]
  );

  // Sort expenses based on current sort column and direction
  const sortExpenses = useCallback(
    (expenseList: Expense[]): Expense[] => {
      return [...expenseList].sort((a, b) => {
        let comparison = 0;

        switch (sortColumn) {
          case "invoicedDate": {
            const aDate = a.invoicedDate ? new Date(a.invoicedDate) : null;
            const bDate = b.invoicedDate ? new Date(b.invoicedDate) : null;
            if (!aDate && !bDate) {
              comparison = 0;
            } else if (!aDate) {
              comparison = 1;
            } else if (!bDate) {
              comparison = -1;
            } else {
              comparison = aDate.getTime() - bDate.getTime();
            }
            break;
          }
          case "dueDate":
            {
              const aDate = a.dueDate ? new Date(a.dueDate) : null;
              const bDate = b.dueDate ? new Date(b.dueDate) : null;
              const aTime =
                aDate && !Number.isNaN(aDate.getTime())
                  ? toUtcDateOnly(aDate).getTime()
                  : null;
              const bTime =
                bDate && !Number.isNaN(bDate.getTime())
                  ? toUtcDateOnly(bDate).getTime()
                  : null;
              if (aTime === null && bTime === null) {
                comparison = 0;
              } else if (aTime === null) {
                comparison = 1;
              } else if (bTime === null) {
                comparison = -1;
              } else {
                comparison = aTime - bTime;
              }
            }
            break;
          case "name":
            comparison = (a.name || "").localeCompare(b.name || "");
            break;
          case "vendor":
            comparison = (a.vendor || "").localeCompare(b.vendor || "");
            break;
          case "paymentAccount":
            comparison = (a.paymentAccount || "").localeCompare(
              b.paymentAccount || ""
            );
            break;
          case "groupName":
            comparison = (a.groupName || "").localeCompare(b.groupName || "");
            break;
          case "internalCategory":
            comparison = (a.internalCategory || "").localeCompare(
              b.internalCategory || ""
            );
            break;
          case "recurrencePattern":
            comparison = (a.recurrencePattern || "").localeCompare(
              b.recurrencePattern || ""
            );
            break;
          case "amount":
            comparison = getEffectiveAmount(a) - getEffectiveAmount(b);
            break;
          case "pastDueDays":
            comparison = getDaysOverdue(a.dueDate) - getDaysOverdue(b.dueDate);
            break;
          case "suspensionDays": {
            // Get days until suspension for both expenses
            const aSuspension = expenseUtils.getDaysUntilSuspension(
              a.dueDate,
              a.suspensionPeriodValue,
              a.suspensionPeriodUnit
            );
            const bSuspension = expenseUtils.getDaysUntilSuspension(
              b.dueDate,
              b.suspensionPeriodValue,
              b.suspensionPeriodUnit
            );
            // Items without suspension data go to the end
            if (aSuspension === null && bSuspension === null) {
              comparison = 0;
            } else if (aSuspension === null) {
              comparison = 1; // a goes after b
            } else if (bSuspension === null) {
              comparison = -1; // b goes after a
            } else {
              comparison = aSuspension - bSuspension;
            }
            break;
          }
          default:
            comparison = 0;
        }

        return sortDirection === "asc" ? comparison : -comparison;
      });
    },
    [sortColumn, sortDirection, getEffectiveAmount]
  );

  const sortByDueDateAsc = useCallback((expenseList: Expense[]): Expense[] => {
    return [...expenseList].sort((a, b) => {
      const aTime = a?.dueDate instanceof Date ? a.dueDate.getTime() : 0;
      const bTime = b?.dueDate instanceof Date ? b.dueDate.getTime() : 0;
      return aTime - bTime;
    });
  }, []);

  // Create sorted expenses for each section
  const sortedExpenses = useMemo(() => {
    const result: Record<ExpenseSection, Expense[]> = {
      overdue: [],
      today: [],
      tomorrow: [],
      thisWeek: [],
      next10Days: [],
      thisMonth: [],
      nextMonth: [],
      received: [],
      pendingDeposit: [],
      expected: [],
      upcoming: [],
    };

    (Object.keys(expenses) as ExpenseSection[]).forEach((section) => {
      result[section] = sortExpenses(expenses[section]).filter(
        (expense) =>
          !skipHiddenIds.has(expense.id) &&
          !paidHiddenIds.has(expense.id) &&
          !deleteHiddenIds.has(expense.id)
      );
    });

    return result;
  }, [expenses, sortExpenses, skipHiddenIds, paidHiddenIds, deleteHiddenIds]);

  const suspensionPreviewRows = useMemo(() => {
    const SUSPENSION_OVERDUE_WINDOW_DAYS = 14;
    const rows: Array<{ expense: Expense; rowKey: string }> = [];
    const todayUTC = getTodayLocal();
    (Object.keys(sortedExpenses) as ExpenseSection[]).forEach((section) => {
      if (section === "overdue") return;
      sortedExpenses[section].forEach((expense) => {
        if (!expense.dueDate) return;
        if (expense.isPaid) return;
        // Avoid duplicating overdue rows: overdue expenses are also present in "expected",
        // which would otherwise add a suspension preview duplicate in the Overdue accordion.
        const dueUTC = toMidnightUTC(expense.dueDate);
        if (dueUTC.getTime() < todayUTC.getTime()) return;
        if (
          expense.suspensionPeriodValue === undefined ||
          !expense.suspensionPeriodUnit
        ) {
          return;
        }
        const suspensionDays = expenseUtils.getDaysUntilSuspensionFrom(
          todayUTC,
          expense.dueDate,
          expense.suspensionPeriodValue,
          expense.suspensionPeriodUnit
        );
        if (suspensionDays === null) return;
        if (
          suspensionDays < 0 ||
          suspensionDays > SUSPENSION_OVERDUE_WINDOW_DAYS
        ) {
          return;
        }
        rows.push({
          expense,
          rowKey: `suspension-preview-${expense.id}`,
        });
      });
    });
    return rows;
  }, [sortedExpenses]);

  const suspensionPreviewIds = useMemo(
    () => new Set(suspensionPreviewRows.map((row) => row.expense.id)),
    [suspensionPreviewRows]
  );

  // Get all visible expenses for select all functionality
  const allVisibleExpenseIds = useMemo(() => {
    const ids: string[] = [];
    (Object.keys(sortedExpenses) as ExpenseSection[]).forEach((section) => {
      sortedExpenses[section].forEach((expense) => {
        if (!expense.id.startsWith("projected-")) {
          ids.push(expense.id);
        }
      });
    });
    return ids;
  }, [sortedExpenses]);

  const visibleExpenseIdSet = useMemo(
    () => new Set(allVisibleExpenseIds),
    [allVisibleExpenseIds]
  );

  // Check if all visible expenses are selected
  const allSelected = useMemo(() => {
    return (
      allVisibleExpenseIds.length > 0 &&
      allVisibleExpenseIds.every((id) => selectedExpenseIds.has(id))
    );
  }, [allVisibleExpenseIds, selectedExpenseIds]);

  // Check if some (but not all) expenses are selected
  const someSelected = useMemo(() => {
    return selectedExpenseIds.size > 0 && !allSelected;
  }, [selectedExpenseIds, allSelected]);

  // Toggle single expense selection, with shift-click range selection.
  const toggleExpenseSelection = (expenseId: string, shiftKey = false) => {
    if (shiftKey && lastSelectedExpenseId) {
      const currentIndex = allVisibleExpenseIds.indexOf(expenseId);
      const anchorIndex = allVisibleExpenseIds.indexOf(lastSelectedExpenseId);
      if (currentIndex !== -1 && anchorIndex !== -1) {
        const [start, end] =
          currentIndex < anchorIndex
            ? [currentIndex, anchorIndex]
            : [anchorIndex, currentIndex];
        const rangeIds = allVisibleExpenseIds.slice(start, end + 1);
        setSelectedExpenseIds((previous) => {
          const next = new Set(previous);
          rangeIds.forEach((id) => next.add(id));
          console.debug("[ExpenseList] Shift-selected expense range", {
            anchorExpenseId: lastSelectedExpenseId,
            targetExpenseId: expenseId,
            selectedCount: next.size,
            rangeCount: rangeIds.length,
          });
          return next;
        });
        setLastSelectedExpenseId(expenseId);
        return;
      }
    }

    setSelectedExpenseIds((previous) => {
      const next = new Set(previous);
      if (next.has(expenseId)) {
        next.delete(expenseId);
      } else {
        next.add(expenseId);
      }
      console.debug("[ExpenseList] Toggled expense selection", {
        expenseId,
        selectedCount: next.size,
      });
      return next;
    });
    setLastSelectedExpenseId(expenseId);
  };

  // Toggle all expenses selection
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedExpenseIds(new Set());
      setLastSelectedExpenseId(null);
    } else {
      setSelectedExpenseIds(new Set(allVisibleExpenseIds));
      setLastSelectedExpenseId(allVisibleExpenseIds[0] || null);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedExpenseIds(new Set());
    setLastSelectedExpenseId(null);
  };

  const isExpenseStruck = useCallback(
    (expense: Expense) =>
      skipStrikethroughIds.has(expense.id) ||
      paidSavingIds.has(expense.id) ||
      expense.isSkipped,
    [skipStrikethroughIds, paidSavingIds]
  );

  const isExpensePaidSaving = useCallback(
    (expense: Expense) => paidSavingIds.has(expense.id),
    [paidSavingIds]
  );

  const isExpenseAnimatingOut = useCallback(
    (expense: Expense) => skipAnimatingOutIds.has(expense.id),
    [skipAnimatingOutIds]
  );

  const isExpensePaidAnimatingOut = useCallback(
    (expense: Expense) => paidAnimatingOutIds.has(expense.id),
    [paidAnimatingOutIds]
  );

  const getDueHighlightColor = useCallback(
    (expense: Expense, section?: ExpenseSection) => {
      // Next month items should never get urgency highlights
      if (section === "nextMonth") return null;

      const dueDateValue = expense?.dueDate;
      const hasValidDate =
        dueDateValue instanceof Date && !Number.isNaN(dueDateValue.getTime());

      if (!hasValidDate || expense.isPaid) {
        return null;
      }
      const todayUTC = getTodayLocal();
      const tomorrowUTC = addDays(todayUTC, 1);
      const next7EndUTC = addDays(todayUTC, 7);
      const next10EndUTC = addDays(todayUTC, 10);
      const dueUTC = toUtcDateOnly(dueDateValue);
      const alpha = isDarkMode ? 0.18 : 0.12;

      if (dueUTC.getTime() < todayUTC.getTime()) {
        return hexToRgba(sectionColors.overdue || "#E53E3E", alpha);
      }
      if (isSameUtcDay(dueUTC, todayUTC)) {
        return hexToRgba(sectionColors.today || "#DD6B20", alpha);
      }
      if (isSameUtcDay(dueUTC, tomorrowUTC)) {
        return hexToRgba(sectionColors.tomorrow || "#D69E2E", alpha);
      }
      if (dueUTC.getTime() <= next7EndUTC.getTime()) {
        return hexToRgba(sectionColors.thisWeek || "#3182CE", alpha);
      }
      if (dueUTC.getTime() <= next10EndUTC.getTime()) {
        return hexToRgba(sectionColors.next10Days || "#718096", alpha);
      }
      return null;
    },
    [isDarkMode]
  );

  const getSkipOpacity = useCallback(
    (expense: Expense, fallbackOpacity: number = 1) => {
      if (
        isExpenseAnimatingOut(expense) ||
        isExpensePaidAnimatingOut(expense)
      ) {
        return 0;
      }
      if (isExpenseStruck(expense)) return Math.min(0.5, fallbackOpacity);
      return fallbackOpacity;
    },
    [isExpenseAnimatingOut, isExpensePaidAnimatingOut, isExpenseStruck]
  );

  const getSkipTransform = useCallback(
    (expense: Expense) =>
      isExpenseAnimatingOut(expense) || isExpensePaidAnimatingOut(expense)
        ? "translateY(-8px)"
        : "translateY(0)",
    [isExpenseAnimatingOut, isExpensePaidAnimatingOut]
  );

  const getSkipTextDecoration = useCallback(
    (expense: Expense) => (isExpenseStruck(expense) ? "line-through" : "none"),
    [isExpenseStruck]
  );

  const openRescheduleModal = (expense: Expense) => {
    if (!onRescheduleExpense) return;
    const dateValue = expense.dueDate
      ? new Date(expense.dueDate).toISOString().split("T")[0]
      : "";
    console.log("[ExpenseList] Open reschedule modal", {
      expenseId: expense.id,
      dueDate: dateValue,
    });
    setRescheduleExpense(expense);
    setRescheduleDate(dateValue);
    setRescheduleScope("single");
    setRescheduleError("");
  };

  const closeRescheduleModal = () => {
    console.log("[ExpenseList] Close reschedule modal");
    setRescheduleExpense(null);
    setRescheduleDate("");
    setRescheduleScope("single");
    setRescheduleError("");
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleExpense || !onRescheduleExpense) return;
    if (!rescheduleDate) {
      setRescheduleError("Please choose a date.");
      return;
    }
    setIsRescheduling(true);
    const nextDate = new Date(rescheduleDate);
    console.log("[ExpenseList] Reschedule submit", {
      expenseId: rescheduleExpense.id,
      nextDate: rescheduleDate,
      scope: rescheduleScope,
    });
    try {
      await onRescheduleExpense(rescheduleExpense, nextDate, rescheduleScope);
      console.log("[ExpenseList] Reschedule success", {
        expenseId: rescheduleExpense.id,
        nextDate: rescheduleDate,
        scope: rescheduleScope,
      });
      closeRescheduleModal();
    } catch (error) {
      console.error("[ExpenseList] Reschedule failed", error);
      setRescheduleError("Failed to reschedule. Please try again.");
    } finally {
      setIsRescheduling(false);
    }
  };

  const handleSkipClick = async (expense: Expense) => {
    if (!onSkipExpense) return;
    const wasSkipped = expense.isSkipped;
    console.log("[ExpenseList] Skip clicked", {
      expenseId: expense.id,
      wasSkipped,
    });
    if (!wasSkipped) {
      setSkipStrikethroughIds((prev) => {
        const next = new Set(prev);
        next.add(expense.id);
        return next;
      });
    }
    try {
      await onSkipExpense(expense);
    } catch (error) {
      console.error("[ExpenseList] Skip failed", error);
      if (!wasSkipped) {
        setSkipStrikethroughIds((prev) => {
          const next = new Set(prev);
          next.delete(expense.id);
          return next;
        });
      }
      return;
    }

    if (wasSkipped) {
      setSkipStrikethroughIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
      setSkipAnimatingOutIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
      setSkipHiddenIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
      console.log("[ExpenseList] Unskip confirmed", { expenseId: expense.id });
      return;
    }

    setSkipAnimatingOutIds((prev) => {
      const next = new Set(prev);
      next.add(expense.id);
      return next;
    });
    if (skipTimeoutsRef.current[expense.id]) {
      window.clearTimeout(skipTimeoutsRef.current[expense.id]);
    }
    skipTimeoutsRef.current[expense.id] = window.setTimeout(() => {
      setSkipHiddenIds((prev) => {
        const next = new Set(prev);
        next.add(expense.id);
        return next;
      });
      setSkipAnimatingOutIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
      setSkipStrikethroughIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
      setSelectedExpenseIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
      delete skipTimeoutsRef.current[expense.id];
      console.log("[ExpenseList] Skip confirmed, hiding row", {
        expenseId: expense.id,
      });
    }, 220);
  };

  // Get selected expenses
  const selectedExpenses = useMemo(() => {
    const selected: Expense[] = [];
    (Object.keys(sortedExpenses) as ExpenseSection[]).forEach((section) => {
      sortedExpenses[section].forEach((expense) => {
        if (selectedExpenseIds.has(expense.id)) {
          selected.push(expense);
        }
      });
    });
    return selected;
  }, [sortedExpenses, selectedExpenseIds]);

  useEffect(() => {
    setSelectedExpenseIds((previous) => {
      const next = new Set(
        [...previous].filter((expenseId) => visibleExpenseIdSet.has(expenseId))
      );
      return next.size === previous.size ? previous : next;
    });
    setLastSelectedExpenseId((previous) =>
      previous && visibleExpenseIdSet.has(previous) ? previous : null
    );
  }, [visibleExpenseIdSet]);

  const selectedExpensesTotal = useMemo(
    () =>
      selectedExpenses.reduce(
        (total, expense) => total + getEffectiveAmount(expense),
        0
      ),
    [selectedExpenses, getEffectiveAmount]
  );

  // Handle bulk duplicate
  const handleBulkDuplicate = () => {
    if (selectedExpenses.length === 0) return;
    setShowBulkDuplicateModal(true);
  };

  // Execute bulk duplicate
  const executeBulkDuplicate = () => {
    if (!onDuplicateExpense) return;

    selectedExpenses.forEach((expense) => {
      // Create a new due date in the selected month/year
      const originalDay = expense.dueDate.getUTCDate();
      const lastDayOfMonth = new Date(
        Date.UTC(bulkDuplicateYear, bulkDuplicateMonth, 0)
      ).getUTCDate();
      const adjustedDay = Math.min(originalDay, lastDayOfMonth);
      const newDueDate = new Date(
        Date.UTC(bulkDuplicateYear, bulkDuplicateMonth - 1, adjustedDay)
      );

      // Create duplicated expense with new date and recurring flag
      const duplicatedExpense: Expense = {
        ...expense,
        id: "", // Will be assigned by backend
        dueDate: newDueDate,
        isRecurring: bulkDuplicateRecurring,
        isPaid: false,
      };

      onDuplicateExpense(duplicatedExpense);
    });

    setShowBulkDuplicateModal(false);
    clearSelection();
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedExpenses.length === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  // Execute bulk delete
  const executeBulkDelete = async () => {
    // Capture the expenses to delete before any state changes
    const expensesToDelete = [...selectedExpenses];

    // Close dialog and clear selection first to prevent re-renders during delete
    setShowBulkDeleteConfirm(false);
    clearSelection();

    // Delete each expense sequentially
    for (const expense of expensesToDelete) {
      try {
        await onDeleteExpense(expense);
      } catch (error) {
        console.error(`Failed to delete expense ${expense.id}:`, error);
      }
    }
  };

  // Group expenses by name within each section
  const groupedExpenses = useMemo(() => {
    const result: Record<
      ExpenseSection,
      { name: string; expenses: Expense[]; total: number }[]
    > = {
      overdue: [],
      today: [],
      tomorrow: [],
      thisWeek: [],
      next10Days: [],
      thisMonth: [],
      nextMonth: [],
      received: [],
      pendingDeposit: [],
      expected: [],
      upcoming: [],
    };

    (Object.keys(sortedExpenses) as ExpenseSection[]).forEach((section) => {
      const sectionExpenses = sortedExpenses[section];
      const groupMap = new Map<string, Expense[]>();

      sectionExpenses.forEach((expense) => {
        const groupKey = getDisplayName(expense);
        if (!groupMap.has(groupKey)) {
          groupMap.set(groupKey, []);
        }
        groupMap.get(groupKey)!.push(expense);
      });

      // Convert map to array and calculate totals (excluding skipped expenses)
      result[section] = Array.from(groupMap.entries()).map(([name, exps]) => ({
        name,
        expenses: exps,
        total: exps
          .filter((exp) => !exp.isSkipped)
          .reduce((sum, exp) => sum + getEffectiveAmount(exp), 0),
      }));
    });

    return result;
  }, [sortedExpenses, getEffectiveAmount, getDisplayName]);

  // Helper to categorize recurrence pattern into groups
  const getRecurrenceGroup = (expense: Expense): RecurrenceGroup => {
    if (!expense.isRecurring) return "one-time";

    const pattern = (expense.recurrencePattern || "monthly").toLowerCase();

    // Daily, weekly, biweekly, monthly -> "monthly" group
    if (
      ["daily", "weekly", "biweekly", "bi-weekly", "monthly"].includes(pattern)
    ) {
      return "monthly";
    }

    // Quarterly -> "quarterly" group
    if (pattern === "quarterly") {
      return "quarterly";
    }

    // Yearly, annually -> "annually" group
    if (["yearly", "annually", "annual"].includes(pattern)) {
      return "annually";
    }

    // Default to monthly for unknown patterns
    return "monthly";
  };

  // Get display name for recurrence group
  const getRecurrenceGroupLabel = (group: RecurrenceGroup): string => {
    switch (group) {
      case "monthly":
        return "Monthly";
      case "quarterly":
        return "Quarterly";
      case "annually":
        return "Annually";
      case "one-time":
        return "One-Time";
    }
  };

  // Get color for recurrence group
  const getRecurrenceGroupColor = (group: RecurrenceGroup): string => {
    switch (group) {
      case "monthly":
        return "#38B2AC"; // Teal
      case "quarterly":
        return "#805AD5"; // Purple
      case "annually":
        return "#D69E2E"; // Yellow/Gold
      case "one-time":
        return "#718096"; // Gray
    }
  };

  // Group expenses by recurrence type
  const recurrenceGroupedExpenses = useMemo(() => {
    if (groupingMode !== "recurrence") return null;

    // Flatten all expenses from all sections
    const allExpenses: Expense[] = [];
    (Object.keys(expenses) as ExpenseSection[]).forEach((section) => {
      // Skip received (paid) section
      if (section !== "received") {
        expenses[section].forEach((expense) => {
          if (skipHiddenIds.has(expense.id) || paidHiddenIds.has(expense.id)) {
            return;
          }
          // Avoid duplicates
          if (!allExpenses.find((e) => e.id === expense.id)) {
            allExpenses.push(expense);
          }
        });
      }
    });

    // Initialize groups
    const groups: Record<
      RecurrenceGroup,
      {
        expenses: Expense[];
        total: number;
        subGroups?: {
          label: string;
          pattern: string;
          expenses: Expense[];
          total: number;
        }[];
      }
    > = {
      monthly: { expenses: [], total: 0 },
      quarterly: { expenses: [], total: 0 },
      annually: { expenses: [], total: 0 },
      "one-time": { expenses: [], total: 0 },
    };

    // Categorize expenses
    allExpenses.forEach((expense) => {
      const group = getRecurrenceGroup(expense);
      groups[group].expenses.push(expense);
      if (!expense.isSkipped) {
        groups[group].total += getEffectiveAmount(expense);
      }
    });

    // Sort expenses within each group by due date (always ascending for recurrence view)
    const sortByDueDate = (expenseList: Expense[]): Expense[] => {
      return [...expenseList].sort(
        (a, b) => a.dueDate.getTime() - b.dueDate.getTime()
      );
    };

    (Object.keys(groups) as RecurrenceGroup[]).forEach((group) => {
      groups[group].expenses = sortByDueDate(groups[group].expenses);
    });

    // Create sub-groups for monthly section (separate accordions for each frequency)
    const monthlyExpenses = groups.monthly.expenses;
    const monthlyOnly: Expense[] = [];
    const biweeklyOnly: Expense[] = [];
    const weeklyOnly: Expense[] = [];
    const dailyOnly: Expense[] = [];

    monthlyExpenses.forEach((expense) => {
      const pattern = (expense.recurrencePattern || "monthly").toLowerCase();
      if (pattern === "daily") {
        dailyOnly.push(expense);
      } else if (pattern === "weekly") {
        weeklyOnly.push(expense);
      } else if (pattern === "biweekly" || pattern === "bi-weekly") {
        biweeklyOnly.push(expense);
      } else {
        monthlyOnly.push(expense);
      }
    });

    // Calculate sub-group totals
    const monthlyOnlyTotal = monthlyOnly
      .filter((e) => !e.isSkipped)
      .reduce((sum, e) => sum + getEffectiveAmount(e), 0);
    const biweeklyOnlyTotal = biweeklyOnly
      .filter((e) => !e.isSkipped)
      .reduce((sum, e) => sum + getEffectiveAmount(e), 0);
    const weeklyOnlyTotal = weeklyOnly
      .filter((e) => !e.isSkipped)
      .reduce((sum, e) => sum + getEffectiveAmount(e), 0);
    const dailyOnlyTotal = dailyOnly
      .filter((e) => !e.isSkipped)
      .reduce((sum, e) => sum + getEffectiveAmount(e), 0);

    // Set sub-groups if there are multiple frequency types
    const hasMultipleFrequencies =
      (monthlyOnly.length > 0 ? 1 : 0) +
        (biweeklyOnly.length > 0 ? 1 : 0) +
        (weeklyOnly.length > 0 ? 1 : 0) +
        (dailyOnly.length > 0 ? 1 : 0) >
      1;

    if (hasMultipleFrequencies) {
      groups.monthly.subGroups = [];

      // Add monthly sub-group
      if (monthlyOnly.length > 0) {
        groups.monthly.subGroups.push({
          label: "Monthly",
          pattern: "monthly-only",
          expenses: monthlyOnly,
          total: monthlyOnlyTotal,
        });
      }

      // Add every other week (bi-weekly) sub-group
      if (biweeklyOnly.length > 0) {
        groups.monthly.subGroups.push({
          label: "Every Other Week",
          pattern: "biweekly",
          expenses: biweeklyOnly,
          total: biweeklyOnlyTotal,
        });
      }

      // Add weekly sub-group (collapsed by default)
      if (weeklyOnly.length > 0) {
        groups.monthly.subGroups.push({
          label: "Weekly",
          pattern: "weekly",
          expenses: weeklyOnly,
          total: weeklyOnlyTotal,
        });
      }

      // Add daily sub-group (collapsed by default)
      if (dailyOnly.length > 0) {
        groups.monthly.subGroups.push({
          label: "Daily",
          pattern: "daily",
          expenses: dailyOnly,
          total: dailyOnlyTotal,
        });
      }
    }

    console.log("[ExpenseList] Recurrence grouped expenses:", {
      monthly: groups.monthly.expenses.length,
      quarterly: groups.quarterly.expenses.length,
      annually: groups.annually.expenses.length,
      oneTime: groups["one-time"].expenses.length,
      hasSubGroups: !!groups.monthly.subGroups,
    });

    return groups;
  }, [
    expenses,
    groupingMode,
    getEffectiveAmount,
    skipHiddenIds,
    paidHiddenIds,
  ]);

  // State for collapsed recurrence sections
  const [collapsedRecurrenceSections, setCollapsedRecurrenceSections] =
    useState<Set<RecurrenceGroup>>(() => {
      const saved = sessionStorage.getItem(
        "expenses_collapsed_recurrence_sections"
      );
      if (saved) {
        try {
          return new Set(JSON.parse(saved) as RecurrenceGroup[]);
        } catch {
          // Default to none collapsed
        }
      }
      return new Set<RecurrenceGroup>();
    });

  // State for collapsed recurrence sub-groups (daily/weekly)
  const [collapsedRecurrenceSubGroups, setCollapsedRecurrenceSubGroups] =
    useState<Set<string>>(() => {
      const saved = sessionStorage.getItem(
        "expenses_collapsed_recurrence_subgroups"
      );
      if (saved) {
        try {
          return new Set(JSON.parse(saved) as string[]);
        } catch {
          // Default to weekly and daily collapsed
        }
      }
      return new Set<string>(["weekly", "daily"]);
    });

  const [collapsedExpectedGroups, setCollapsedExpectedGroups] = useState<
    Set<string>
  >(() => {
    const saved = sessionStorage.getItem("expenses_collapsed_expected_groups");
    if (saved) {
      try {
        return new Set(JSON.parse(saved) as string[]);
      } catch {
        // Default to none collapsed
      }
    }
    return new Set<string>();
  });

  const [pausedExpectedIds, setPausedExpectedIds] = useState<Set<string>>(
    () => {
      const saved = sessionStorage.getItem("expenses_paused_expected_ids");
      if (saved) {
        try {
          return new Set(JSON.parse(saved) as string[]);
        } catch {
          // Default to none paused
        }
      }
      return new Set<string>();
    }
  );
  const [pauseAnimatingOutIds, setPauseAnimatingOutIds] = useState<Set<string>>(
    () => new Set<string>()
  );
  const [pauseAnimatingInIds, setPauseAnimatingInIds] = useState<Set<string>>(
    () => new Set<string>()
  );
  const pauseTimeoutsRef = useRef<Record<string, number>>({});

  const toggleRecurrenceSection = (group: RecurrenceGroup) => {
    const newCollapsed = new Set(collapsedRecurrenceSections);
    if (newCollapsed.has(group)) {
      newCollapsed.delete(group);
    } else {
      newCollapsed.add(group);
    }
    setCollapsedRecurrenceSections(newCollapsed);
    sessionStorage.setItem(
      "expenses_collapsed_recurrence_sections",
      JSON.stringify([...newCollapsed])
    );
  };

  const toggleRecurrenceSubGroup = (key: string) => {
    const newCollapsed = new Set(collapsedRecurrenceSubGroups);
    if (newCollapsed.has(key)) {
      newCollapsed.delete(key);
    } else {
      newCollapsed.add(key);
    }
    setCollapsedRecurrenceSubGroups(newCollapsed);
    sessionStorage.setItem(
      "expenses_collapsed_recurrence_subgroups",
      JSON.stringify([...newCollapsed])
    );
  };

  const toggleExpectedGroup = (key: string) => {
    const newCollapsed = new Set(collapsedExpectedGroups);
    if (newCollapsed.has(key)) {
      newCollapsed.delete(key);
    } else {
      newCollapsed.add(key);
    }
    setCollapsedExpectedGroups(newCollapsed);
    sessionStorage.setItem(
      "expenses_collapsed_expected_groups",
      JSON.stringify([...newCollapsed])
    );
    console.log("[ExpenseList] Toggle expected group", {
      key,
      collapsed: newCollapsed.has(key),
    });
  };

  useEffect(() => {
    sessionStorage.setItem(
      "expenses_paused_expected_ids",
      JSON.stringify([...pausedExpectedIds])
    );
    onPausedIdsChange?.(pausedExpectedIds);
  }, [pausedExpectedIds, onPausedIdsChange]);

  useEffect(() => {
    const timeoutMap = pauseTimeoutsRef.current;
    return () => {
      Object.values(timeoutMap).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
    };
  }, []);

  const schedulePauseTimeout = (
    key: string,
    delayMs: number,
    cb: () => void
  ) => {
    const timeoutId = window.setTimeout(() => {
      delete pauseTimeoutsRef.current[key];
      cb();
    }, delayMs);
    pauseTimeoutsRef.current[key] = timeoutId;
  };

  const handlePauseExpense = (expense: Expense) => {
    if (!expense?.id || pausedExpectedIds.has(expense.id)) return;
    console.log("[ExpenseList] Pause expense", {
      id: expense.id,
      name: expense.name,
    });
    setPauseAnimatingOutIds((prev) => new Set(prev).add(expense.id));
    schedulePauseTimeout(`pause-out-${expense.id}`, 200, () => {
      setPauseAnimatingOutIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
      setPausedExpectedIds((prev) => new Set(prev).add(expense.id));
      setPauseAnimatingInIds((prev) => new Set(prev).add(expense.id));
      schedulePauseTimeout(`pause-in-${expense.id}`, 220, () => {
        setPauseAnimatingInIds((prev) => {
          const next = new Set(prev);
          next.delete(expense.id);
          return next;
        });
      });
    });
  };

  const toggleGroup = (groupKey: string) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupKey)) {
      newCollapsed.delete(groupKey);
    } else {
      newCollapsed.add(groupKey);
    }
    setCollapsedGroups(newCollapsed);
    sessionStorage.setItem(
      "expenses_collapsed_groups",
      JSON.stringify([...newCollapsed])
    );
  };

  const toggleSection = (section: ExpenseSection) => {
    const isCollapsed = collapsedSections.has(section);
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(section)) {
      newCollapsed.delete(section);
    } else {
      newCollapsed.add(section);
    }
    setCollapsedSections(newCollapsed);
    // Persist to sessionStorage
    sessionStorage.setItem(
      "expenses_collapsed_sections",
      JSON.stringify([...newCollapsed])
    );
    if (onSectionChange) {
      onSectionChange(isCollapsed ? section : null);
    }
  };

  // Add more future months
  const handleAddFutureMonth = () => {
    const newCount = futureMonthsCount + 1;
    setFutureMonthsCount(newCount);
    void persistFutureMonthsCount(newCount);
  };

  // Remove the last future month
  const handleRemoveFutureMonth = () => {
    if (futureMonthsCount > 0) {
      const newCount = futureMonthsCount - 1;
      setFutureMonthsCount(newCount);
      void persistFutureMonthsCount(newCount);
    }
  };

  // Generate future month data (sorted)
  const futureMonthsData = useMemo(() => {
    const data: { monthOffset: number; name: string; expenses: Expense[] }[] =
      [];
    for (let i = 2; i <= futureMonthsCount + 1; i++) {
      const baseDate = getTodayLocal();
      const targetMonth = new Date(
        Date.UTC(baseDate.getUTCFullYear(), baseDate.getUTCMonth() + i, 1)
      );
      const targetYear = targetMonth.getUTCFullYear();
      const targetMonthNum = targetMonth.getUTCMonth();

      // For future months, show real DB rows (recurring instances, one-time, etc.)
      // rather than client-side projections.
      const sourceList = projectionSources ?? null;
      const monthExpenses = (sourceList || [])
        .filter((expense) => {
          if (
            skipHiddenIds.has(expense.id) ||
            paidHiddenIds.has(expense.id) ||
            deleteHiddenIds.has(expense.id)
          ) {
            return false;
          }
          if (!expense.dueDate) return false;
          if (expense.id.startsWith("projected-")) return false;
          const due = expense.dueDate;
          return (
            due.getUTCFullYear() === targetYear &&
            due.getUTCMonth() === targetMonthNum &&
            !expense.isPaid
          );
        })
        .filter((expense) => matchesSearchQuery(expense))
        .filter(
          (expense, index, arr) =>
            arr.findIndex((e) => e.id === expense.id) === index
        );
      data.push({
        monthOffset: i,
        name: getFutureMonthName(i),
        expenses: sortByDueDateAsc(monthExpenses),
      });
    }
    return data;
  }, [
    futureMonthsCount,
    sortByDueDateAsc,
    projectionSources,
    skipHiddenIds,
    paidHiddenIds,
    deleteHiddenIds,
    matchesSearchQuery,
  ]);

  const handleDeleteFutureMonthsFrom = (monthOffset: number) => {
    const newCount = Math.max(0, monthOffset - 2);
    setFutureMonthsCount(newCount);
    void persistFutureMonthsCount(newCount);
  };

  const openPaidModal = async (expense: Expense) => {
    setSelectedExpense(expense);
    const now = new Date();
    setPaymentDate(now.toISOString().split("T")[0]);
    setPaymentTime(now.toTimeString().split(" ")[0].slice(0, 5));
    setPaymentAccount("TPC Chase");
    setSelectedTransaction("");
    setTransactionSearch("");
    setShowPaidModal(true);

    // Load recent transactions
    setLoadingTransactions(true);
    try {
      const recentTransactions =
        await transactionService.getRecentTransactions(60);
      setTransactions(recentTransactions);
    } catch (error) {
      console.error("Failed to load transactions:", error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Shared finalizer: persist paid status via onMarkAsPaid and run the
  // row animate-out. Used by the paid modal and the link-transaction flow.
  const finalizeMarkPaid = async (
    expense: Expense,
    fullPaymentDate: Date,
    account: string,
    transactionId?: string
  ): Promise<boolean> => {
    let markResult: boolean | void = true;
    try {
      markResult = await onMarkAsPaid(
        expense,
        fullPaymentDate,
        account,
        transactionId
      );
    } catch (error) {
      console.error("[ExpenseList] Mark as paid failed", error);
      markResult = false;
    }

    if (markResult !== false) {
      const expenseId = expense.id;
      console.log("[ExpenseList] Paid saved, animating out", {
        expenseId,
      });
      setPaidAnimatingOutIds((prev) => new Set(prev).add(expenseId));
      if (paidTimeoutsRef.current[expenseId]) {
        window.clearTimeout(paidTimeoutsRef.current[expenseId]);
      }
      paidTimeoutsRef.current[expenseId] = window.setTimeout(() => {
        setPaidHiddenIds((prev) => new Set(prev).add(expenseId));
        setPaidAnimatingOutIds((prev) => {
          const next = new Set(prev);
          next.delete(expenseId);
          return next;
        });
        setSelectedExpenseIds((prev) => {
          const next = new Set(prev);
          next.delete(expenseId);
          return next;
        });
        delete paidTimeoutsRef.current[expenseId];
        console.log("[ExpenseList] Paid confirmed, hiding row", {
          expenseId,
        });
      }, 220);
    }
    return markResult !== false;
  };

  const handleMarkAsPaid = async () => {
    if (!selectedExpense) return;

    const expense = selectedExpense;
    const fullPaymentDate = new Date(`${paymentDate}T${paymentTime}:00`);
    const account = paymentAccount;
    const txnId = selectedTransaction || undefined;
    const txnToLink = selectedTransaction;

    // Optimistic: close the modal instantly and start the "breathing" save
    // state on the row so the action feels immediate.
    setShowPaidModal(false);
    setSelectedExpense(null);
    setPaidSavingIds((prev) => new Set(prev).add(expense.id));

    // Link transaction if selected
    if (txnToLink) {
      try {
        await transactionService.linkTransactionToExpense(
          txnToLink,
          expense.id
        );

        // Learn from this manual linking for future suggestions
        const linkedTransaction = transactions.find((t) => t.id === txnToLink);
        if (linkedTransaction) {
          linkingService.learnFromLink(
            expense.vendor || "",
            getDisplayName(expense),
            linkedTransaction.name,
            linkedTransaction.merchant_name || "",
            getEffectiveAmount(expense),
            linkedTransaction.amount
          );
        }
      } catch (error) {
        console.error("Failed to link transaction:", error);
      }
    }

    const ok = await finalizeMarkPaid(expense, fullPaymentDate, account, txnId);

    // Clear the breathing state: on success the row animates out via
    // finalizeMarkPaid; on failure it reverts to its normal (unpaid) look.
    setPaidSavingIds((prev) => {
      const next = new Set(prev);
      next.delete(expense.id);
      return next;
    });

    if (!ok) {
      console.error("[ExpenseList] Mark as paid did not persist", {
        expenseId: expense.id,
      });
    }
  };

  // Filter transactions based on search and expense amount
  const getFilteredTransactions = () => {
    if (!selectedExpense) return [];

    const expenseAmount = getEffectiveAmount(selectedExpense);
    const searchLower = transactionSearch.toLowerCase();

    if (searchLower === "") {
      // No search term - show AI suggested matches first
      const suggestions = linkingService.getSuggestedTransactions(
        selectedExpense.vendor || "",
        getDisplayName(selectedExpense),
        expenseAmount,
        transactions
      );

      const suggestedIds = new Set(suggestions.map((s) => s.transaction.id));
      const suggested = suggestions.map((s) => ({
        ...s.transaction,
        _confidence: s.confidence,
        _reason: s.reason,
      }));

      // Add remaining transactions with basic amount filtering
      const remaining = transactions
        .filter((tx) => !suggestedIds.has(tx.id))
        .filter((tx) => {
          const amountTolerance = Math.max(expenseAmount * 0.5, 25); // Wider tolerance for browsing
          return Math.abs(tx.amount - expenseAmount) <= amountTolerance;
        })
        .slice(0, 10);

      return [...suggested, ...remaining].slice(0, 20);
    } else {
      // User is searching - filter by text
      return transactions
        .filter((tx) => {
          const nameMatch = tx.name.toLowerCase().includes(searchLower);
          const merchantMatch =
            tx.merchant_name?.toLowerCase().includes(searchLower) || false;
          return nameMatch || merchantMatch;
        })
        .slice(0, 20);
    }
  };

  const handleTransactionSelect = (transaction: Transaction) => {
    setSelectedTransaction(transaction.id);
    setTransactionSearch(
      `${transaction.name} - $${transaction.amount.toFixed(2)}`
    );
    setShowTransactionDropdown(false);
  };

  // Sort sections by priority for display
  const sectionOrder: ExpenseSection[] = [
    "expected",
    "overdue",
    "today",
    "tomorrow",
    "thisWeek",
    "next10Days",
    "thisMonth",
    "nextMonth",
  ];

  const sectionContainerStyle = {
    marginBottom: "14px",
  };

  const sectionHeaderStyle = (isCollapsed: boolean) => ({
    backgroundColor: isDarkMode ? "#1a1a1a" : "white",
    padding: "10px",
    borderRadius: isCollapsed ? "8px" : "8px 8px 0 0",
    boxShadow: isDarkMode
      ? "0 4px 6px rgba(0, 0, 0, 0.3)"
      : "0 1px 3px rgba(0,0,0,0.1)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  });

  const sectionContentStyle = {
    backgroundColor: isDarkMode ? "#252525" : "#f7f8fa",
    padding: "10px",
    borderRadius: "0 0 8px 8px",
    boxShadow: isDarkMode
      ? "0 4px 6px rgba(0, 0, 0, 0.3)"
      : "0 1px 3px rgba(0,0,0,0.1)",
  };

  const headingStyle = {
    fontSize: "18px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
  };

  const badgeStyle = (color: string) => ({
    backgroundColor: `${color}20`,
    color: color,
    fontSize: "12px",
    fontWeight: "bold",
    padding: "2px 8px",
    borderRadius: "9999px",
    marginLeft: "8px",
  });

  const expenseItemStyle = {
    backgroundColor: isDarkMode ? "#1a1a1a" : "white",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "6px",
    paddingRight: "6px",
    borderRadius: "6px",
    boxShadow: isDarkMode
      ? "0 2px 4px rgba(0, 0, 0, 0.3)"
      : "0 1px 2px rgba(0,0,0,0.05)",
    marginBottom: "0px",
  };

  // Row styles are applied directly in the JSX

  const categoryTagStyle = {
    backgroundColor: "#805AD520",
    color: "#805AD5",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "12px",
    marginRight: "4px",
  };

  const recurringTagStyle = {
    backgroundColor: "#38B2AC20",
    color: "#38B2AC",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "12px",
  };

  const getBillBudgetType = (expense: Expense): "bill" | "budget" =>
    expense.billOrBudget === "budget" ? "budget" : "bill";

  const sharedTagStyle = {
    backgroundColor: "#3182CE20",
    color: "#3182CE",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "12px",
  };

  // Sortable header render helper
  const renderSortableHeader = (
    column: SortColumn,
    label: string,
    align: "left" | "right" = "left"
  ) => {
    const isActive = sortColumn === column;
    return (
      <th
        onClick={() => handleSort(column)}
        style={{
          padding: "8px 12px",
          textAlign: align,
          fontWeight: 600,
          cursor: "pointer",
          userSelect: "none",
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {label}
          {isActive &&
            (sortDirection === "asc" ? (
              <ArrowUpwardIcon sx={{ fontSize: 14, opacity: 0.8 }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: 14, opacity: 0.8 }} />
            ))}
          {!isActive && <ArrowUpwardIcon sx={{ fontSize: 14, opacity: 0.2 }} />}
        </span>
      </th>
    );
  };

  // Non-sortable header for the "Linked Transaction" indicator column.
  const renderLinkedHeader = () => (
    <th
      key="linked-header"
      style={{
        padding: "8px 4px",
        textAlign: "center",
        fontWeight: 600,
        whiteSpace: "nowrap",
        width: "70px",
      }}
    >
      Linked
    </th>
  );

  // Status column sortable header with dropdown for Past Due Days / Suspension Days
  const renderStatusSortHeader = () => {
    const isPastDueActive = sortColumn === "pastDueDays";
    const isSuspensionActive = sortColumn === "suspensionDays";

    return (
      <th
        style={{
          padding: "8px 4px",
          width: "100px",
          fontWeight: 600,
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            fontSize: "10px",
          }}
        >
          <Tooltip title="Sort by Past Due Days" arrow>
            <span
              onClick={() => handleSort("pastDueDays")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
                cursor: "pointer",
                padding: "2px 4px",
                borderRadius: "4px",
                backgroundColor: isPastDueActive
                  ? isDarkMode
                    ? "rgba(229, 62, 62, 0.2)"
                    : "rgba(229, 62, 62, 0.1)"
                  : "transparent",
                color: isPastDueActive ? "#E53E3E" : "inherit",
                fontWeight: isPastDueActive ? 700 : 600,
              }}
            >
              Due
              {isPastDueActive &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon sx={{ fontSize: 10, opacity: 0.8 }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: 10, opacity: 0.8 }} />
                ))}
            </span>
          </Tooltip>
          <span style={{ opacity: 0.5 }}>|</span>
          <Tooltip title="Sort by Days Until Suspension (soonest first)" arrow>
            <span
              onClick={() => handleSort("suspensionDays")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
                cursor: "pointer",
                padding: "2px 4px",
                borderRadius: "4px",
                backgroundColor: isSuspensionActive
                  ? isDarkMode
                    ? "rgba(237, 137, 54, 0.2)"
                    : "rgba(237, 137, 54, 0.1)"
                  : "transparent",
                color: isSuspensionActive ? "#ED8936" : "inherit",
                fontWeight: isSuspensionActive ? 700 : 600,
              }}
            >
              Susp
              {isSuspensionActive &&
                (sortDirection === "asc" ? (
                  <ArrowUpwardIcon sx={{ fontSize: 10, opacity: 0.8 }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: 10, opacity: 0.8 }} />
                ))}
            </span>
          </Tooltip>
        </div>
      </th>
    );
  };

  const [copyToastKey, setCopyToastKey] = useState(0);
  const [copyToastOpen, setCopyToastOpen] = useState(false);
  const copyToastTimeoutRef = useRef<number | null>(null);

  const showCopyToast = () => {
    setCopyToastKey(Date.now());
    setCopyToastOpen(true);
    if (copyToastTimeoutRef.current) {
      window.clearTimeout(copyToastTimeoutRef.current);
    }
    copyToastTimeoutRef.current = window.setTimeout(() => {
      setCopyToastOpen(false);
      copyToastTimeoutRef.current = null;
    }, 1400);
  };

  useEffect(() => {
    return () => {
      if (copyToastTimeoutRef.current) {
        window.clearTimeout(copyToastTimeoutRef.current);
        copyToastTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCopyTaskId = async (expense: Expense) => {
    const taskId = expense.id;
    if (!taskId) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(taskId);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = taskId;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      console.debug("[ExpenseList] Copied task id", { expenseId: taskId });
      showCopyToast();
    } catch (error) {
      console.error("[ExpenseList] Failed to copy task id", {
        expenseId: taskId,
        error,
      });
    }
  };

  const renderExpenseTitle = (
    expense: Expense,
    options?: { isSuspensionPreview?: boolean }
  ) => {
    const hasShoppingList = !!expense.linkedShoppingListId;
    const isSuspensionPreview = options?.isSuspensionPreview;
    // For a debt-linked expense, show the debt's account under the creditor.
    const linkedDebt =
      expense.linkedDebtId && debts
        ? debts.find((d) => d.id === expense.linkedDebtId)
        : undefined;
    const debtCreditor = linkedDebt?.creditor || linkedDebt?.account;
    const debtAccountSubtitle =
      linkedDebt && linkedDebt.account && linkedDebt.account !== debtCreditor
        ? linkedDebt.account
        : undefined;
    const nameNode = (
      <span
        onMouseEnter={() => {
          if (expense.notes) {
            console.debug("[ExpenseList] Notes tooltip", {
              expenseId: expense.id,
            });
          }
        }}
        style={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {hasShoppingList ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                openShoppingModal(expense);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  openShoppingModal(expense);
                }
              }}
              style={{
                color: isDarkMode ? "#63b3ed" : "#3182CE",
                textDecoration: "underline",
                textDecorationStyle: "dotted" as const,
                cursor: "pointer",
              }}
              title="View shopping list"
            >
              {getDisplayName(expense)}
            </span>
          ) : (
            getDisplayName(expense)
          )}
          <CreatedViaBadge createdVia={expense.createdVia} />
        </span>
        {debtAccountSubtitle && (
          <span
            style={{
              fontSize: "11px",
              color: "#A0AEC0",
              lineHeight: 1.1,
              fontWeight: 400,
            }}
          >
            {debtAccountSubtitle}
          </span>
        )}
      </span>
    );
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={(event) => {
          if (hasShoppingList) return; // handled by inner click
          event.stopPropagation();
          handleCopyTaskId(expense);
        }}
        onKeyDown={(event) => {
          if (hasShoppingList) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            handleCopyTaskId(expense);
          }
        }}
        style={{
          cursor: hasShoppingList ? "default" : "pointer",
          display: "inline-flex",
          alignItems: "center",
        }}
        title={hasShoppingList ? undefined : "Copy Task ID"}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {pinnedExpenseIds?.has(expense.id) && (
            <Tooltip title="Pinned to Today" arrow>
              <PushPinIcon sx={{ fontSize: 14, color: "#38A169" }} />
            </Tooltip>
          )}
          {isSuspensionPreview && (
            <Tooltip
              title="This is not overdue, but its suspension date is within 14 days."
              arrow
            >
              <PushPinIcon sx={{ fontSize: 14, color: "#A0AEC0" }} />
            </Tooltip>
          )}
          {hasShoppingList && (
            <Tooltip title="Shopping List" arrow>
              <ShoppingCartIcon
                sx={{ fontSize: 16, color: isDarkMode ? "#63b3ed" : "#3182CE" }}
              />
            </Tooltip>
          )}
          {expense.notes ? (
            <Tooltip title={expense.notes} arrow placement="top">
              {nameNode}
            </Tooltip>
          ) : (
            nameNode
          )}
          {(() => {
            if (!isRecurringSeriesExpense(expense)) return null;
            const rootId = getExpenseSeriesRootId(expense);
            const count = (seriesPastPresentIds.get(rootId) || []).length;
            if (count < 1) return null;
            return (
              <Tooltip
                title={`${count} past/current instance${
                  count === 1 ? "" : "s"
                } in this recurring series — click to highlight`}
                arrow
              >
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Highlight ${count} past or current recurring instances`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleSeriesBadgeClick(rootId);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      handleSeriesBadgeClick(rootId);
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "20px",
                    height: "18px",
                    padding: "0 6px",
                    borderRadius: "9px",
                    fontSize: "11px",
                    fontWeight: 700,
                    lineHeight: 1,
                    cursor: "pointer",
                    backgroundColor: isDarkMode ? "#805AD540" : "#805AD520",
                    color: isDarkMode ? "#D6BCFA" : "#6B46C1",
                    border: `1px solid ${isDarkMode ? "#805AD560" : "#805AD540"}`,
                  }}
                >
                  {count}
                </span>
              </Tooltip>
            );
          })()}
        </span>
      </span>
    );
  };

  const recurringSeriesCandidates = useMemo(
    () =>
      visibleExpensePool
        .filter(
          (expense) =>
            expense.isRecurring &&
            !expense.isRecurringInstance &&
            !expense.originalId
        )
        .sort((a, b) => {
          const aName = getDisplayName(a).toLowerCase();
          const bName = getDisplayName(b).toLowerCase();
          return aName.localeCompare(bName);
        }),
    [visibleExpensePool, getDisplayName]
  );

  const filteredRecurringSeriesCandidates = useMemo(() => {
    const query = mergeSeriesQuery.trim().toLowerCase();
    if (!query) return recurringSeriesCandidates;
    return recurringSeriesCandidates.filter((expense) => {
      const haystack = [
        getDisplayName(expense),
        expense.vendor,
        expense.internalCategory,
        expense.recurrencePattern,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [recurringSeriesCandidates, mergeSeriesQuery, getDisplayName]);

  const renderOccurrencesAction = (
    expense: Expense,
    options?: { disabled?: boolean; iconSize?: "small" | "medium" }
  ) => {
    const isSeriesExpense = isRecurringSeriesExpense(expense);
    const canMergeIntoSeries =
      !isSeriesExpense && Boolean(onMergeExpenseIntoSeries);
    if (!isSeriesExpense && !canMergeIntoSeries) return null;

    return (
      <Tooltip
        title={isSeriesExpense ? "View Occurrences" : "Find Recurring Series"}
        arrow
      >
        <IconButton
          onClick={() =>
            isSeriesExpense
              ? setOccurrencesModalExpense(expense)
              : setMergeSeriesExpense(expense)
          }
          size={options?.iconSize || "small"}
          disabled={options?.disabled}
          sx={{
            backgroundColor: "#4C51BF",
            color: "white",
            width: 28,
            height: 28,
            "&:hover": { backgroundColor: "#434190" },
            "&.Mui-disabled": {
              backgroundColor: "#4C51BF",
              color: "white",
              opacity: 0.6,
            },
          }}
        >
          {isSeriesExpense ? (
            <FormatListBulletedIcon sx={{ fontSize: 16 }} />
          ) : (
            <LinkIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>
    );
  };

  const buildDateBarMeta = (
    rows: Expense[],
    options?: { isEligible?: (expense: Expense, index: number) => boolean }
  ) => {
    const todayUTC = getTodayLocal();
    const tomorrowUTC = addDays(todayUTC, 1);
    const next7EndUTC = addDays(todayUTC, 7);
    const next10EndUTC = addDays(todayUTC, 10);
    const isEligible =
      options?.isEligible || ((expense: Expense) => Boolean(expense));

    const inRange = (date: Date, end: Date) => {
      const dueUTC = toUtcDateOnly(date).getTime();
      return dueUTC >= todayUTC.getTime() && dueUTC <= end.getTime();
    };

    const isRemainingInThisMonth = (expense: Expense) => {
      const dueUTC = toUtcDateOnly(expense.dueDate);
      return (
        dueUTC.getUTCFullYear() === todayUTC.getUTCFullYear() &&
        dueUTC.getUTCMonth() === todayUTC.getUTCMonth() &&
        dueUTC.getTime() > next10EndUTC.getTime()
      );
    };

    const flags = {
      overdue: rows.map(
        (expense, index) =>
          isEligible(expense, index) && isOverdueForBars(expense)
      ),
      today: rows.map(
        (expense, index) =>
          isEligible(expense, index) && isSameUtcDay(expense.dueDate, todayUTC)
      ),
      tomorrow: rows.map(
        (expense, index) =>
          isEligible(expense, index) &&
          isSameUtcDay(expense.dueDate, tomorrowUTC)
      ),
      next7: rows.map(
        (expense, index) =>
          isEligible(expense, index) && inRange(expense.dueDate, next7EndUTC)
      ),
      next10: rows.map(
        (expense, index) =>
          isEligible(expense, index) && inRange(expense.dueDate, next10EndUTC)
      ),
      remaining: rows.map(
        (expense, index) =>
          isEligible(expense, index) &&
          !isOverdueForBars(expense) &&
          isRemainingInThisMonth(expense)
      ),
    };
    const totals = {
      overdue: 0,
      today: 0,
      tomorrow: 0,
      next7: 0,
      next10: 0,
      remaining: 0,
    };
    rows.forEach((expense, index) => {
      const amount = getEffectiveAmount(expense);
      if (flags.overdue[index]) totals.overdue += amount;
      if (flags.today[index]) totals.today += amount;
      if (flags.tomorrow[index]) totals.tomorrow += amount;
      if (flags.next7[index]) totals.next7 += amount;
      if (flags.next10[index]) totals.next10 += amount;
      if (flags.remaining[index]) totals.remaining += amount;
    });

    const buildSpans = (values: boolean[]) => {
      const map = new Map<number, number>();
      let index = 0;
      while (index < values.length) {
        if (!values[index]) {
          index += 1;
          continue;
        }
        let end = index + 1;
        while (end < values.length && values[end]) {
          end += 1;
        }
        map.set(index, end - index);
        index = end;
      }
      return map;
    };

    return {
      flags,
      totals,
      spans: {
        overdue: buildSpans(flags.overdue),
        today: buildSpans(flags.today),
        tomorrow: buildSpans(flags.tomorrow),
        next7: buildSpans(flags.next7),
        next10: buildSpans(flags.next10),
        remaining: buildSpans(flags.remaining),
      },
    };
  };

  const renderDateBarCells = (
    meta: ReturnType<typeof buildDateBarMeta>,
    rowIndex: number
  ) => {
    const barConfigs = [
      {
        key: "overdue",
        label: "Overdue",
        background: isDarkMode ? "#7F1D1D" : "#FCA5A5",
      },
      {
        key: "today",
        label: "Today",
        background: isDarkMode ? "#374151" : "#4B5563",
      },
      {
        key: "tomorrow",
        label: "Tomorrow",
        background: isDarkMode ? "#4B5563" : "#6B7280",
      },
      {
        key: "next7",
        label: "Within 7 Days",
        background: isDarkMode ? "#6B7280" : "#9CA3AF",
      },
      {
        key: "next10",
        label: "Within 10 Days",
        background: isDarkMode ? "#9CA3AF" : "#D1D5DB",
      },
      {
        key: "remaining",
        label: "Remaining",
        background: isDarkMode ? "#10B981" : "#A7F3D0",
      },
    ] as const;

    return barConfigs.map((config) => {
      const spans = meta.spans[config.key];
      const flags = meta.flags[config.key];
      const totalLabel = expenseUtils.formatCurrency(
        meta.totals[config.key] || 0
      );
      if (spans.has(rowIndex)) {
        const span = spans.get(rowIndex) ?? 1;
        return (
          <td
            key={`bar-${config.key}-${rowIndex}`}
            rowSpan={span}
            style={{
              width: "18px",
              padding: 0,
              backgroundColor: config.background,
              verticalAlign: "middle",
            }}
          >
            <Tooltip
              title={
                <span style={{ fontSize: "12px", fontWeight: 600 }}>
                  {totalLabel}
                </span>
              }
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#000",
                    color: "#fff",
                    fontSize: "12px",
                    px: 1,
                    py: 0.5,
                  },
                },
                arrow: { sx: { color: "#000" } },
              }}
            >
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    transform: "rotate(-90deg)",
                    whiteSpace: "nowrap",
                    fontSize: "10px",
                    letterSpacing: "0.06em",
                    color: isDarkMode ? "#F9FAFB" : "#1F2937",
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "center",
                    lineHeight: 1.2,
                  }}
                >
                  <span>{config.label}</span>
                  <span style={{ fontSize: "9px", fontWeight: 600 }}>
                    {totalLabel}
                  </span>
                </span>
              </div>
            </Tooltip>
          </td>
        );
      }
      if (flags[rowIndex]) {
        return null;
      }
      return (
        <td
          key={`bar-${config.key}-${rowIndex}`}
          style={{ width: "18px", padding: 0 }}
        />
      );
    });
  };

  const isOverdueForBars = (expense: Expense) => {
    if (!expense?.dueDate) return false;
    const todayUTC = getTodayLocal().getTime();
    const dueUTC = toUtcDateOnly(expense.dueDate).getTime();
    return dueUTC < todayUTC && !expense.isPaid;
  };

  const getExpectedGroupKey = (expense: Expense) => {
    const SUSPENSION_OVERDUE_WINDOW_DAYS = 14;
    if (!expense?.dueDate) return "remainder";
    const todayUTC = getTodayLocal();
    const tomorrowUTC = addDays(todayUTC, 1);
    const next7EndUTC = addDays(todayUTC, 7);
    const next10EndUTC = addDays(todayUTC, 10);
    const dueUTC = toUtcDateOnly(expense.dueDate);

    // Suspension date override: if it will suspend within 14 days, treat as "overdue"
    // inside Within 30 Days buckets, even if due date is in the future.
    if (
      !expense.isPaid &&
      expense.suspensionPeriodValue !== undefined &&
      expense.suspensionPeriodUnit
    ) {
      const suspensionDays = expenseUtils.getDaysUntilSuspensionFrom(
        todayUTC,
        expense.dueDate,
        expense.suspensionPeriodValue,
        expense.suspensionPeriodUnit
      );
      if (
        suspensionDays !== null &&
        suspensionDays <= SUSPENSION_OVERDUE_WINDOW_DAYS
      ) {
        return "overdue";
      }
    }

    if (dueUTC.getTime() < todayUTC.getTime()) return "overdue";
    if (isSameUtcDay(dueUTC, todayUTC)) return "today";
    if (isSameUtcDay(dueUTC, tomorrowUTC)) return "tomorrow";
    if (dueUTC.getTime() <= next7EndUTC.getTime()) return "next7";
    if (dueUTC.getTime() <= next10EndUTC.getTime()) return "next10";
    return "remainder";
  };

  // Table view row render helper
  const renderTableRow = (
    expense: Expense,
    section: ExpenseSection,
    options?: {
      hideStatusAndDue?: boolean;
      barCells?: React.ReactNode[];
      rowKey?: string;
      isSuspensionPreview?: boolean;
    }
  ) => {
    const cellPadding = "4px 8px";
    const compactCellPadding = "2px 4px";
    const checkboxCellPadding = "4px 4px";
    const hideStatusAndDue = options?.hideStatusAndDue || false;
    const barCells = options?.barCells;
    const rowKey = options?.rowKey || expense.id;
    const isSuspensionPreview = options?.isSuspensionPreview || false;
    const isSelected = selectedExpenseIds.has(expense.id);
    const isProjected = expense.id.startsWith("projected-");
    const isHighlighted = highlightedExpenseId === expense.id;
    const isSeriesHighlighted = seriesHighlightIds.has(expense.id);
    const isStruck = isExpenseStruck(expense);
    const isPaidSaving = isExpensePaidSaving(expense);
    const isAnimatingOut = isExpenseAnimatingOut(expense);
    const isDeleting = deleteLoadingIds.has(expense.id);
    const isDeleteAnimatingOut = deleteAnimatingOutIds.has(expense.id);
    const isPausedInExpected =
      section === "expected" && pausedExpectedIds.has(expense.id);
    const isPausingOut =
      section === "expected" && pauseAnimatingOutIds.has(expense.id);
    const isPausingIn =
      section === "expected" && pauseAnimatingInIds.has(expense.id);
    const isPinned = Boolean(pinnedExpenseIds?.has(expense.id));
    const styleSection =
      (expense as Expense & { pinnedFromSection?: ExpenseSection })
        .pinnedFromSection || section;
    const dueHighlight = getDueHighlightColor(expense, section);
    const expectedGroupKey =
      styleSection === "expected"
        ? (expense as Expense & { pinnedExpectedGroupKey?: string })
            .pinnedExpectedGroupKey || getExpectedGroupKey(expense)
        : null;
    const shouldForceOverdueHighlight =
      !dueHighlight &&
      (styleSection === "overdue" || expectedGroupKey === "overdue");
    // Suspension-warning case: shown with the overdue (red) treatment inside
    // "Within 30 Days" because its suspension date is near, even though the due
    // date itself is still in the future (not actually overdue). Mark these with
    // a blue left border so they're distinguishable from genuinely past-due rows.
    const isSuspensionWarning =
      styleSection === "expected" &&
      expectedGroupKey === "overdue" &&
      !!expense.dueDate &&
      toUtcDateOnly(expense.dueDate).getTime() >= getTodayLocal().getTime();
    const resolvedDueHighlight =
      dueHighlight ||
      (shouldForceOverdueHighlight
        ? hexToRgba(
            sectionColors.overdue || "#E53E3E",
            isDarkMode ? 0.18 : 0.12
          )
        : null);
    const baseOpacity = getSkipOpacity(expense, 1);
    const resolvedOpacity = isSuspensionPreview
      ? Math.min(0.6, baseOpacity)
      : baseOpacity;
    const showRowActions = !isSuspensionPreview && !readOnly;
    const isActionsOpen = actionsOpenId === expense.id;
    const dateBadgeStyle = getDueDateStyle(
      expense,
      styleSection,
      expectedGroupKey,
      isDarkMode
    );
    const invoiceBadgeStyle = getNeutralDateStyle(isDarkMode);
    const daysUntilDue =
      styleSection === "expected" ? getDaysUntilDue(expense.dueDate) : null;
    const suspensionDays =
      styleSection === "expected" ? getExpenseSuspensionDays(expense) : null;
    const isSuspendedExpense = isExpenseSuspended(expense);
    if (
      section === "expected" &&
      !expectedStatusLoggedRef.current.has(expense.id)
    ) {
      expectedStatusLoggedRef.current.add(expense.id);
    }
    return (
      <tr
        key={rowKey}
        style={{
          backgroundColor: isDeleteAnimatingOut
            ? "#ffffff"
            : isHighlighted
              ? isDarkMode
                ? "#805AD540"
                : "#805AD520"
              : isStruck
                ? isDarkMode
                  ? "#1a1a1a"
                  : "#f0f0f0"
                : isSelected
                  ? isDarkMode
                    ? "#805AD520"
                    : "#805AD510"
                  : resolvedDueHighlight || "transparent",
          border: isSuspendedExpense ? "2px solid #FF3B30" : undefined,
          borderLeft: isSuspensionWarning ? "3px solid #3182CE" : undefined,
          transition: "background-color 0.15s, opacity 0.2s, transform 0.2s",
          opacity: isDeleteAnimatingOut
            ? 0
            : isPaidSaving
              ? 0.55
              : resolvedOpacity,
          transform: isDeleteAnimatingOut
            ? "translateY(-10px)"
            : getSkipTransform(expense),
          animation: isPaidSaving
            ? "expenseBreathing 1.1s ease-in-out infinite"
            : isPausingOut
              ? "pauseOut 0.2s ease forwards"
              : isPausingIn
                ? "pauseIn 0.2s ease forwards"
                : isHighlighted
                  ? "pulseHighlight 1s ease-in-out 3"
                  : "none",
        }}
        className={[
          "expense-row",
          isHighlighted ? "expense-highlighted" : "",
          isSeriesHighlighted ? "expense-series-highlighted" : "",
          isSelected ? "expense-selected" : "",
          isStruck ? "expense-skipped-row" : "",
          isAnimatingOut ? "expense-skip-out" : "",
          isDeleting ? "expense-deleting" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {barCells}
        <td style={{ padding: checkboxCellPadding }}>
          {!isProjected && (
            <Checkbox
              checked={isSelected}
              onChange={(event) => {
                if (!isSuspensionPreview) {
                  toggleExpenseSelection(
                    expense.id,
                    (event.nativeEvent as MouseEvent).shiftKey
                  );
                }
              }}
              size="small"
              disabled={isSuspensionPreview}
              sx={{
                color: isDarkMode ? "#a0aec0" : "#718096",
                "&.Mui-checked": { color: "#805AD5" },
              }}
            />
          )}
        </td>
        {!hideStatusAndDue && (
          <td style={{ padding: compactCellPadding, whiteSpace: "nowrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {styleSection === "overdue" && !isSuspensionPreview && (
                <Tooltip title="Days Past Due" arrow>
                  <span
                    style={{
                      backgroundColor: "#E53E3E",
                      color: "white",
                      width: "26px",
                      height: "26px",
                      borderRadius: "50%",
                      fontSize: "11px",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {getDaysOverdue(expense.dueDate)}
                  </span>
                </Tooltip>
              )}
              {styleSection === "overdue" &&
                expense.suspensionPeriodValue !== undefined &&
                expense.suspensionPeriodUnit && (
                  <Tooltip
                    title={(() => {
                      const days = expenseUtils.getDaysUntilSuspension(
                        expense.dueDate,
                        expense.suspensionPeriodValue,
                        expense.suspensionPeriodUnit
                      );
                      if (days === null) return "";
                      if (days > 0)
                        return `Will be suspended in ${days} day${days === 1 ? "" : "s"}`;
                      if (days === 0) return "Suspension date is today";
                      return `Suspended ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
                    })()}
                    arrow
                  >
                    <span
                      style={{
                        backgroundColor: getSuspensionDateStyle(
                          expense,
                          isDarkMode
                        ).backgroundColor,
                        color: getSuspensionDateStyle(expense, isDarkMode)
                          .color,
                        width: "26px",
                        height: "26px",
                        borderRadius: "50%",
                        fontSize: "11px",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {getSuspensionBadgeLabel(
                        getExpenseSuspensionDays(expense)
                      )}
                    </span>
                  </Tooltip>
                )}
              {styleSection === "expected" && daysUntilDue !== null && (
                <Tooltip
                  title={(() => {
                    if (daysUntilDue > 0)
                      return `Due in ${daysUntilDue} day${daysUntilDue === 1 ? "" : "s"}`;
                    if (daysUntilDue === 0) return "Due today";
                    return `Overdue by ${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) === 1 ? "" : "s"}`;
                  })()}
                  arrow
                >
                  <span
                    style={{
                      ...getExpectedDueBadgeStyle(expectedGroupKey, isDarkMode),
                      width: "26px",
                      height: "26px",
                      borderRadius: "50%",
                      fontSize: "11px",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {Math.abs(daysUntilDue)}
                  </span>
                </Tooltip>
              )}
              {styleSection === "expected" &&
                expense.suspensionPeriodValue !== undefined &&
                expense.suspensionPeriodUnit &&
                suspensionDays !== null && (
                  <Tooltip
                    title={(() => {
                      if (suspensionDays > 0)
                        return `Suspends in ${suspensionDays} day${suspensionDays === 1 ? "" : "s"}`;
                      if (suspensionDays === 0)
                        return "Suspension date is today";
                      return `Suspended ${Math.abs(suspensionDays)} day${Math.abs(suspensionDays) === 1 ? "" : "s"} ago`;
                    })()}
                    arrow
                  >
                    <span
                      style={{
                        ...getSuspensionDateStyle(expense, isDarkMode),
                        width: "26px",
                        height: "26px",
                        borderRadius: "50%",
                        fontSize: "11px",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {getSuspensionBadgeLabel(suspensionDays)}
                    </span>
                  </Tooltip>
                )}
            </div>
          </td>
        )}
        {!hideStatusAndDue && (
          <td style={{ padding: cellPadding, whiteSpace: "nowrap" }}>
            <span
              style={{
                ...dateBadgeStyle,
                padding: "3px 8px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              {formatDueDate(expense.dueDate)}
            </span>
          </td>
        )}
        {!hideStatusAndDue && (
          <td style={{ padding: cellPadding, whiteSpace: "nowrap" }}>
            {expense.invoicedDate ? (
              <span
                style={{
                  ...invoiceBadgeStyle,
                  padding: "3px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                {formatDueDate(new Date(expense.invoicedDate))}
              </span>
            ) : (
              <span
                style={{
                  color: isDarkMode ? "#718096" : "#A0AEC0",
                  fontSize: "12px",
                }}
              >
                —
              </span>
            )}
          </td>
        )}
        <td style={{ padding: cellPadding, fontWeight: 500 }}>
          {renderExpenseTitle(expense, { isSuspensionPreview })}
        </td>
        <td style={{ padding: cellPadding, color: "#718096" }}>
          {expense.vendor || "—"}
        </td>
        <td style={{ padding: cellPadding }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {(() => {
              const billBudgetType = getBillBudgetType(expense);
              const isBudget = billBudgetType === "budget";
              return (
                <Tooltip title={isBudget ? "Budget" : "Bill"} arrow>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      color: isBudget ? "#38A169" : "#4B5563",
                    }}
                  >
                    {isBudget ? (
                      <SavingsIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ReceiptLongIcon sx={{ fontSize: 16 }} />
                    )}
                  </span>
                </Tooltip>
              );
            })()}
            <span style={categoryTagStyle}>{expense.internalCategory}</span>
          </div>
        </td>
        <td style={{ padding: cellPadding }}>
          {expense.isRecurring ? (
            <span style={{ ...recurringTagStyle, textTransform: "capitalize" }}>
              {expense.recurrencePattern}
            </span>
          ) : (
            <span
              style={{
                backgroundColor: "#71809620",
                color: "#718096",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "12px",
              }}
            >
              One-time
            </span>
          )}
        </td>
        <td
          style={{
            padding: cellPadding,
            textAlign: "center",
          }}
        >
          {(() => {
            const isLinked = Boolean(expense.linkedTransactionId);
            if (!onLinkTransaction || isProjected) {
              return isLinked ? (
                <CheckCircleIcon sx={{ color: "#38A169", fontSize: 20 }} />
              ) : (
                <CancelIcon sx={{ color: "#E53E3E", fontSize: 20 }} />
              );
            }
            return (
              <Tooltip
                title={
                  isLinked
                    ? "Transaction linked — click to change"
                    : "No transaction linked — click to link"
                }
                arrow
              >
                <IconButton
                  size="small"
                  onClick={() => setLinkTxnExpense(expense)}
                  sx={{
                    p: 0.25,
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#2D3748" : "#F7FAFC",
                    },
                  }}
                >
                  {isLinked ? (
                    <CheckCircleIcon sx={{ color: "#38A169", fontSize: 20 }} />
                  ) : (
                    <CancelIcon sx={{ color: "#E53E3E", fontSize: 20 }} />
                  )}
                </IconButton>
              </Tooltip>
            );
          })()}
        </td>
        <td style={{ padding: cellPadding, position: "relative" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "8px",
            }}
          >
            {showRowActions && (
              <div
                className="expense-row-actions"
                style={{
                  // Click-toggled action menu: the buttons slide out from the
                  // kebab toggle on the row's right when the row's menu is open
                  // (actionsOpenId === expense.id), and slide back in when
                  // toggled off or on an outside click. The strip is anchored to
                  // the LEFT of the toggle (right: 44px) and pinned within the
                  // row's vertical bounds so it never overlaps adjacent rows.
                  position: "absolute",
                  right: "44px",
                  top: "50%",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  justifyContent: "flex-end",
                  opacity: isActionsOpen ? 1 : 0,
                  transform: isActionsOpen
                    ? "translate(0, -50%)"
                    : "translate(16px, -50%)",
                  transition: "opacity 0.18s ease, transform 0.18s ease",
                  pointerEvents: isActionsOpen ? "auto" : "none",
                  zIndex: 6,
                  backgroundColor: isDarkMode
                    ? "rgba(26,26,26,0.96)"
                    : "rgba(255,255,255,0.96)",
                  borderRadius: 999,
                  padding: "3px 6px",
                  boxShadow: isDarkMode
                    ? "0 0 0 1px rgba(255,255,255,0.08)"
                    : "0 1px 6px rgba(0,0,0,0.14)",
                }}
              >
                {onTogglePinExpense && (
                  <Tooltip
                    title={isPinned ? "Unpin from Today" : "Pin to Today"}
                    arrow
                  >
                    <IconButton
                      onClick={() => onTogglePinExpense(expense)}
                      size="small"
                      sx={{
                        backgroundColor: isPinned ? "#38A169" : "#4A5568",
                        color: "white",
                        width: 28,
                        height: 28,
                        "&:hover": {
                          backgroundColor: isPinned ? "#2F855A" : "#2D3748",
                        },
                      }}
                    >
                      <PushPinIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {onResetOccurrences && expense.isRecurring && !isProjected && (
                  <Tooltip
                    title={
                      resetLoadingIds.has(expense.id)
                        ? "Resetting..."
                        : "Reset Occurrences"
                    }
                    arrow
                  >
                    <span>
                      <IconButton
                        onClick={() => void handleResetClick(expense)}
                        size="small"
                        disabled={resetLoadingIds.has(expense.id)}
                        sx={{
                          backgroundColor: "#DD6B20",
                          color: "white",
                          width: 28,
                          height: 28,
                          "&:hover": { backgroundColor: "#C05621" },
                          "&.Mui-disabled": {
                            backgroundColor: "#DD6B20",
                            color: "white",
                            opacity: 0.6,
                          },
                        }}
                      >
                        {resetLoadingIds.has(expense.id) ? (
                          <CircularProgress size={14} sx={{ color: "white" }} />
                        ) : (
                          <RestartAltIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {renderOccurrencesAction(expense)}
                <Tooltip title="Edit" arrow>
                  <IconButton
                    onClick={() => onEditExpense(expense)}
                    size="small"
                    sx={{
                      backgroundColor: "#4299E1",
                      color: "white",
                      width: 28,
                      height: 28,
                      "&:hover": { backgroundColor: "#3182CE" },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                {(onPostponeExpense || onPostponeDebt) && (
                  <Tooltip
                    title={expense.isPostponed ? "Postponed" : "Postpone"}
                    arrow
                  >
                    <IconButton
                      onClick={(e) => openPostpone(e, expense)}
                      size="small"
                      sx={{
                        backgroundColor: expense.isPostponed
                          ? "#DD6B20"
                          : "#718096",
                        color: "white",
                        width: 28,
                        height: 28,
                        "&:hover": {
                          backgroundColor: expense.isPostponed
                            ? "#C05621"
                            : "#4A5568",
                        },
                      }}
                    >
                      <SnoozeIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {onDuplicateExpense && (
                  <Tooltip title="Duplicate" arrow>
                    <IconButton
                      onClick={() => onDuplicateExpense(expense)}
                      size="small"
                      sx={{
                        backgroundColor: "#805AD5",
                        color: "white",
                        width: 28,
                        height: 28,
                        "&:hover": { backgroundColor: "#6B46C1" },
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {onShareExpense && (
                  <Tooltip title="Share" arrow>
                    <IconButton
                      onClick={() => onShareExpense(expense)}
                      size="small"
                      sx={{
                        backgroundColor: "#4299E1",
                        color: "white",
                        width: 28,
                        height: 28,
                        "&:hover": { backgroundColor: "#3182CE" },
                      }}
                    >
                      <ShareIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {onRescheduleExpense && (
                  <Tooltip title="Reschedule" arrow>
                    <IconButton
                      onClick={() => openRescheduleModal(expense)}
                      size="small"
                      sx={{
                        backgroundColor: "#38B2AC",
                        color: "white",
                        width: 28,
                        height: 28,
                        "&:hover": { backgroundColor: "#319795" },
                      }}
                    >
                      <EventIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {section === "expected" && (
                  <Tooltip
                    title={isPausedInExpected ? "Unpause" : "Pause"}
                    arrow
                  >
                    <IconButton
                      onClick={() =>
                        isPausedInExpected
                          ? setPausedExpectedIds((prev) => {
                              const next = new Set(prev);
                              next.delete(expense.id);
                              console.log("[ExpenseList] Unpause expense", {
                                expenseId: expense.id,
                              });
                              return next;
                            })
                          : handlePauseExpense(expense)
                      }
                      size="small"
                      sx={{
                        backgroundColor: isPausedInExpected
                          ? "#38A169"
                          : "#2D3748",
                        color: "white",
                        width: 28,
                        height: 28,
                        "&:hover": {
                          backgroundColor: isPausedInExpected
                            ? "#2F855A"
                            : "#1A202C",
                        },
                      }}
                    >
                      {isPausedInExpected ? (
                        <UndoIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <PauseCircleOutlineIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={isDeleting ? "Deleting..." : "Delete"} arrow>
                  <span>
                    <IconButton
                      onClick={() => void handleDeleteClick(expense)}
                      size="small"
                      disabled={isDeleting}
                      sx={{
                        backgroundColor: "#E53E3E",
                        color: "white",
                        width: 28,
                        height: 28,
                        "&:hover": { backgroundColor: "#C53030" },
                        "&.Mui-disabled": {
                          backgroundColor: "#E53E3E",
                          color: "white",
                          opacity: 0.6,
                        },
                      }}
                    >
                      {isDeleting ? (
                        <CircularProgress size={14} sx={{ color: "white" }} />
                      ) : (
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {!expense.isPaid && (
                  <Tooltip title="Mark as Paid" arrow>
                    <IconButton
                      onClick={() => openPaidModal(expense)}
                      size="small"
                      sx={{
                        backgroundColor: "#38A169",
                        color: "white",
                        width: 28,
                        height: 28,
                        "&:hover": { backgroundColor: "#2F855A" },
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {onSkipExpense && !expense.isPaid && (
                  <Tooltip title={expense.isSkipped ? "Unskip" : "Skip"} arrow>
                    <IconButton
                      onClick={() => handleSkipClick(expense)}
                      size="small"
                      sx={{
                        backgroundColor: expense.isSkipped
                          ? "#805AD5"
                          : "#718096",
                        color: "white",
                        width: 28,
                        height: 28,
                        "&:hover": {
                          backgroundColor: expense.isSkipped
                            ? "#6B46C1"
                            : "#4A5568",
                        },
                      }}
                    >
                      {expense.isSkipped ? (
                        <UndoIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <BlockIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}
            <span style={{ fontWeight: 600 }}>
              {expenseUtils.formatCurrency(getEffectiveAmount(expense))}
            </span>
            {showRowActions && (
              <Tooltip
                title={isActionsOpen ? "Close actions" : "Actions"}
                arrow
              >
                <IconButton
                  className="expense-row-actions-toggle"
                  size="small"
                  aria-label="Toggle row actions"
                  onClick={(event) => {
                    event.stopPropagation();
                    setActionsOpenId((prev) =>
                      prev === expense.id ? null : expense.id
                    );
                  }}
                  sx={{
                    width: 28,
                    height: 28,
                    zIndex: 7,
                    color: isActionsOpen
                      ? "#fff"
                      : isDarkMode
                        ? "#a0aec0"
                        : "#4a5568",
                    backgroundColor: isActionsOpen ? "#805AD5" : "transparent",
                    "&:hover": {
                      backgroundColor: isActionsOpen
                        ? "#6B46C1"
                        : isDarkMode
                          ? "#2D3748"
                          : "#EDF2F7",
                    },
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      style={
        {
          "--expense-hover-bg": isDarkMode ? "#2d2d2d" : "#f7fafc",
        } as React.CSSProperties
      }
    >
      {/* Pulse highlight animation for newly added expenses */}
      <style>
        {`
          @keyframes pulseHighlight {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(128, 90, 213, 0.4);
            }
            50% {
              box-shadow: 0 0 0 8px rgba(128, 90, 213, 0);
            }
          }
          @keyframes pauseOut {
            0% {
              opacity: 1;
              transform: translateY(0);
            }
            100% {
              opacity: 0;
              transform: translateY(-8px);
            }
          }
          @keyframes pauseIn {
            0% {
              opacity: 0;
              transform: translateY(8px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes expenseBreathing {
            0%, 100% {
              opacity: 0.55;
              transform: scale(1);
            }
            50% {
              opacity: 0.3;
              transform: scale(0.994);
            }
          }
          @keyframes copyToastInOut {
            0% {
              opacity: 0;
              transform: translateY(10px);
            }
            15% {
              opacity: 1;
              transform: translateY(0);
            }
            80% {
              opacity: 1;
              transform: translateY(-2px);
            }
            100% {
              opacity: 0;
              transform: translateY(-14px);
            }
          }
          .expense-highlighted {
            position: relative;
          }
          .expense-highlighted::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border: 2px solid #805AD5;
            border-radius: 4px;
            animation: pulseHighlight 1s ease-in-out 3;
            pointer-events: none;
          }
          /* Recurring-series badge highlight: tint every cell of the row.
             The slow (3s) transition lives on the base td so that when the
             highlighted class is removed (after the 10s hold), the tint
             slow-fades out. Applying it on td (not tr) keeps the overlay
             reliable across browsers' table-row background quirks. */
          .expense-row > td {
            transition: background-color 3s ease-out, box-shadow 3s ease-out;
          }
          .expense-series-highlighted > td {
            background-color: rgba(128, 90, 213, 0.22) !important;
            transition: background-color 0.2s ease-in, box-shadow 0.2s ease-in;
          }
          .expense-series-highlighted > td:first-child {
            box-shadow: inset 3px 0 0 0 #805AD5;
          }
          .expense-skipped-row td {
            text-decoration: line-through;
            text-decoration-color: rgba(113, 128, 150, 0.8);
          }
          .expense-skipped-row td:last-child {
            text-decoration: none;
          }
          .expense-skip-out {
            opacity: 0;
            transform: translateY(-8px);
          }
          .expense-row:not(.expense-highlighted):not(.expense-selected):not(.expense-skipped-row):hover {
            background-color: var(--expense-hover-bg) !important;
          }
          /* Each row carries an inline transform, so it is its own stacking
             context. The action strip drops below the row (top:100%) into the
             next row's space; without lifting the hovered row, the later sibling
             row paints on top of the strip and intercepts its clicks. Raise the
             hovered/selected row's stacking context above its siblings so the
             overflowing action buttons sit above — and stay clickable over — the
             row beneath. */
          .expense-row:hover,
          .expense-selected {
            position: relative;
            z-index: 20;
          }
          /* The row action strip is now click-toggled (driven by React state),
             not hover — see the inline opacity/transform/pointer-events on the
             .expense-row-actions element and the kebab toggle button. */
        `}
      </style>
      {copyToastOpen && (
        <div
          key={copyToastKey}
          style={{
            position: "fixed",
            left: "50%",
            bottom: 22,
            transform: "translateX(-50%)",
            backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
            color: isDarkMode ? "#e2e8f0" : "#1a202c",
            border: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
            borderRadius: 999,
            padding: "10px 14px",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.01em",
            boxShadow: isDarkMode
              ? "0 12px 30px rgba(0,0,0,0.5)"
              : "0 12px 30px rgba(0,0,0,0.15)",
            zIndex: 2000,
            pointerEvents: "none",
            animation: "copyToastInOut 1.35s ease forwards",
          }}
        >
          Copied Expense ID!
        </div>
      )}
      {selectedExpenseIds.size > 0 && (
        <Box
          aria-live="polite"
          sx={{
            position: "fixed",
            left: "50%",
            bottom: 22,
            transform: "translateX(-50%)",
            zIndex: 1999,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            py: 1.25,
            borderRadius: "999px",
            backgroundColor: isDarkMode ? "#050505" : "#FFFFFF",
            color: isDarkMode ? "#F7FAFC" : "#111827",
            border: `1px solid ${isDarkMode ? "#2D3748" : "#CBD5E0"}`,
            boxShadow: isDarkMode
              ? "0 18px 40px rgba(0,0,0,0.55)"
              : "0 18px 40px rgba(15,23,42,0.18)",
            pointerEvents: "none",
          }}
        >
          <ReceiptLongIcon sx={{ fontSize: 18, color: "#805AD5" }} />
          <Typography
            component="span"
            sx={{
              fontSize: 13,
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {selectedExpenseIds.size} selected
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: 18,
              fontWeight: 800,
              color: selectedExpensesTotal > 0 ? "#E53E3E" : "inherit",
              whiteSpace: "nowrap",
            }}
          >
            {selectedExpensesTotal > 0 ? "-" : ""}
            {expenseUtils.formatCurrency(selectedExpensesTotal)}
          </Typography>
        </Box>
      )}
      {/* Bulk Actions Toolbar */}
      {selectedExpenseIds.size > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            padding: "12px 16px",
            marginBottom: "16px",
            backgroundColor: isDarkMode ? "#805AD520" : "#805AD510",
            borderRadius: "8px",
            border: `1px solid ${isDarkMode ? "#805AD540" : "#805AD530"}`,
          }}
        >
          <span style={{ fontWeight: 600, color: "#805AD5" }}>
            {selectedExpenseIds.size} selected
          </span>
          <Button
            variant="contained"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleBulkDuplicate}
            sx={{
              backgroundColor: "#805AD5",
              "&:hover": { backgroundColor: "#6B46C1" },
            }}
          >
            Duplicate
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
            sx={{
              backgroundColor: "#E53E3E",
              "&:hover": { backgroundColor: "#C53030" },
            }}
          >
            Delete
          </Button>
          {onLinkAsRecurring && (
            <Button
              variant="contained"
              size="small"
              startIcon={<LinkIcon />}
              onClick={() => onLinkAsRecurring(Array.from(selectedExpenseIds))}
              sx={{
                backgroundColor: "#805AD5",
                "&:hover": { backgroundColor: "#6B46C1" },
              }}
            >
              Link as Recurring
            </Button>
          )}
          {onMergeExpenses && selectedExpenseIds.size >= 2 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CallMergeIcon />}
              onClick={() => onMergeExpenses(Array.from(selectedExpenseIds))}
              sx={{
                backgroundColor: "#805AD5",
                "&:hover": { backgroundColor: "#6B46C1" },
              }}
            >
              Merge
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={clearSelection}
            sx={{
              borderColor: "#805AD5",
              color: "#805AD5",
              "&:hover": {
                borderColor: "#6B46C1",
                backgroundColor: "#805AD510",
              },
            }}
          >
            Clear Selection
          </Button>
        </Box>
      )}

      {/* Recurrence-based grouping view */}
      {groupingMode === "recurrence" && recurrenceGroupedExpenses && (
        <>
          {(
            [
              "monthly",
              "quarterly",
              "annually",
              "one-time",
            ] as RecurrenceGroup[]
          ).map((group) => {
            const groupData = recurrenceGroupedExpenses[group];
            if (groupData.expenses.length === 0) return null;

            const isCollapsed = isSearchActive
              ? false
              : collapsedRecurrenceSections.has(group);
            const groupColor = getRecurrenceGroupColor(group);
            const hasSubGroups =
              !!groupData.subGroups && groupData.subGroups.length > 0;

            return (
              <div key={group} style={sectionContainerStyle}>
                <div
                  style={{
                    ...sectionHeaderStyle(isCollapsed),
                    borderLeft: `4px solid ${groupColor}`,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => toggleRecurrenceSection(group)}
                >
                  <div style={headingStyle}>
                    <span style={{ marginRight: "8px", fontSize: "14px" }}>
                      {isCollapsed ? "▶" : "▼"}
                    </span>
                    {getRecurrenceGroupLabel(group)}
                    <span style={badgeStyle(groupColor)}>
                      {groupData.expenses.length}
                    </span>
                  </div>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: groupData.total > 0 ? "#E53E3E" : undefined,
                    }}
                  >
                    {groupData.total > 0
                      ? `-${expenseUtils.formatCurrency(groupData.total)}`
                      : expenseUtils.formatCurrency(groupData.total)}
                  </div>
                </div>

                {!isCollapsed && viewMode === "table" && (
                  <div style={sectionContentStyle}>
                    {/* If there are sub-groups (daily/weekly), show them */}
                    {hasSubGroups ? (
                      <>
                        {groupData.subGroups!.map((subGroup) => {
                          const isSubGroupCollapsed = isSearchActive
                            ? false
                            : collapsedRecurrenceSubGroups.has(
                                subGroup.pattern
                              );
                          const isHighFrequency =
                            subGroup.pattern === "weekly" ||
                            subGroup.pattern === "daily";

                          return (
                            <div
                              key={subGroup.pattern}
                              style={{ marginBottom: "16px" }}
                            >
                              {/* Sub-group header */}
                              <div
                                onClick={() =>
                                  toggleRecurrenceSubGroup(subGroup.pattern)
                                }
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "10px 12px",
                                  backgroundColor: isDarkMode
                                    ? "#1a1a1a"
                                    : "white",
                                  borderRadius: isSubGroupCollapsed
                                    ? "6px"
                                    : "6px 6px 0 0",
                                  cursor: "pointer",
                                  userSelect: "none",
                                  borderLeft: `3px solid ${isHighFrequency ? "#DD6B20" : groupColor}`,
                                  marginBottom: isSubGroupCollapsed ? 0 : "1px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <span style={{ fontSize: "12px" }}>
                                    {isSubGroupCollapsed ? "▶" : "▼"}
                                  </span>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontSize: "14px",
                                    }}
                                  >
                                    {subGroup.label}
                                  </span>
                                  <span
                                    style={{
                                      backgroundColor: `${isHighFrequency ? "#DD6B20" : groupColor}20`,
                                      color: isHighFrequency
                                        ? "#DD6B20"
                                        : groupColor,
                                      fontSize: "11px",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {subGroup.expenses.length}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      subGroup.total > 0
                                        ? "#E53E3E"
                                        : undefined,
                                    fontSize: "14px",
                                  }}
                                >
                                  {subGroup.total > 0
                                    ? `-${expenseUtils.formatCurrency(subGroup.total)}`
                                    : expenseUtils.formatCurrency(
                                        subGroup.total
                                      )}
                                </span>
                              </div>

                              {/* Sub-group table */}
                              {!isSubGroupCollapsed && (
                                <table
                                  style={{
                                    width: "100%",
                                    tableLayout: "fixed",
                                    borderCollapse: "collapse",
                                    fontSize: "14px",
                                    backgroundColor: isDarkMode
                                      ? "#1a1a1a"
                                      : "white",
                                    borderRadius: "0 0 6px 6px",
                                  }}
                                >
                                  <colgroup>
                                    {[
                                      <col
                                        key="rec-sub-select"
                                        style={{ width: "40px" }}
                                      />,
                                      <col
                                        key="rec-sub-title"
                                        style={{ width: "auto" }}
                                      />,
                                      <col
                                        key="rec-sub-vendor"
                                        style={{ width: "auto" }}
                                      />,
                                      <col
                                        key="rec-sub-type"
                                        style={{ width: "110px" }}
                                      />,
                                      <col
                                        key="rec-sub-frequency"
                                        style={{ width: "90px" }}
                                      />,
                                      <col
                                        key="rec-sub-amount"
                                        style={{ width: "100px" }}
                                      />,
                                    ]}
                                  </colgroup>
                                  <thead>
                                    <tr
                                      style={{
                                        borderBottom: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
                                        color: isDarkMode
                                          ? "#a0aec0"
                                          : "#718096",
                                        fontSize: "12px",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                      }}
                                    >
                                      <th
                                        style={{
                                          padding: "8px 4px",
                                          width: "40px",
                                        }}
                                      >
                                        <Checkbox
                                          size="small"
                                          disabled
                                          sx={{ opacity: 0.3 }}
                                        />
                                      </th>
                                      {renderSortableHeader("name", "Title")}
                                      {renderSortableHeader("vendor", "Vendor")}
                                      {renderSortableHeader(
                                        "internalCategory",
                                        "Type"
                                      )}
                                      {renderSortableHeader(
                                        "recurrencePattern",
                                        "Frequency"
                                      )}
                                      {renderLinkedHeader()}
                                      {renderSortableHeader(
                                        "amount",
                                        "Amount",
                                        "right"
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {subGroup.expenses.map((expense) =>
                                      renderTableRow(
                                        expense,
                                        "thisMonth" as ExpenseSection,
                                        { hideStatusAndDue: true }
                                      )
                                    )}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      /* Regular table without sub-groups */
                      <table
                        style={{
                          width: "100%",
                          tableLayout: "fixed",
                          borderCollapse: "collapse",
                          fontSize: "14px",
                        }}
                      >
                        <colgroup>
                          {[
                            <col key="rec-select" style={{ width: "40px" }} />,
                            <col key="rec-title" style={{ width: "auto" }} />,
                            <col key="rec-vendor" style={{ width: "auto" }} />,
                            <col key="rec-type" style={{ width: "110px" }} />,
                            <col
                              key="rec-frequency"
                              style={{ width: "90px" }}
                            />,
                            <col key="rec-linked" style={{ width: "70px" }} />,
                            <col key="rec-amount" style={{ width: "100px" }} />,
                          ]}
                        </colgroup>
                        <thead>
                          <tr
                            style={{
                              borderBottom: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
                              color: isDarkMode ? "#a0aec0" : "#718096",
                              fontSize: "12px",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            <th style={{ padding: "8px 4px", width: "40px" }}>
                              <Checkbox
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={toggleSelectAll}
                                size="small"
                                sx={{
                                  color: isDarkMode ? "#a0aec0" : "#718096",
                                  "&.Mui-checked": { color: "#805AD5" },
                                  "&.MuiCheckbox-indeterminate": {
                                    color: "#805AD5",
                                  },
                                }}
                              />
                            </th>
                            {renderSortableHeader("name", "Title")}
                            {renderSortableHeader("vendor", "Vendor")}
                            {renderSortableHeader("internalCategory", "Type")}
                            {renderSortableHeader(
                              "recurrencePattern",
                              "Frequency"
                            )}
                            {renderLinkedHeader()}
                            {renderSortableHeader("amount", "Amount", "right")}
                          </tr>
                        </thead>
                        <tbody>
                          {groupData.expenses.map((expense) =>
                            renderTableRow(
                              expense,
                              "thisMonth" as ExpenseSection,
                              { hideStatusAndDue: true }
                            )
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Card view for recurrence grouping */}
                {!isCollapsed && viewMode === "card" && (
                  <div style={sectionContentStyle}>
                    {hasSubGroups ? (
                      <>
                        {groupData.subGroups!.map((subGroup) => {
                          const isSubGroupCollapsed = isSearchActive
                            ? false
                            : collapsedRecurrenceSubGroups.has(
                                subGroup.pattern
                              );
                          const isHighFrequency =
                            subGroup.pattern === "weekly" ||
                            subGroup.pattern === "daily";

                          return (
                            <div
                              key={subGroup.pattern}
                              style={{ marginBottom: "16px" }}
                            >
                              {/* Sub-group header */}
                              <div
                                onClick={() =>
                                  toggleRecurrenceSubGroup(subGroup.pattern)
                                }
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "10px 12px",
                                  backgroundColor: isDarkMode
                                    ? "#1a1a1a"
                                    : "white",
                                  borderRadius: isSubGroupCollapsed
                                    ? "6px"
                                    : "6px 6px 0 0",
                                  cursor: "pointer",
                                  userSelect: "none",
                                  borderLeft: `3px solid ${isHighFrequency ? "#DD6B20" : groupColor}`,
                                  marginBottom: isSubGroupCollapsed ? 0 : "8px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <span style={{ fontSize: "12px" }}>
                                    {isSubGroupCollapsed ? "▶" : "▼"}
                                  </span>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontSize: "14px",
                                    }}
                                  >
                                    {subGroup.label}
                                  </span>
                                  <span
                                    style={{
                                      backgroundColor: `${isHighFrequency ? "#DD6B20" : groupColor}20`,
                                      color: isHighFrequency
                                        ? "#DD6B20"
                                        : groupColor,
                                      fontSize: "11px",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {subGroup.expenses.length}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      subGroup.total > 0
                                        ? "#E53E3E"
                                        : undefined,
                                    fontSize: "14px",
                                  }}
                                >
                                  {subGroup.total > 0
                                    ? `-${expenseUtils.formatCurrency(subGroup.total)}`
                                    : expenseUtils.formatCurrency(
                                        subGroup.total
                                      )}
                                </span>
                              </div>

                              {/* Sub-group cards */}
                              {!isSubGroupCollapsed && (
                                <div style={{ paddingLeft: "8px" }}>
                                  {subGroup.expenses.map((expense) => (
                                    <div
                                      key={expense.id}
                                      style={{
                                        ...expenseItemStyle,
                                        borderLeft: `3px solid ${isHighFrequency ? "#DD6B20" : groupColor}40`,
                                        opacity: getSkipOpacity(expense, 1),
                                        transform: getSkipTransform(expense),
                                        transition:
                                          "opacity 0.2s, transform 0.2s",
                                        textDecoration:
                                          getSkipTextDecoration(expense),
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <div>
                                          <div style={{ fontWeight: "bold" }}>
                                            {renderExpenseTitle(expense)}
                                          </div>
                                          <div
                                            style={{
                                              color: "#718096",
                                              fontSize: "13px",
                                            }}
                                          >
                                            {expense.vendor || "No vendor"}
                                          </div>
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "flex-end",
                                              gap: "6px",
                                            }}
                                          >
                                            {onRescheduleExpense && (
                                              <Tooltip title="Reschedule" arrow>
                                                <IconButton
                                                  onClick={() =>
                                                    openRescheduleModal(expense)
                                                  }
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: "#38B2AC",
                                                    color: "white",
                                                    width: 24,
                                                    height: 24,
                                                    "&:hover": {
                                                      backgroundColor:
                                                        "#319795",
                                                    },
                                                  }}
                                                >
                                                  <EventIcon
                                                    sx={{ fontSize: 14 }}
                                                  />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            <div style={{ fontWeight: 600 }}>
                                              {expenseUtils.formatCurrency(
                                                getEffectiveAmount(expense)
                                              )}
                                            </div>
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#718096",
                                            }}
                                          >
                                            {formatDueDate(expense.dueDate)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      /* Regular cards without sub-groups */
                      groupData.expenses.map((expense) => (
                        <div
                          key={expense.id}
                          style={{
                            ...expenseItemStyle,
                            opacity: getSkipOpacity(expense, 1),
                            transform: getSkipTransform(expense),
                            transition: "opacity 0.2s, transform 0.2s",
                            textDecoration: getSkipTextDecoration(expense),
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: "bold" }}>
                                {renderExpenseTitle(expense)}
                              </div>
                              <div
                                style={{ color: "#718096", fontSize: "13px" }}
                              >
                                {expense.vendor || "No vendor"}
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  gap: "6px",
                                }}
                              >
                                {onRescheduleExpense && (
                                  <Tooltip title="Reschedule" arrow>
                                    <IconButton
                                      onClick={() =>
                                        openRescheduleModal(expense)
                                      }
                                      size="small"
                                      sx={{
                                        backgroundColor: "#38B2AC",
                                        color: "white",
                                        width: 24,
                                        height: 24,
                                        "&:hover": {
                                          backgroundColor: "#319795",
                                        },
                                      }}
                                    >
                                      <EventIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <div style={{ fontWeight: 600 }}>
                                  {expenseUtils.formatCurrency(
                                    getEffectiveAmount(expense)
                                  )}
                                </div>
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#718096" }}
                              >
                                {formatDueDate(expense.dueDate)}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              marginTop: "4px",
                              display: "flex",
                              gap: "4px",
                              flexWrap: "wrap",
                            }}
                          >
                            <span style={categoryTagStyle}>
                              {expense.internalCategory}
                            </span>
                            {expense.isRecurring && (
                              <span
                                style={{
                                  ...recurringTagStyle,
                                  textTransform: "capitalize",
                                }}
                              >
                                {expense.recurrencePattern}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* Time-based grouping view (default) */}
      {groupingMode === "time" &&
        sectionOrder.map((section) => {
          const sectionExpenses = sortedExpenses[section];
          const isOverdueSection = section === "overdue";
          const total =
            sectionTotals.find((s) => s.section === section)?.total || 0;
          const isCollapsed = isSearchActive
            ? false
            : collapsedSections.has(section);

          const overduePreviewExpenses = isOverdueSection
            ? suspensionPreviewRows.map((row) => row.expense)
            : [];
          const overduePreviewIds = isOverdueSection
            ? suspensionPreviewIds
            : new Set<string>();

          // Skip empty sections. During search we only keep sections with matches.
          if (
            !isLoading &&
            sectionExpenses.length === 0 &&
            overduePreviewExpenses.length === 0 &&
            (isSearchActive || section !== "nextMonth")
          )
            return null;

          const displayExpenses =
            section === "nextMonth"
              ? sortByDueDateAsc(sectionExpenses)
              : section === "thisMonth"
                ? sortByDueDateAsc(sectionExpenses)
                : isOverdueSection
                  ? sortExpenses([
                      ...sectionExpenses,
                      ...overduePreviewExpenses,
                    ])
                  : sectionExpenses;
          const overdueVisibleCount = isOverdueSection
            ? sectionExpenses.length
            : displayExpenses.length;
          const displayRows = isOverdueSection
            ? displayExpenses.map((expense) => ({
                expense,
                isSuspensionPreview: overduePreviewIds.has(expense.id),
                rowKey: overduePreviewIds.has(expense.id)
                  ? `suspension-preview-${expense.id}`
                  : expense.id,
              }))
            : displayExpenses.map((expense) => ({
                expense,
                isSuspensionPreview: false,
                rowKey: expense.id,
              }));
          const expectedUnpaidExpenses =
            section === "expected"
              ? displayExpenses.filter((expense) => !expense.isPaid)
              : displayExpenses;
          const displayTotal =
            section === "nextMonth"
              ? displayExpenses
                  .filter((exp) => !exp.isSkipped)
                  .reduce((sum, exp) => sum + getEffectiveAmount(exp), 0)
              : section === "expected"
                ? expectedUnpaidExpenses
                    .filter((exp) => !exp.isSkipped)
                    .reduce((sum, exp) => sum + getEffectiveAmount(exp), 0)
                : total;
          const showDateBars = viewMode === "table" && section === "thisMonth";
          const dateBarColumnCount = showDateBars ? 6 : 0;
          const tableExpenses = showDateBars
            ? [...expectedUnpaidExpenses].sort(
                (a, b) => a.dueDate.getTime() - b.dueDate.getTime()
              )
            : expectedUnpaidExpenses;
          const dateBarMeta = showDateBars
            ? buildDateBarMeta(tableExpenses, {
                isEligible: section === "thisMonth" ? () => true : undefined,
              })
            : null;
          const tableRows = showDateBars
            ? tableExpenses.map((expense, index) => ({
                expense,
                rowKey: expense.id,
                isSuspensionPreview: false,
                barCells: dateBarMeta
                  ? renderDateBarCells(dateBarMeta, index)
                  : undefined,
              }))
            : displayRows.map((row) => ({
                ...row,
                barCells: undefined,
              }));

          return (
            <React.Fragment key={section}>
              {section === "overdue" && (
                <div
                  style={{
                    margin: "6px 0 10px",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: isDarkMode ? "#94A3B8" : "#4A5568",
                  }}
                >
                  This Month
                </div>
              )}
              {section === "thisMonth" && (
                <div
                  style={{
                    margin: "16px 0 10px",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: isDarkMode ? "#94A3B8" : "#4A5568",
                  }}
                >
                  Grouped by Month
                </div>
              )}
              <div style={sectionContainerStyle}>
                <div
                  style={{
                    ...sectionHeaderStyle(isCollapsed),
                    borderLeft: `4px solid ${sectionColors[section] || "#805AD5"}`,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => toggleSection(section)}
                >
                  <div style={headingStyle}>
                    <span style={{ marginRight: "8px", fontSize: "14px" }}>
                      {isCollapsed ? "▶" : "▼"}
                    </span>
                    {sectionTitles[section] || section}
                    {isLoading ? (
                      renderLoadingCountBadge(
                        sectionColors[section] || "#805AD5"
                      )
                    ) : (
                      <span
                        style={badgeStyle(sectionColors[section] || "#805AD5")}
                      >
                        {isOverdueSection
                          ? overdueVisibleCount
                          : displayExpenses.length}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: displayTotal > 0 ? "#E53E3E" : undefined,
                    }}
                  >
                    {isLoading
                      ? renderLoadingTotal()
                      : displayTotal > 0
                        ? `-${expenseUtils.formatCurrency(displayTotal)}`
                        : expenseUtils.formatCurrency(displayTotal)}
                  </div>
                </div>

                {!isCollapsed && viewMode === "table" && (
                  <div style={sectionContentStyle}>
                    <table
                      style={{
                        width: "100%",
                        tableLayout: "fixed",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                      }}
                    >
                      <colgroup>
                        {[
                          ...(showDateBars
                            ? [
                                <col
                                  key="bar-overdue"
                                  style={{ width: "18px" }}
                                />,
                                <col
                                  key="bar-today"
                                  style={{ width: "18px" }}
                                />,
                                <col
                                  key="bar-tomorrow"
                                  style={{ width: "18px" }}
                                />,
                                <col
                                  key="bar-next7"
                                  style={{ width: "18px" }}
                                />,
                                <col
                                  key="bar-next10"
                                  style={{ width: "18px" }}
                                />,
                                <col
                                  key="bar-remaining"
                                  style={{ width: "18px" }}
                                />,
                              ]
                            : []),
                          <col key="main-select" style={{ width: "40px" }} />,
                          <col key="main-status" style={{ width: "100px" }} />,
                          <col
                            key="main-invoiced"
                            style={{ width: "120px" }}
                          />,
                          <col key="main-due" style={{ width: "120px" }} />,
                          <col key="main-title" style={{ width: "auto" }} />,
                          <col key="main-vendor" style={{ width: "auto" }} />,
                          <col key="main-type" style={{ width: "110px" }} />,
                          <col
                            key="main-frequency"
                            style={{ width: "90px" }}
                          />,
                          <col key="main-linked" style={{ width: "70px" }} />,
                          <col key="main-amount" style={{ width: "100px" }} />,
                        ]}
                      </colgroup>
                      <thead>
                        <tr
                          style={{
                            borderBottom: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
                            color: isDarkMode ? "#a0aec0" : "#718096",
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {showDateBars && (
                            <>
                              <th style={{ padding: 0 }}></th>
                              <th style={{ padding: 0 }}></th>
                              <th style={{ padding: 0 }}></th>
                              <th style={{ padding: 0 }}></th>
                              <th style={{ padding: 0 }}></th>
                              <th style={{ padding: 0 }}></th>
                            </>
                          )}
                          <th style={{ padding: "8px 4px", width: "40px" }}>
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected}
                              onChange={toggleSelectAll}
                              size="small"
                              sx={{
                                color: isDarkMode ? "#a0aec0" : "#718096",
                                "&.Mui-checked": { color: "#805AD5" },
                                "&.MuiCheckbox-indeterminate": {
                                  color: "#805AD5",
                                },
                              }}
                            />
                          </th>
                          {renderStatusSortHeader()}
                          {renderSortableHeader("dueDate", "Due")}
                          {renderSortableHeader("invoicedDate", "Invoiced")}
                          {renderSortableHeader("name", "Title")}
                          {renderSortableHeader("vendor", "Vendor")}
                          {renderSortableHeader("internalCategory", "Type")}
                          {renderSortableHeader(
                            "recurrencePattern",
                            "Frequency"
                          )}
                          {renderLinkedHeader()}
                          {renderSortableHeader("amount", "Amount", "right")}
                          {/* Actions column removed */}
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading
                          ? Array.from({ length: 7 }).map((_, index) => {
                              const cols = 10 + dateBarColumnCount;
                              const titleCol = 4 + dateBarColumnCount;
                              const vendorCol = 5 + dateBarColumnCount;
                              return (
                                <tr key={`loading-${section}-${index}`}>
                                  {Array.from({ length: cols }).map(
                                    (__, col) => (
                                      <td
                                        key={col}
                                        style={{
                                          padding: "10px 8px",
                                          borderBottom: `1px solid ${
                                            isDarkMode ? "#333" : "#e2e8f0"
                                          }`,
                                        }}
                                      >
                                        {col < dateBarColumnCount ? null : (
                                          <Skeleton
                                            variant="text"
                                            width={
                                              col === cols - 1
                                                ? 56
                                                : col === titleCol
                                                  ? 140
                                                  : col === vendorCol
                                                    ? 120
                                                    : 80
                                            }
                                            sx={{ display: "inline-block" }}
                                          />
                                        )}
                                      </td>
                                    )
                                  )}
                                </tr>
                              );
                            })
                          : section === "expected"
                            ? (() => {
                                const totalColumns = 10 + dateBarColumnCount;
                                const groupTitles: Record<string, string> = {
                                  overdue: "Overdue",
                                  today: "Today",
                                  tomorrow: "Tomorrow",
                                  next7: "Within 7 Days",
                                  next10: "Within 10 Days",
                                  remainder: "Remainder",
                                  paused: "Paused",
                                };
                                const groups: Record<string, number[]> = {
                                  overdue: [],
                                  today: [],
                                  tomorrow: [],
                                  next7: [],
                                  next10: [],
                                  remainder: [],
                                  paused: [],
                                };
                                tableExpenses.forEach((expense, index) => {
                                  const pinnedExpectedKey = (
                                    expense as Expense & {
                                      pinnedExpectedGroupKey?: string;
                                    }
                                  ).pinnedExpectedGroupKey;
                                  const key = pausedExpectedIds.has(expense.id)
                                    ? "paused"
                                    : pinnedExpectedKey ||
                                      getExpectedGroupKey(expense);
                                  if (!groups[key]) return;
                                  groups[key].push(index);
                                });
                                const groupOrder = [
                                  "overdue",
                                  "today",
                                  "tomorrow",
                                  "next7",
                                  "next10",
                                  "remainder",
                                  "paused",
                                ];
                                const getDueTime = (expense: Expense) => {
                                  if (!(expense?.dueDate instanceof Date)) {
                                    return Number.MAX_SAFE_INTEGER;
                                  }
                                  return toUtcDateOnly(
                                    expense.dueDate
                                  ).getTime();
                                };
                                const getSuspensionSortValue = (
                                  expense: Expense
                                ): number => {
                                  if (
                                    !(expense?.dueDate instanceof Date) ||
                                    expense.suspensionPeriodValue ===
                                      undefined ||
                                    !expense.suspensionPeriodUnit
                                  ) {
                                    return Number.MAX_SAFE_INTEGER;
                                  }
                                  const daysUntilSuspension =
                                    expenseUtils.getDaysUntilSuspensionFrom(
                                      getTodayLocal(),
                                      expense.dueDate,
                                      expense.suspensionPeriodValue,
                                      expense.suspensionPeriodUnit
                                    );
                                  return daysUntilSuspension === null
                                    ? Number.MAX_SAFE_INTEGER
                                    : daysUntilSuspension;
                                };
                                return groupOrder.flatMap((key) => {
                                  const indices = groups[key];
                                  if (!indices || indices.length === 0)
                                    return [];
                                  const sortedIndices = [...indices].sort(
                                    (aIndex, bIndex) => {
                                      const aExpense = tableExpenses[aIndex];
                                      const bExpense = tableExpenses[bIndex];
                                      if (key === "overdue") {
                                        const suspensionDiff =
                                          getSuspensionSortValue(aExpense) -
                                          getSuspensionSortValue(bExpense);
                                        if (suspensionDiff !== 0) {
                                          return suspensionDiff;
                                        }
                                      }
                                      return (
                                        getDueTime(aExpense) -
                                        getDueTime(bExpense)
                                      );
                                    }
                                  );
                                  const isGroupCollapsed = isSearchActive
                                    ? false
                                    : collapsedExpectedGroups.has(key);
                                  const total = sortedIndices.reduce(
                                    (sum, idx) => {
                                      const expense = tableExpenses[idx];
                                      if (expense.isSkipped) return sum;
                                      return sum + getEffectiveAmount(expense);
                                    },
                                    0
                                  );
                                  const headerRow = (
                                    <tr key={`expected-${key}-header`}>
                                      <td
                                        colSpan={totalColumns}
                                        style={{ padding: "6px 8px" }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            borderBottom: `1px solid ${
                                              isDarkMode ? "#3a3a3a" : "#e2e8f0"
                                            }`,
                                            paddingBottom: "4px",
                                          }}
                                        >
                                          <div
                                            onClick={() =>
                                              toggleExpectedGroup(key)
                                            }
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "6px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontSize: "12px",
                                                color: isDarkMode
                                                  ? "#CBD5F5"
                                                  : "#4A5568",
                                              }}
                                            >
                                              {isGroupCollapsed ? "▶" : "▼"}
                                            </span>
                                            <span
                                              style={{
                                                fontSize: "13px",
                                                fontWeight: 700,
                                                color: isDarkMode
                                                  ? "#E2E8F0"
                                                  : "#2D3748",
                                              }}
                                            >
                                              {groupTitles[key]}
                                            </span>
                                          </div>
                                          <span
                                            style={{
                                              fontSize: "13px",
                                              fontWeight: 700,
                                              color:
                                                total > 0
                                                  ? "#E53E3E"
                                                  : undefined,
                                            }}
                                          >
                                            {total > 0
                                              ? `-${expenseUtils.formatCurrency(total)}`
                                              : expenseUtils.formatCurrency(
                                                  total
                                                )}
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                  if (isGroupCollapsed) {
                                    return [headerRow];
                                  }
                                  const rows = sortedIndices.map((index) =>
                                    renderTableRow(
                                      tableExpenses[index],
                                      section,
                                      {
                                        barCells:
                                          showDateBars && dateBarMeta
                                            ? renderDateBarCells(
                                                dateBarMeta,
                                                index
                                              )
                                            : undefined,
                                      }
                                    )
                                  );
                                  return [headerRow, ...rows];
                                });
                              })()
                            : section === "overdue"
                              ? (() => {
                                  const totalColumns = 10 + dateBarColumnCount;
                                  const groupTitles: Record<string, string> = {
                                    overdue: "Overdue",
                                    paused: "Paused",
                                  };
                                  const groups: Record<string, number[]> = {
                                    overdue: [],
                                    paused: [],
                                  };
                                  tableRows.forEach((row, index) => {
                                    const key = row.isSuspensionPreview
                                      ? "overdue"
                                      : pausedExpectedIds.has(row.expense.id)
                                        ? "paused"
                                        : "overdue";
                                    groups[key].push(index);
                                  });
                                  const groupOrder = ["overdue", "paused"];
                                  return groupOrder.flatMap((key) => {
                                    const indices = groups[key];
                                    if (!indices || indices.length === 0)
                                      return [];
                                    const total = indices.reduce((sum, idx) => {
                                      const row = tableRows[idx];
                                      if (row.expense.isSkipped) return sum;
                                      if (row.isSuspensionPreview) return sum;
                                      return (
                                        sum + getEffectiveAmount(row.expense)
                                      );
                                    }, 0);
                                    const headerRow = (
                                      <tr key={`overdue-${key}-header`}>
                                        <td
                                          colSpan={totalColumns}
                                          style={{ padding: "6px 8px" }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                              borderBottom: `1px solid ${
                                                isDarkMode
                                                  ? "#3a3a3a"
                                                  : "#e2e8f0"
                                              }`,
                                              paddingBottom: "4px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "13px",
                                                  fontWeight: 700,
                                                  color: isDarkMode
                                                    ? "#E2E8F0"
                                                    : "#2D3748",
                                                }}
                                              >
                                                {groupTitles[key]}
                                              </span>
                                            </div>
                                            <span
                                              style={{
                                                fontSize: "13px",
                                                fontWeight: 700,
                                                color:
                                                  total > 0
                                                    ? "#E53E3E"
                                                    : undefined,
                                              }}
                                            >
                                              {total > 0
                                                ? `-${expenseUtils.formatCurrency(total)}`
                                                : expenseUtils.formatCurrency(
                                                    total
                                                  )}
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                    const rows = indices.map((index) => {
                                      const row = tableRows[index];
                                      return renderTableRow(
                                        row.expense,
                                        section,
                                        {
                                          barCells: row.barCells,
                                          rowKey: row.rowKey,
                                          isSuspensionPreview:
                                            row.isSuspensionPreview,
                                        }
                                      );
                                    });
                                    return [headerRow, ...rows];
                                  });
                                })()
                              : tableRows.map((row) =>
                                  renderTableRow(row.expense, section, {
                                    barCells: row.barCells,
                                    rowKey: row.rowKey,
                                    isSuspensionPreview:
                                      row.isSuspensionPreview,
                                  })
                                )}
                      </tbody>
                    </table>
                  </div>
                )}

                {!isCollapsed && viewMode === "card" && (
                  <div style={sectionContentStyle}>
                    {(() => {
                      const buildCardGroups = (
                        expenses: Expense[],
                        options?: { excludePreview?: boolean }
                      ) => {
                        const groupMap = new Map<string, Expense[]>();
                        expenses.forEach((expense) => {
                          const groupKey = getDisplayName(expense);
                          if (!groupMap.has(groupKey)) {
                            groupMap.set(groupKey, []);
                          }
                          groupMap.get(groupKey)!.push(expense);
                        });
                        return Array.from(groupMap.entries()).map(
                          ([name, exps]) => ({
                            name,
                            expenses: exps,
                            total: exps
                              .filter((exp) => {
                                if (exp.isSkipped) return false;
                                if (options?.excludePreview) {
                                  return !overduePreviewIds.has(exp.id);
                                }
                                return true;
                              })
                              .reduce(
                                (sum, exp) => sum + getEffectiveAmount(exp),
                                0
                              ),
                          })
                        );
                      };

                      const cardExpenseGroups = groupedExpenses[section];

                      const cardGroupSections = isOverdueSection
                        ? (() => {
                            const overdueExpenses = displayExpenses.filter(
                              (expense) => !pausedExpectedIds.has(expense.id)
                            );
                            const pausedExpenses = displayExpenses.filter(
                              (expense) => pausedExpectedIds.has(expense.id)
                            );
                            return [
                              {
                                key: "overdue",
                                title: "Overdue",
                                groups: buildCardGroups(overdueExpenses, {
                                  excludePreview: true,
                                }),
                              },
                              {
                                key: "paused",
                                title: "Paused",
                                groups: buildCardGroups(pausedExpenses, {
                                  excludePreview: true,
                                }),
                              },
                            ].filter(
                              (sectionGroup) => sectionGroup.groups.length
                            );
                          })()
                        : [
                            {
                              key: section,
                              title: null,
                              groups: cardExpenseGroups,
                            },
                          ];

                      return cardGroupSections.flatMap((sectionGroup) => {
                        const sectionNodes: React.ReactNode[] = [];
                        if (sectionGroup.title) {
                          const sectionTotal = sectionGroup.groups.reduce(
                            (sum, group) => sum + group.total,
                            0
                          );
                          sectionNodes.push(
                            <div
                              key={`card-section-${section}-${sectionGroup.key}`}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "6px 8px",
                                borderBottom: `1px solid ${
                                  isDarkMode ? "#2d3748" : "#e2e8f0"
                                }`,
                                margin: "6px 0",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: isDarkMode ? "#E2E8F0" : "#2D3748",
                                }}
                              >
                                {sectionGroup.title}
                              </span>
                              <span
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color:
                                    sectionTotal > 0 ? "#E53E3E" : undefined,
                                }}
                              >
                                {sectionTotal > 0
                                  ? `-${expenseUtils.formatCurrency(sectionTotal)}`
                                  : expenseUtils.formatCurrency(sectionTotal)}
                              </span>
                            </div>
                          );
                        }

                        sectionGroup.groups.forEach((group) => {
                          const groupKey = `${section}-${sectionGroup.key}-${group.name}`;
                          const isGroupCollapsed = isSearchActive
                            ? false
                            : collapsedGroups.has(groupKey);
                          const hasMultiple = group.expenses.length > 1;

                          // Render expense item
                          const renderExpenseItem = (
                            expense: Expense,
                            isNested: boolean = false
                          ) => {
                            const isStruck = isExpenseStruck(expense);
                            const isSuspensionPreview =
                              isOverdueSection &&
                              overduePreviewIds.has(expense.id);
                            const baseOpacity = getSkipOpacity(expense, 1);
                            const resolvedOpacity = isSuspensionPreview
                              ? Math.min(0.6, baseOpacity)
                              : baseOpacity;
                            const showCardActions =
                              !isSuspensionPreview && !readOnly;
                            const isDeleting = deleteLoadingIds.has(expense.id);
                            const isDeleteAnimatingOut =
                              deleteAnimatingOutIds.has(expense.id);
                            const expectedGroupKey =
                              section === "expected"
                                ? (
                                    expense as Expense & {
                                      pinnedExpectedGroupKey?: string;
                                    }
                                  ).pinnedExpectedGroupKey ||
                                  getExpectedGroupKey(expense)
                                : null;
                            const dueBadgeStyle = getDueDateStyle(
                              expense,
                              section,
                              expectedGroupKey,
                              isDarkMode
                            );
                            const invoiceBadgeStyle =
                              getNeutralDateStyle(isDarkMode);
                            const hasShoppingList =
                              !!expense.linkedShoppingListId;
                            const isSuspendedExpense =
                              isExpenseSuspended(expense);
                            return (
                              <div
                                key={expense.id}
                                style={{
                                  ...expenseItemStyle,
                                  marginLeft: isNested ? "24px" : 0,
                                  borderLeft: isNested
                                    ? `3px solid ${sectionColors[section] || "#805AD5"}40`
                                    : undefined,
                                  opacity: isDeleteAnimatingOut
                                    ? 0
                                    : resolvedOpacity,
                                  transform: isDeleteAnimatingOut
                                    ? "translateY(-10px)"
                                    : getSkipTransform(expense),
                                  transition:
                                    "background-color 0.15s, opacity 0.2s, transform 0.2s",
                                  textDecoration:
                                    getSkipTextDecoration(expense),
                                  backgroundColor: isDeleteAnimatingOut
                                    ? "#ffffff"
                                    : isStruck
                                      ? isDarkMode
                                        ? "#1a1a1a"
                                        : "#f0f0f0"
                                      : expenseItemStyle.backgroundColor,
                                  border: isSuspendedExpense
                                    ? "2px solid #FF3B30"
                                    : undefined,
                                  paddingBottom: hasShoppingList
                                    ? "28px"
                                    : undefined,
                                  position: hasShoppingList
                                    ? ("relative" as const)
                                    : undefined,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "6px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div>
                                      <div style={{ fontWeight: "bold" }}>
                                        {renderExpenseTitle(expense, {
                                          isSuspensionPreview,
                                        })}
                                      </div>
                                      <div
                                        style={{
                                          color: "#718096",
                                          fontSize: "13px",
                                        }}
                                      >
                                        {expense.vendor || "No vendor"}
                                      </div>
                                    </div>
                                  </div>
                                  {showCardActions && (
                                    <div
                                      style={{ display: "flex", gap: "4px" }}
                                    >
                                      {renderOccurrencesAction(expense, {
                                        disabled: isDeleting,
                                      })}
                                      <Tooltip title="Edit" arrow>
                                        <IconButton
                                          onClick={() => onEditExpense(expense)}
                                          size="small"
                                          disabled={isDeleting}
                                          sx={{
                                            backgroundColor: "#4299E1",
                                            color: "white",
                                            "&:hover": {
                                              backgroundColor: "#3182CE",
                                            },
                                            "&.Mui-disabled": {
                                              backgroundColor: "#4299E1",
                                              color: "white",
                                              opacity: 0.6,
                                            },
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      {(onPostponeExpense ||
                                        onPostponeDebt) && (
                                        <Tooltip
                                          title={
                                            expense.isPostponed
                                              ? "Postponed"
                                              : "Postpone"
                                          }
                                          arrow
                                        >
                                          <IconButton
                                            onClick={(e) =>
                                              openPostpone(e, expense)
                                            }
                                            size="small"
                                            disabled={isDeleting}
                                            sx={{
                                              backgroundColor:
                                                expense.isPostponed
                                                  ? "#DD6B20"
                                                  : "#718096",
                                              color: "white",
                                              "&:hover": {
                                                backgroundColor:
                                                  expense.isPostponed
                                                    ? "#C05621"
                                                    : "#4A5568",
                                              },
                                            }}
                                          >
                                            <SnoozeIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      {onDuplicateExpense && (
                                        <Tooltip title="Duplicate" arrow>
                                          <IconButton
                                            onClick={() =>
                                              onDuplicateExpense(expense)
                                            }
                                            size="small"
                                            disabled={isDeleting}
                                            sx={{
                                              backgroundColor: "#805AD5",
                                              color: "white",
                                              "&:hover": {
                                                backgroundColor: "#6B46C1",
                                              },
                                              "&.Mui-disabled": {
                                                backgroundColor: "#805AD5",
                                                color: "white",
                                                opacity: 0.6,
                                              },
                                            }}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      {onShareExpense && (
                                        <Tooltip title="Share" arrow>
                                          <IconButton
                                            onClick={() =>
                                              onShareExpense(expense)
                                            }
                                            size="small"
                                            disabled={isDeleting}
                                            sx={{
                                              backgroundColor: "#4299E1",
                                              color: "white",
                                              "&:hover": {
                                                backgroundColor: "#3182CE",
                                              },
                                              "&.Mui-disabled": {
                                                backgroundColor: "#4299E1",
                                                color: "white",
                                                opacity: 0.6,
                                              },
                                            }}
                                          >
                                            <ShareIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      {onRescheduleExpense && (
                                        <Tooltip title="Reschedule" arrow>
                                          <IconButton
                                            onClick={() =>
                                              openRescheduleModal(expense)
                                            }
                                            size="small"
                                            disabled={isDeleting}
                                            sx={{
                                              backgroundColor: "#38B2AC",
                                              color: "white",
                                              "&:hover": {
                                                backgroundColor: "#319795",
                                              },
                                              "&.Mui-disabled": {
                                                backgroundColor: "#38B2AC",
                                                color: "white",
                                                opacity: 0.6,
                                              },
                                            }}
                                          >
                                            <EventIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip
                                        title={
                                          deleteLoadingIds.has(expense.id)
                                            ? "Deleting..."
                                            : "Delete"
                                        }
                                        arrow
                                      >
                                        <span>
                                          <IconButton
                                            onClick={() =>
                                              void handleDeleteClick(expense)
                                            }
                                            size="small"
                                            disabled={deleteLoadingIds.has(
                                              expense.id
                                            )}
                                            sx={{
                                              backgroundColor: "#E53E3E",
                                              color: "white",
                                              "&:hover": {
                                                backgroundColor: "#C53030",
                                              },
                                              "&.Mui-disabled": {
                                                backgroundColor: "#E53E3E",
                                                color: "white",
                                                opacity: 0.6,
                                              },
                                            }}
                                          >
                                            {deleteLoadingIds.has(
                                              expense.id
                                            ) ? (
                                              <CircularProgress
                                                size={14}
                                                sx={{ color: "white" }}
                                              />
                                            ) : (
                                              <DeleteIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      {!expense.isPaid && (
                                        <Tooltip title="Mark as Paid" arrow>
                                          <IconButton
                                            onClick={() =>
                                              openPaidModal(expense)
                                            }
                                            size="small"
                                            sx={{
                                              backgroundColor: "#38A169",
                                              color: "white",
                                              "&:hover": {
                                                backgroundColor: "#2F855A",
                                              },
                                            }}
                                          >
                                            <CheckCircleIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      {onSkipExpense && !expense.isPaid && (
                                        <Tooltip
                                          title={
                                            expense.isSkipped
                                              ? "Unskip"
                                              : "Skip"
                                          }
                                          arrow
                                        >
                                          <IconButton
                                            onClick={() =>
                                              handleSkipClick(expense)
                                            }
                                            size="small"
                                            sx={{
                                              backgroundColor: expense.isSkipped
                                                ? "#805AD5"
                                                : "#718096",
                                              color: "white",
                                              "&:hover": {
                                                backgroundColor:
                                                  expense.isSkipped
                                                    ? "#6B46C1"
                                                    : "#4A5568",
                                              },
                                            }}
                                          >
                                            {expense.isSkipped ? (
                                              <UndoIcon fontSize="small" />
                                            ) : (
                                              <BlockIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-end",
                                  }}
                                >
                                  <div>
                                    <div
                                      style={{
                                        marginTop: "2px",
                                        display: "flex",
                                        gap: "4px",
                                      }}
                                    >
                                      <span style={categoryTagStyle}>
                                        {expense.internalCategory}
                                      </span>
                                      {expense.linkedDebtId && (
                                        <span
                                          style={{
                                            backgroundColor: "#E53E3E20",
                                            color: "#E53E3E",
                                            padding: "2px 6px",
                                            borderRadius: "4px",
                                            fontSize: "12px",
                                          }}
                                        >
                                          Debt
                                        </span>
                                      )}
                                      {expense.isPaid && (
                                        <span
                                          style={{
                                            backgroundColor: "#38A16920",
                                            color: "#38A169",
                                            padding: "2px 6px",
                                            borderRadius: "4px",
                                            fontSize: "12px",
                                          }}
                                        >
                                          ✓ Paid
                                        </span>
                                      )}
                                      {expense.isSkipped && (
                                        <span
                                          style={{
                                            backgroundColor: "#80808020",
                                            color: "#808080",
                                            padding: "2px 6px",
                                            borderRadius: "4px",
                                            fontSize: "12px",
                                          }}
                                        >
                                          Skipped
                                        </span>
                                      )}
                                      {expense.isRecurring && (
                                        <span
                                          style={{
                                            ...recurringTagStyle,
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          {expense.recurrencePattern}
                                        </span>
                                      )}
                                      {(
                                        expense as Expense & {
                                          is_shared?: boolean;
                                        }
                                      ).is_shared || expense.isShared ? (
                                        <span style={sharedTagStyle}>
                                          Shared
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: "right" }}>
                                    <div style={{ fontWeight: "bold" }}>
                                      {expenseUtils.formatCurrency(
                                        getEffectiveAmount(expense)
                                      )}
                                      {expense.linkedDebtId && (
                                        <span
                                          style={{
                                            fontSize: "12px",
                                            color: "#718096",
                                            display: "block",
                                          }}
                                        >
                                          (from linked debt)
                                        </span>
                                      )}
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        justifyContent: "flex-end",
                                      }}
                                    >
                                      {section === "overdue" &&
                                        !isSuspensionPreview && (
                                          <Tooltip title="Days Past Due" arrow>
                                            <span
                                              style={{
                                                backgroundColor: "#E53E3E",
                                                color: "white",
                                                width: "26px",
                                                height: "26px",
                                                borderRadius: "50%",
                                                fontSize: "11px",
                                                fontWeight: 700,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              {getDaysOverdue(expense.dueDate)}
                                            </span>
                                          </Tooltip>
                                        )}
                                      {section === "overdue" &&
                                        expense.suspensionPeriodValue !==
                                          undefined &&
                                        expense.suspensionPeriodUnit && (
                                          <Tooltip
                                            title={(() => {
                                              const days =
                                                expenseUtils.getDaysUntilSuspension(
                                                  expense.dueDate,
                                                  expense.suspensionPeriodValue,
                                                  expense.suspensionPeriodUnit
                                                );
                                              if (days === null) return "";
                                              if (days > 0)
                                                return `Will be suspended in ${days} day${days === 1 ? "" : "s"}`;
                                              if (days === 0)
                                                return "Suspension date is today";
                                              return `Suspended ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
                                            })()}
                                            arrow
                                          >
                                            <span
                                              style={{
                                                backgroundColor:
                                                  getSuspensionDateStyle(
                                                    expense,
                                                    isDarkMode
                                                  ).backgroundColor,
                                                color: getSuspensionDateStyle(
                                                  expense,
                                                  isDarkMode
                                                ).color,
                                                width: "26px",
                                                height: "26px",
                                                borderRadius: "50%",
                                                fontSize: "11px",
                                                fontWeight: 700,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              {getSuspensionBadgeLabel(
                                                getExpenseSuspensionDays(
                                                  expense
                                                )
                                              )}
                                            </span>
                                          </Tooltip>
                                        )}
                                      {expense.invoicedDate && (
                                        <span
                                          style={{
                                            fontSize: "12px",
                                            padding: "2px 8px",
                                            borderRadius: "4px",
                                            ...invoiceBadgeStyle,
                                          }}
                                        >
                                          {`Invoiced: ${formatCompactDate(
                                            new Date(expense.invoicedDate)
                                          )}`}
                                        </span>
                                      )}
                                      <span
                                        style={{
                                          fontSize: "14px",
                                          padding: "2px 8px",
                                          borderRadius: "4px",
                                          ...dueBadgeStyle,
                                        }}
                                      >
                                        {`Due: ${formatCompactDate(
                                          expense.dueDate
                                        )}`}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          };

                          // Wrap an expense in shopping list accordion if linked
                          const renderWithShoppingAccordion = (
                            expense: Expense,
                            renderedItem: React.ReactNode
                          ) => {
                            if (!expense.linkedShoppingListId)
                              return renderedItem;
                            const isExpanded = expandedShoppingExpenses.has(
                              expense.id
                            );
                            const listId = expense.linkedShoppingListId;
                            const items = shoppingListItemsCache[listId];
                            const isLoading = shoppingListLoading.has(listId);
                            return (
                              <div
                                key={`shop-${expense.id}`}
                                style={{ marginBottom: "4px" }}
                              >
                                <div
                                  onClick={() =>
                                    toggleShoppingAccordion(expense)
                                  }
                                  style={{
                                    cursor: "pointer",
                                    position: "relative",
                                  }}
                                >
                                  {renderedItem}
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "6px",
                                      left: "8px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <ShoppingCartIcon
                                      sx={{
                                        fontSize: 14,
                                        color: isDarkMode
                                          ? "#63b3ed"
                                          : "#3182CE",
                                      }}
                                    />
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: isDarkMode
                                          ? "#63b3ed"
                                          : "#3182CE",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {isExpanded ? "Hide items" : "Show items"}
                                    </span>
                                    {isExpanded ? (
                                      <KeyboardArrowDownIcon
                                        sx={{
                                          fontSize: 16,
                                          color: isDarkMode
                                            ? "#63b3ed"
                                            : "#3182CE",
                                        }}
                                      />
                                    ) : (
                                      <KeyboardArrowRightIcon
                                        sx={{
                                          fontSize: 16,
                                          color: isDarkMode
                                            ? "#63b3ed"
                                            : "#3182CE",
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                                {isExpanded && (
                                  <div
                                    style={{
                                      backgroundColor: isDarkMode
                                        ? "#1a202c"
                                        : "#f7fafc",
                                      borderRadius: "0 0 6px 6px",
                                      border: `1px solid ${isDarkMode ? "#2d3748" : "#e2e8f0"}`,
                                      borderTop: "none",
                                      padding: "8px 12px",
                                    }}
                                  >
                                    {isLoading ? (
                                      <div
                                        style={{
                                          textAlign: "center",
                                          padding: "12px",
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Loading items...
                                        </Typography>
                                      </div>
                                    ) : items && items.length > 0 ? (
                                      <table
                                        style={{
                                          width: "100%",
                                          borderCollapse: "collapse",
                                          fontSize: "13px",
                                        }}
                                      >
                                        <thead>
                                          <tr
                                            style={{
                                              borderBottom: `1px solid ${isDarkMode ? "#2d3748" : "#e2e8f0"}`,
                                            }}
                                          >
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "4px 8px",
                                                fontWeight: 600,
                                                color: isDarkMode
                                                  ? "#a0aec0"
                                                  : "#718096",
                                              }}
                                            >
                                              Item
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "4px 8px",
                                                fontWeight: 600,
                                                color: isDarkMode
                                                  ? "#a0aec0"
                                                  : "#718096",
                                              }}
                                            >
                                              Vendor
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "right",
                                                padding: "4px 8px",
                                                fontWeight: 600,
                                                color: isDarkMode
                                                  ? "#a0aec0"
                                                  : "#718096",
                                              }}
                                            >
                                              Price
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "center",
                                                padding: "4px 8px",
                                                fontWeight: 600,
                                                color: isDarkMode
                                                  ? "#a0aec0"
                                                  : "#718096",
                                              }}
                                            >
                                              Link
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {items.map((item) => (
                                            <tr
                                              key={item.id}
                                              style={{
                                                borderBottom: `1px solid ${isDarkMode ? "#2d374820" : "#e2e8f020"}`,
                                              }}
                                            >
                                              <td
                                                style={{
                                                  padding: "4px 8px",
                                                  color: isDarkMode
                                                    ? "#e2e8f0"
                                                    : "#2d3748",
                                                }}
                                              >
                                                {item.title}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "4px 8px",
                                                  color: isDarkMode
                                                    ? "#a0aec0"
                                                    : "#718096",
                                                }}
                                              >
                                                {item.vendor || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "4px 8px",
                                                  textAlign: "right",
                                                  color: isDarkMode
                                                    ? "#e2e8f0"
                                                    : "#2d3748",
                                                }}
                                              >
                                                {expenseUtils.formatCurrency(
                                                  item.price
                                                )}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "4px 8px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {item.link ? (
                                                  <a
                                                    href={item.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                    style={{
                                                      color: isDarkMode
                                                        ? "#63b3ed"
                                                        : "#3182CE",
                                                    }}
                                                  >
                                                    <OpenInNewIcon
                                                      sx={{ fontSize: 14 }}
                                                    />
                                                  </a>
                                                ) : (
                                                  "-"
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        No items in this shopping list
                                      </Typography>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          };

                          // Single item - render directly
                          if (!hasMultiple) {
                            const exp = group.expenses[0];
                            sectionNodes.push(
                              renderWithShoppingAccordion(
                                exp,
                                renderExpenseItem(exp, false)
                              )
                            );
                            return;
                          }

                          // Multiple items - render as accordion
                          sectionNodes.push(
                            <div key={groupKey} style={{ marginBottom: "4px" }}>
                              {/* Group Header */}
                              <div
                                onClick={() => toggleGroup(groupKey)}
                                style={{
                                  ...expenseItemStyle,
                                  cursor: "pointer",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  backgroundColor: isDarkMode
                                    ? "#252525"
                                    : "#f0f4f8",
                                  borderLeft: `3px solid ${sectionColors[section] || "#805AD5"}`,
                                  marginBottom: isGroupCollapsed ? "4px" : 0,
                                  borderRadius: isGroupCollapsed
                                    ? "6px"
                                    : "6px 6px 0 0",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  {isGroupCollapsed ? (
                                    <KeyboardArrowRightIcon
                                      sx={{
                                        fontSize: 20,
                                        color: isDarkMode
                                          ? "#a0aec0"
                                          : "#4a5568",
                                      }}
                                    />
                                  ) : (
                                    <KeyboardArrowDownIcon
                                      sx={{
                                        fontSize: 20,
                                        color: isDarkMode
                                          ? "#a0aec0"
                                          : "#4a5568",
                                      }}
                                    />
                                  )}
                                  <div>
                                    <div style={{ fontWeight: "bold" }}>
                                      {group.name}
                                    </div>
                                    <div
                                      style={{
                                        color: "#718096",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {group.expenses.length} items
                                    </div>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    color:
                                      group.total > 0 ? "#E53E3E" : undefined,
                                  }}
                                >
                                  {group.total > 0
                                    ? `-${expenseUtils.formatCurrency(group.total)}`
                                    : expenseUtils.formatCurrency(group.total)}
                                </div>
                              </div>

                              {/* Group Items */}
                              {!isGroupCollapsed && (
                                <div
                                  style={{
                                    backgroundColor: isDarkMode
                                      ? "#1f1f1f"
                                      : "#fafbfc",
                                    borderRadius: "0 0 6px 6px",
                                    padding: "8px 0",
                                  }}
                                >
                                  {group.expenses.map((expense) =>
                                    renderExpenseItem(expense, true)
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        });

                        return sectionNodes;
                      });
                    })()}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}

      {/* Future Months Sections - Only show in time grouping mode */}
      {groupingMode === "time" &&
        futureMonthsData.map((monthData) => {
          const monthKey = `future-${monthData.monthOffset}`;
          if (isSearchActive && monthData.expenses.length === 0) {
            return null;
          }
          const isCollapsed = isSearchActive
            ? false
            : collapsedSections.has(monthKey as ExpenseSection);
          const monthTotal = monthData.expenses
            .filter((exp) => !exp.isSkipped)
            .reduce((sum, exp) => sum + getEffectiveAmount(exp), 0);

          return (
            <div key={monthKey} style={sectionContainerStyle}>
              <div
                style={{
                  ...sectionHeaderStyle(isCollapsed),
                  borderLeft: "4px solid #805AD5",
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => toggleSection(monthKey as ExpenseSection)}
              >
                <div style={headingStyle}>
                  <span style={{ marginRight: "8px", fontSize: "14px" }}>
                    {isCollapsed ? "▶" : "▼"}
                  </span>
                  {monthData.name}
                  <span style={badgeStyle("#805AD5")}>
                    {monthData.expenses.length}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Tooltip title="Remove this month and later" arrow>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleDeleteFutureMonthsFrom(monthData.monthOffset);
                      }}
                      sx={{
                        width: 30,
                        height: 30,
                        backgroundColor: isDarkMode ? "#2d2d2d" : "#edf2f7",
                        border: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
                        color: isDarkMode ? "#a0aec0" : "#4a5568",
                        "&:hover": {
                          backgroundColor: isDarkMode ? "#333" : "#e2e8f0",
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: monthTotal > 0 ? "#E53E3E" : undefined,
                    }}
                  >
                    {monthTotal > 0
                      ? `-${expenseUtils.formatCurrency(monthTotal)}`
                      : expenseUtils.formatCurrency(monthTotal)}
                  </div>
                </div>
              </div>

              {!isCollapsed && viewMode === "table" && (
                <div style={sectionContentStyle}>
                  {monthData.expenses.length === 0 ? (
                    <div
                      style={{
                        padding: "24px",
                        textAlign: "center",
                        color: isDarkMode ? "#718096" : "#a0aec0",
                      }}
                    >
                      No expenses found for this month
                    </div>
                  ) : (
                    <table
                      style={{
                        width: "100%",
                        tableLayout: "fixed",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                      }}
                    >
                      <colgroup>
                        {[
                          <col key="main-select" style={{ width: "40px" }} />,
                          <col key="main-status" style={{ width: "100px" }} />,
                          <col key="main-due" style={{ width: "120px" }} />,
                          <col
                            key="main-invoiced"
                            style={{ width: "120px" }}
                          />,
                          <col key="main-title" style={{ width: "auto" }} />,
                          <col key="main-vendor" style={{ width: "auto" }} />,
                          <col key="main-type" style={{ width: "110px" }} />,
                          <col
                            key="main-frequency"
                            style={{ width: "90px" }}
                          />,
                          <col key="main-linked" style={{ width: "70px" }} />,
                          <col key="main-amount" style={{ width: "100px" }} />,
                        ]}
                      </colgroup>
                      <thead>
                        <tr
                          style={{
                            borderBottom: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
                            color: isDarkMode ? "#a0aec0" : "#718096",
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          <th style={{ padding: "8px 8px" }}></th>
                          {renderStatusSortHeader()}
                          {renderSortableHeader("dueDate", "Due")}
                          {renderSortableHeader("invoicedDate", "Invoiced")}
                          {renderSortableHeader("name", "Title")}
                          {renderSortableHeader("vendor", "Vendor")}
                          {renderSortableHeader("internalCategory", "Type")}
                          {renderSortableHeader(
                            "recurrencePattern",
                            "Frequency"
                          )}
                          {renderLinkedHeader()}
                          {renderSortableHeader("amount", "Amount", "right")}
                        </tr>
                      </thead>
                      <tbody>
                        {monthData.expenses.map((expense) =>
                          renderTableRow(expense, monthKey as ExpenseSection)
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {!isCollapsed && viewMode === "card" && (
                <div style={sectionContentStyle}>
                  {monthData.expenses.length === 0 ? (
                    <div
                      style={{
                        padding: "24px",
                        textAlign: "center",
                        color: isDarkMode ? "#718096" : "#a0aec0",
                      }}
                    >
                      No recurring expenses projected for this month
                    </div>
                  ) : (
                    monthData.expenses.map((expense) => {
                      const isStruck = isExpenseStruck(expense);
                      return (
                        <div
                          key={expense.id}
                          style={{
                            ...expenseItemStyle,
                            opacity: getSkipOpacity(expense, 0.8),
                            transform: getSkipTransform(expense),
                            transition: "opacity 0.2s, transform 0.2s",
                            textDecoration: getSkipTextDecoration(expense),
                            backgroundColor: isStruck
                              ? isDarkMode
                                ? "#1a1a1a"
                                : "#f0f0f0"
                              : expenseItemStyle.backgroundColor,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "10px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: "bold" }}>
                                  {renderExpenseTitle(expense)}
                                </div>
                                <div
                                  style={{
                                    color: "#718096",
                                    fontSize: "13px",
                                  }}
                                >
                                  {expense.vendor || "No vendor"}
                                </div>
                              </div>
                            </div>
                            {!readOnly && onRescheduleExpense && (
                              <Tooltip title="Reschedule" arrow>
                                <IconButton
                                  onClick={() => openRescheduleModal(expense)}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#38B2AC",
                                    color: "white",
                                    width: 28,
                                    height: 28,
                                    "&:hover": {
                                      backgroundColor: "#319795",
                                    },
                                  }}
                                >
                                  <EventIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-end",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  marginTop: "4px",
                                  display: "flex",
                                  gap: "5px",
                                }}
                              >
                                <span style={categoryTagStyle}>
                                  {expense.internalCategory}
                                </span>
                                <span
                                  style={{
                                    ...recurringTagStyle,
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {expense.recurrencePattern}
                                </span>
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontWeight: "bold" }}>
                                {expenseUtils.formatCurrency(
                                  getEffectiveAmount(expense)
                                )}
                              </div>
                              {expense.invoicedDate && (
                                <div
                                  style={{
                                    color: "#718096",
                                    fontSize: "12px",
                                  }}
                                >
                                  {`Invoiced: ${formatCompactDate(
                                    new Date(expense.invoicedDate)
                                  )}`}
                                </div>
                              )}
                              <div
                                style={{ color: "#718096", fontSize: "14px" }}
                              >
                                {`Due: ${formatCompactDate(expense.dueDate)}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}

      {/* Load More Months Controls - Only show in time grouping mode */}
      {groupingMode === "time" && (
        <div
          style={{
            marginTop: "24px",
            marginBottom: "32px",
            padding: "16px",
            backgroundColor: isDarkMode ? "#1a1a1a" : "#f7fafc",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddFutureMonth}
              sx={{
                borderColor: isDarkMode ? "#444" : "#e2e8f0",
                color: isDarkMode ? "#a0aec0" : "#4a5568",
                "&:hover": {
                  borderColor: isDarkMode ? "#666" : "#cbd5e0",
                  backgroundColor: isDarkMode ? "#2d2d2d" : "#edf2f7",
                },
              }}
            >
              Load More Months
            </Button>
            {futureMonthsCount > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RemoveIcon />}
                onClick={handleRemoveFutureMonth}
                sx={{
                  borderColor: isDarkMode ? "#444" : "#e2e8f0",
                  color: isDarkMode ? "#a0aec0" : "#4a5568",
                  "&:hover": {
                    borderColor: isDarkMode ? "#666" : "#cbd5e0",
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#edf2f7",
                  },
                }}
              >
                Remove Last Month
              </Button>
            )}
            {futureMonthsCount > 0 && (
              <span
                style={{
                  color: isDarkMode ? "#718096" : "#a0aec0",
                  fontSize: "13px",
                }}
              >
                Showing {futureMonthsCount} additional month
                {futureMonthsCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      )}

      <Dialog
        open={Boolean(rescheduleExpense)}
        onClose={closeRescheduleModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
          },
        }}
      >
        <DialogTitle>Reschedule Expense</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ mb: 2, fontSize: "14px" }}>
            What day should this be rescheduled for?
          </Typography>
          <TextField
            type="date"
            value={rescheduleDate}
            onChange={(e) => {
              setRescheduleDate(e.target.value);
              setRescheduleError("");
              console.log("[ExpenseList] Reschedule date changed", {
                date: e.target.value,
              });
            }}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          {(rescheduleExpense?.linkedShoppingListId ||
            rescheduleExpense?.isRecurring ||
            rescheduleExpense?.isRecurringInstance ||
            rescheduleExpense?.originalId) && (
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel id="reschedule-shift-scope-label">
                Shift scope
              </InputLabel>
              <Select
                labelId="reschedule-shift-scope-label"
                value={rescheduleScope}
                label="Shift scope"
                onChange={(e) =>
                  setRescheduleScope(e.target.value as "single" | "all_future")
                }
              >
                <MenuItem value="single">Only this item</MenuItem>
                <MenuItem value="all_future">This and all future</MenuItem>
              </Select>
            </FormControl>
          )}
          {rescheduleError && (
            <Typography sx={{ color: "#E53E3E", mt: 1, fontSize: "12px" }}>
              {rescheduleError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{ borderTop: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}` }}
        >
          <Button onClick={closeRescheduleModal} disabled={isRescheduling}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRescheduleSubmit}
            disabled={isRescheduling}
            sx={{
              backgroundColor: "#38B2AC",
              "&:hover": { backgroundColor: "#319795" },
            }}
          >
            {isRescheduling ? "Rescheduling..." : "Reschedule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Paid Modal */}
      {showPaidModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              color: theme.palette.text.primary,
              padding: "24px",
              borderRadius: "8px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
          >
            <h3
              style={{
                marginBottom: "16px",
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              Mark as Paid:{" "}
              {selectedExpense ? getDisplayName(selectedExpense) : ""}
            </h3>

            <Box sx={{ mb: 2 }}>
              <TextField
                label="Payment Date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                label="Payment Time"
                type="time"
                value={paymentTime}
                onChange={(e) => setPaymentTime(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ position: "relative" }}>
                <TextField
                  label="Link Transaction (Optional) 🤖 AI Suggestions"
                  placeholder={
                    loadingTransactions
                      ? "Loading transactions..."
                      : "Search transactions by name or merchant..."
                  }
                  value={transactionSearch}
                  onChange={(e) => {
                    setTransactionSearch(e.target.value);
                    setShowTransactionDropdown(true);
                  }}
                  onFocus={() => setShowTransactionDropdown(true)}
                  disabled={loadingTransactions}
                  size="small"
                  fullWidth
                />
                {showTransactionDropdown && !loadingTransactions && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: txnPicker.optionBg,
                      color: txnPicker.text,
                      border: `1px solid ${txnPicker.divider}`,
                      borderTop: "none",
                      borderRadius: "0 0 4px 4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 1001,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {getFilteredTransactions().length === 0 ? (
                      <div
                        style={{
                          padding: "8px",
                          color: txnPicker.secondary,
                          fontSize: "14px",
                        }}
                      >
                        No matching transactions found
                      </div>
                    ) : (
                      getFilteredTransactions().map((tx) => {
                        const txDate = new Date(tx.date);
                        const isAISuggested =
                          tx._confidence && tx._confidence > 0.5;
                        return (
                          <div
                            key={tx.id}
                            onClick={() => handleTransactionSelect(tx)}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              borderBottom: `1px solid ${txnPicker.divider}`,
                              fontSize: "14px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              color: txnPicker.text,
                              backgroundColor: isAISuggested
                                ? txnPicker.optionAiBg
                                : txnPicker.optionBg,
                              borderLeft: isAISuggested
                                ? `3px solid ${txnPicker.accent}`
                                : "none",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                isAISuggested
                                  ? txnPicker.optionAiHoverBg
                                  : txnPicker.optionHoverBg)
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                isAISuggested
                                  ? txnPicker.optionAiBg
                                  : txnPicker.optionBg)
                            }
                          >
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: "500",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {isAISuggested && (
                                  <span
                                    style={{
                                      marginRight: "6px",
                                      color: txnPicker.accent,
                                      fontSize: "12px",
                                    }}
                                  >
                                    🤖
                                  </span>
                                )}
                                {tx.name}
                              </div>
                              {tx.merchant_name &&
                                tx.merchant_name !== tx.name && (
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: txnPicker.secondary,
                                    }}
                                  >
                                    {tx.merchant_name}
                                  </div>
                                )}
                              {tx._reason && (
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: txnPicker.accent,
                                    marginTop: "2px",
                                  }}
                                >
                                  {tx._reason}
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div
                                style={{
                                  fontWeight: "500",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                ${tx.amount.toFixed(2)}
                                {isAISuggested && (
                                  <span
                                    style={{
                                      marginLeft: "4px",
                                      fontSize: "10px",
                                      color: txnPicker.accent,
                                    }}
                                  >
                                    {Math.round(tx._confidence * 100)}%
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: txnPicker.secondary,
                                }}
                              >
                                {txDate.toLocaleDateString("en-US", {
                                  timeZone: "UTC",
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
                {selectedTransaction && (
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "6px 8px",
                      backgroundColor: txnPicker.accentBg,
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: txnPicker.accent,
                    }}
                  >
                    ✓ Transaction selected
                  </div>
                )}
              </Box>
            </Box>

            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>Payment Account</InputLabel>
              <Select
                value={paymentAccount}
                onChange={(e) => setPaymentAccount(e.target.value as string)}
                label="Payment Account"
              >
                <MenuItem value="">
                  <em>Select account...</em>
                </MenuItem>
                <MenuItem value="TPC Chase">TPC Chase</MenuItem>
                <MenuItem value="Personal Chase">Personal Chase</MenuItem>
                <MenuItem value="Capital One">Capital One</MenuItem>
                <MenuItem value="Wells Fargo">Wells Fargo</MenuItem>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => setShowPaidModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleMarkAsPaid}
              >
                Mark as Paid
              </Button>
            </Box>
          </div>
        </div>
      )}

      {/* Bulk Duplicate Modal */}
      <Dialog
        open={showBulkDuplicateModal}
        onClose={() => setShowBulkDuplicateModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
            color: isDarkMode ? "#fff" : "#1a202c",
          },
        }}
      >
        <DialogTitle
          sx={{ borderBottom: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}` }}
        >
          Duplicate {selectedExpenses.length} Expense
          {selectedExpenses.length > 1 ? "s" : ""}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="bulk-month-label">Month</InputLabel>
                <Select
                  labelId="bulk-month-label"
                  value={bulkDuplicateMonth}
                  label="Month"
                  onChange={(e) =>
                    setBulkDuplicateMonth(Number(e.target.value))
                  }
                  sx={{
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#fff",
                  }}
                >
                  <MenuItem value={1}>January</MenuItem>
                  <MenuItem value={2}>February</MenuItem>
                  <MenuItem value={3}>March</MenuItem>
                  <MenuItem value={4}>April</MenuItem>
                  <MenuItem value={5}>May</MenuItem>
                  <MenuItem value={6}>June</MenuItem>
                  <MenuItem value={7}>July</MenuItem>
                  <MenuItem value={8}>August</MenuItem>
                  <MenuItem value={9}>September</MenuItem>
                  <MenuItem value={10}>October</MenuItem>
                  <MenuItem value={11}>November</MenuItem>
                  <MenuItem value={12}>December</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="bulk-year-label">Year</InputLabel>
                <Select
                  labelId="bulk-year-label"
                  value={bulkDuplicateYear}
                  label="Year"
                  onChange={(e) => setBulkDuplicateYear(Number(e.target.value))}
                  sx={{
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#fff",
                  }}
                >
                  {[...Array(5)].map((_, i) => {
                    const year = new Date().getFullYear() + i;
                    return (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkDuplicateRecurring}
                  onChange={(e) => setBulkDuplicateRecurring(e.target.checked)}
                  sx={{
                    color: isDarkMode ? "#a0aec0" : "#718096",
                    "&.Mui-checked": { color: "#805AD5" },
                  }}
                />
              }
              label="Make recurring"
            />
            <Box
              sx={{
                backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
                borderRadius: "8px",
                padding: "12px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "8px",
                }}
              >
                Selected expenses:
              </div>
              <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                {selectedExpenses.map((expense) => (
                  <div
                    key={expense.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "4px 0",
                      fontSize: "13px",
                    }}
                  >
                    <span>{renderExpenseTitle(expense)}</span>
                    <span style={{ color: "#805AD5", fontWeight: 600 }}>
                      {expenseUtils.formatCurrency(getEffectiveAmount(expense))}
                    </span>
                  </div>
                ))}
              </div>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
            padding: "16px",
          }}
        >
          <Button
            onClick={() => setShowBulkDuplicateModal(false)}
            sx={{ color: isDarkMode ? "#a0aec0" : "#718096" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={executeBulkDuplicate}
            sx={{
              backgroundColor: "#805AD5",
              "&:hover": { backgroundColor: "#6B46C1" },
            }}
          >
            Duplicate to{" "}
            {format(
              new Date(bulkDuplicateYear, bulkDuplicateMonth - 1),
              "MMMM yyyy"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
            color: isDarkMode ? "#fff" : "#1a202c",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
            color: "#E53E3E",
          }}
        >
          Delete {selectedExpenses.length} Expense
          {selectedExpenses.length > 1 ? "s" : ""}?
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <div
              style={{
                fontSize: "14px",
                color: isDarkMode ? "#a0aec0" : "#4a5568",
              }}
            >
              This action cannot be undone. The following expenses will be
              permanently deleted:
            </div>
            <Box
              sx={{
                backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
                borderRadius: "8px",
                padding: "12px",
                border: `1px solid ${isDarkMode ? "#E53E3E40" : "#E53E3E30"}`,
              }}
            >
              <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                {selectedExpenses.map((expense) => (
                  <div
                    key={expense.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "6px 0",
                      fontSize: "13px",
                      borderBottom: isDarkMode
                        ? "1px solid #333"
                        : "1px solid #e2e8f0",
                    }}
                  >
                    <span>{renderExpenseTitle(expense)}</span>
                    <span style={{ color: "#E53E3E", fontWeight: 600 }}>
                      {expenseUtils.formatCurrency(getEffectiveAmount(expense))}
                    </span>
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: isDarkMode
                    ? "1px solid #444"
                    : "1px solid #cbd5e0",
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: 600,
                }}
              >
                <span>Total:</span>
                <span style={{ color: "#E53E3E" }}>
                  {expenseUtils.formatCurrency(
                    selectedExpenses.reduce(
                      (sum, exp) => sum + getEffectiveAmount(exp),
                      0
                    )
                  )}
                </span>
              </div>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
            padding: "16px",
          }}
        >
          <Button
            onClick={() => setShowBulkDeleteConfirm(false)}
            sx={{ color: isDarkMode ? "#a0aec0" : "#718096" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={executeBulkDelete}
            sx={{
              backgroundColor: "#E53E3E",
              "&:hover": { backgroundColor: "#C53030" },
            }}
          >
            Delete {selectedExpenses.length} Expense
            {selectedExpenses.length > 1 ? "s" : ""}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!mergeSeriesExpense}
        onClose={() => {
          setMergeSeriesExpense(null);
          setMergeSeriesQuery("");
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#121212" : "#fff",
            color: isDarkMode ? "#e2e8f0" : "#2d3748",
          },
        }}
      >
        {mergeSeriesExpense && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderBottom: `1px solid ${isDarkMode ? "#2d3748" : "#e2e8f0"}`,
              }}
            >
              <LinkIcon sx={{ color: isDarkMode ? "#90cdf4" : "#2B6CB0" }} />
              Merge Into Recurring Series
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? "#a0aec0" : "#718096" }}
              >
                {getDisplayName(mergeSeriesExpense)}
              </Typography>
              <Box sx={{ ml: "auto" }}>
                <Tooltip title="Close" arrow>
                  <IconButton
                    onClick={() => {
                      setMergeSeriesExpense(null);
                      setMergeSeriesQuery("");
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: isDarkMode ? "#a0aec0" : "#718096" }}
              >
                Search recurring expenses and attach this one-time item to that
                series.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="Search recurring expenses"
                value={mergeSeriesQuery}
                onChange={(event) => setMergeSeriesQuery(event.target.value)}
                placeholder="Search by name, vendor, category, or recurrence"
                sx={{ mb: 2 }}
              />
              <Box
                sx={{
                  border: `1px solid ${isDarkMode ? "#2d3748" : "#e2e8f0"}`,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <TableContainer
                  component={Paper}
                  sx={{ backgroundColor: "transparent", boxShadow: "none" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Pattern</TableCell>
                        <TableCell align="center">Merge</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRecurringSeriesCandidates.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              textAlign: "center",
                              color: isDarkMode ? "#a0aec0" : "#718096",
                              py: 3,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isDarkMode ? "#a0aec0" : "#718096",
                                }}
                              >
                                No recurring expenses matched your search.
                              </Typography>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  if (!mergeSeriesExpense) return;
                                  console.debug(
                                    "[ExpenseList] Create recurring series from merge modal",
                                    {
                                      expenseId: mergeSeriesExpense.id,
                                    }
                                  );
                                  setMergeSeriesExpense(null);
                                  setMergeSeriesQuery("");
                                  onEditExpense({
                                    ...mergeSeriesExpense,
                                    isRecurring: true,
                                    isRecurringInstance: false,
                                    originalId: undefined,
                                    recurrencePattern:
                                      mergeSeriesExpense.recurrencePattern ||
                                      "monthly",
                                  });
                                }}
                                sx={{
                                  backgroundColor: "#2B6CB0",
                                  "&:hover": {
                                    backgroundColor: "#2C5282",
                                  },
                                }}
                              >
                                Create One
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredRecurringSeriesCandidates.map((seriesRoot) => (
                          <TableRow key={seriesRoot.id}>
                            <TableCell>{getDisplayName(seriesRoot)}</TableCell>
                            <TableCell>
                              {seriesRoot.vendor || "No vendor"}
                            </TableCell>
                            <TableCell sx={{ textTransform: "capitalize" }}>
                              {seriesRoot.recurrencePattern || "Recurring"}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Merge Into This Series" arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      void handleMergeIntoSeries(seriesRoot)
                                    }
                                    disabled={
                                      mergeSeriesTargetId === seriesRoot.id
                                    }
                                    sx={{
                                      backgroundColor: "#2B6CB0",
                                      color: "white",
                                      width: 28,
                                      height: 28,
                                      "&:hover": {
                                        backgroundColor: "#2C5282",
                                      },
                                      "&.Mui-disabled": {
                                        backgroundColor: "#2B6CB0",
                                        color: "white",
                                        opacity: 0.6,
                                      },
                                    }}
                                  >
                                    {mergeSeriesTargetId === seriesRoot.id ? (
                                      <CircularProgress
                                        size={14}
                                        sx={{ color: "white" }}
                                      />
                                    ) : (
                                      <LinkIcon sx={{ fontSize: 16 }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                borderTop: `1px solid ${isDarkMode ? "#2d3748" : "#e2e8f0"}`,
                px: 3,
                py: 2,
              }}
            >
              <Button
                onClick={() => {
                  setMergeSeriesExpense(null);
                  setMergeSeriesQuery("");
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!occurrencesModalExpense}
        onClose={() => setOccurrencesModalExpense(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#121212" : "#fff",
            color: isDarkMode ? "#e2e8f0" : "#2d3748",
          },
        }}
      >
        {occurrencesModalExpense && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderBottom: `1px solid ${isDarkMode ? "#2d3748" : "#e2e8f0"}`,
              }}
            >
              <FormatListBulletedIcon
                sx={{ color: isDarkMode ? "#a78bfa" : "#4C51BF" }}
              />
              {getDisplayName(occurrencesModalExpense)} Occurrences
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? "#a0aec0" : "#718096",
                }}
              >
                {occurrenceSeries.length} total
              </Typography>
              <Box sx={{ ml: "auto" }}>
                <Tooltip title="Close" arrow>
                  <IconButton onClick={() => setOccurrencesModalExpense(null)}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              {occurrenceSeries.length === 0 ? (
                <Typography
                  sx={{ color: isDarkMode ? "#a0aec0" : "#718096", py: 2 }}
                >
                  No occurrences found.
                </Typography>
              ) : (
                <Box
                  sx={{
                    border: `1px solid ${isDarkMode ? "#2d3748" : "#e2e8f0"}`,
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <TableContainer
                    component={Paper}
                    sx={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Due</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Vendor</TableCell>
                          <TableCell>ID</TableCell>
                          {onStopRecurringAfterDate && (
                            <TableCell align="center">Actions</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {occurrenceSeries.map((occurrence) => {
                          const isPastDue =
                            !occurrence.isPaid &&
                            getDaysOverdue(occurrence.dueDate) > 0;
                          const hasFutureOccurrences =
                            getFutureExpenseSeriesOccurrences(
                              visibleExpensePool,
                              occurrence
                            ).length > 0;
                          const statusLabel = occurrence.isPaid
                            ? "Paid"
                            : occurrence.isSkipped
                              ? "Skipped"
                              : isPastDue
                                ? "Overdue"
                                : "Upcoming";

                          return (
                            <TableRow
                              key={occurrence.id}
                              sx={{
                                "& td": {
                                  borderColor: isDarkMode
                                    ? "#2d3748"
                                    : "#e2e8f0",
                                },
                              }}
                            >
                              <TableCell>
                                {occurrence.dueDate
                                  ? formatDueDate(new Date(occurrence.dueDate))
                                  : "No due date"}
                              </TableCell>
                              <TableCell>
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    px: 1,
                                    py: 0.375,
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    backgroundColor: occurrence.isPaid
                                      ? "#38A16920"
                                      : occurrence.isSkipped
                                        ? "#805AD520"
                                        : isPastDue
                                          ? "#E53E3E20"
                                          : "#3182CE20",
                                    color: occurrence.isPaid
                                      ? "#68D391"
                                      : occurrence.isSkipped
                                        ? "#B794F4"
                                        : isPastDue
                                          ? "#FC8181"
                                          : "#63B3ED",
                                  }}
                                >
                                  {statusLabel}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                {expenseUtils.formatCurrency(
                                  getEffectiveAmount(occurrence)
                                )}
                              </TableCell>
                              <TableCell>
                                {occurrence.vendor || "No vendor"}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, monospace",
                                  fontSize: 12,
                                  color: isDarkMode ? "#a0aec0" : "#718096",
                                }}
                              >
                                {occurrence.id}
                              </TableCell>
                              {onStopRecurringAfterDate && (
                                <TableCell align="center">
                                  {hasFutureOccurrences ? (
                                    <Tooltip title="Stop After This Date" arrow>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            void handleStopRecurringAfterDate(
                                              occurrence
                                            )
                                          }
                                          disabled={
                                            stopRecurringExpenseId ===
                                            occurrence.id
                                          }
                                          sx={{
                                            backgroundColor: "#DD6B20",
                                            color: "white",
                                            width: 28,
                                            height: 28,
                                            "&:hover": {
                                              backgroundColor: "#C05621",
                                            },
                                            "&.Mui-disabled": {
                                              backgroundColor: "#DD6B20",
                                              color: "white",
                                              opacity: 0.6,
                                            },
                                          }}
                                        >
                                          {stopRecurringExpenseId ===
                                          occurrence.id ? (
                                            <CircularProgress
                                              size={14}
                                              sx={{ color: "white" }}
                                            />
                                          ) : (
                                            <StopCircleIcon
                                              sx={{ fontSize: 16 }}
                                            />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: isDarkMode
                                          ? "#718096"
                                          : "#A0AEC0",
                                      }}
                                    >
                                      Final
                                    </Typography>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                borderTop: `1px solid ${isDarkMode ? "#2d3748" : "#e2e8f0"}`,
                px: 3,
                py: 2,
              }}
            >
              <Button onClick={() => setOccurrencesModalExpense(null)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Shopping List Modal */}
      <Dialog
        open={!!shoppingModalExpense}
        onClose={() => setShoppingModalExpense(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#121212" : "#fff",
            color: isDarkMode ? "#e2e8f0" : "#2d3748",
          },
        }}
      >
        {shoppingModalExpense &&
          (() => {
            const listId = shoppingModalExpense.linkedShoppingListId!;
            const items = shoppingListItemsCache[listId];
            const isLoading = shoppingListLoading.has(listId);
            const visibleItems = (items || [])
              .filter((item) => !shoppingItemHiddenIds.has(item.id))
              .filter((item) => (shoppingModalShowPaid ? true : !item.isPaid))
              .filter((item) =>
                shoppingModalShowSkipped ? true : !item.isSkipped
              );
            const total = visibleItems
              .filter((item) => !item.isSkipped)
              .reduce((sum, i) => sum + i.price, 0);

            const updateCachedItem = (itemId: string, patch: Partial<any>) => {
              setShoppingListItemsCache((prev) => {
                const list = prev[listId];
                if (!list) return prev;
                return {
                  ...prev,
                  [listId]: list.map((i) =>
                    i.id === itemId ? ({ ...i, ...patch } as any) : i
                  ),
                };
              });
            };

            const handleToggleItemPaid = async (item: ShoppingListItem) => {
              if (shoppingItemActionLoadingIds.has(item.id)) return;
              setShoppingItemActionLoadingIds((prev) =>
                new Set(prev).add(item.id)
              );
              try {
                const nextPaid = !item.isPaid;
                const now = new Date();
                const updated = await shoppingListAPI.updateItem(item.id, {
                  isPaid: nextPaid,
                  paymentDate: nextPaid ? now.toISOString() : null,
                  paymentAccount: nextPaid ? "Manual" : null,
                });
                updateCachedItem(item.id, updated);
              } catch (error) {
                console.error("[ShoppingListModal] Failed to toggle paid", {
                  itemId: item.id,
                  error,
                });
                alert("Failed to update item. Check console for details.");
              } finally {
                setShoppingItemActionLoadingIds((prev) => {
                  const next = new Set(prev);
                  next.delete(item.id);
                  return next;
                });
              }
            };

            const handleToggleItemSkipped = async (item: ShoppingListItem) => {
              if (shoppingItemActionLoadingIds.has(item.id)) return;
              setShoppingItemActionLoadingIds((prev) =>
                new Set(prev).add(item.id)
              );
              try {
                const updated = await shoppingListAPI.updateItem(item.id, {
                  isSkipped: !item.isSkipped,
                });
                updateCachedItem(item.id, updated);
              } catch (error) {
                console.error("[ShoppingListModal] Failed to toggle skipped", {
                  itemId: item.id,
                  error,
                });
                alert("Failed to update item. Check console for details.");
              } finally {
                setShoppingItemActionLoadingIds((prev) => {
                  const next = new Set(prev);
                  next.delete(item.id);
                  return next;
                });
              }
            };

            const handleDeleteItem = async (item: ShoppingListItem) => {
              if (shoppingItemActionLoadingIds.has(item.id)) return;
              setShoppingItemActionLoadingIds((prev) =>
                new Set(prev).add(item.id)
              );
              try {
                await shoppingListAPI.deleteItem(item.id);
                setShoppingItemDeleteAnimatingOutIds((prev) =>
                  new Set(prev).add(item.id)
                );
                window.setTimeout(() => {
                  setShoppingItemHiddenIds((prev) =>
                    new Set(prev).add(item.id)
                  );
                  setShoppingItemDeleteAnimatingOutIds((prev) => {
                    const next = new Set(prev);
                    next.delete(item.id);
                    return next;
                  });
                  setShoppingItemActionLoadingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(item.id);
                    return next;
                  });
                  setShoppingListItemsCache((prev) => {
                    const list = prev[listId];
                    if (!list) return prev;
                    return {
                      ...prev,
                      [listId]: list.filter((i) => i.id !== item.id),
                    };
                  });
                }, 220);
              } catch (error) {
                console.error("[ShoppingListModal] Failed to delete item", {
                  itemId: item.id,
                  error,
                });
                alert("Failed to delete item. Check console for details.");
                setShoppingItemActionLoadingIds((prev) => {
                  const next = new Set(prev);
                  next.delete(item.id);
                  return next;
                });
              }
            };

            const handleAddItem = async () => {
              if (!shoppingModalDraftItem.title.trim()) return;
              const parsedPrice = Number(shoppingModalDraftItem.price || 0);
              if (Number.isNaN(parsedPrice)) return;

              const draftKey = `draft-${listId}`;
              if (shoppingItemActionLoadingIds.has(draftKey)) return;
              setShoppingItemActionLoadingIds((prev) =>
                new Set(prev).add(draftKey)
              );
              try {
                const created = await shoppingListAPI.addItem(listId, {
                  title: shoppingModalDraftItem.title.trim(),
                  vendor: shoppingModalDraftItem.vendor.trim() || undefined,
                  link: shoppingModalDraftItem.link.trim() || undefined,
                  price: parsedPrice,
                });
                setShoppingListItemsCache((prev) => {
                  const list = prev[listId] || [];
                  return { ...prev, [listId]: [...list, created] };
                });
                setShoppingModalDraftItem({
                  title: "",
                  vendor: "",
                  link: "",
                  price: "",
                });
              } catch (error) {
                console.error("[ShoppingListModal] Failed to add item", {
                  error,
                });
                alert("Failed to add item. Check console for details.");
              } finally {
                setShoppingItemActionLoadingIds((prev) => {
                  const next = new Set(prev);
                  next.delete(draftKey);
                  return next;
                });
              }
            };
            return (
              <>
                <DialogTitle
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    borderBottom: `1px solid ${isDarkMode ? "#2d3748" : "#e2e8f0"}`,
                  }}
                >
                  <ShoppingCartIcon
                    sx={{ color: isDarkMode ? "#63b3ed" : "#3182CE" }}
                  />
                  {getDisplayName(shoppingModalExpense)}
                  <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
                    <Tooltip
                      title={
                        shoppingModalShowPaid ? "Showing Paid" : "Hide Paid"
                      }
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          setShoppingModalShowPaid((prev) => !prev)
                        }
                        sx={{
                          color: shoppingModalShowPaid
                            ? "white"
                            : isDarkMode
                              ? "#a0aec0"
                              : "#718096",
                          backgroundColor: shoppingModalShowPaid
                            ? "#38A169"
                            : "transparent",
                          border: `1px solid ${shoppingModalShowPaid ? "#38A169" : isDarkMode ? "#4a5568" : "#cbd5e0"}`,
                          "&:hover": {
                            backgroundColor: shoppingModalShowPaid
                              ? "#2F855A"
                              : isDarkMode
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.06)",
                          },
                        }}
                      >
                        {shoppingModalShowPaid ? (
                          <CheckCircleIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        shoppingModalShowSkipped
                          ? "Showing Skipped"
                          : "Hide Skipped"
                      }
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          setShoppingModalShowSkipped((prev) => !prev)
                        }
                        sx={{
                          color: shoppingModalShowSkipped
                            ? "white"
                            : isDarkMode
                              ? "#a0aec0"
                              : "#718096",
                          backgroundColor: shoppingModalShowSkipped
                            ? "#805AD5"
                            : "transparent",
                          border: `1px solid ${shoppingModalShowSkipped ? "#805AD5" : isDarkMode ? "#4a5568" : "#cbd5e0"}`,
                          "&:hover": {
                            backgroundColor: shoppingModalShowSkipped
                              ? "#6B46C1"
                              : isDarkMode
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.06)",
                          },
                        }}
                      >
                        <BlockIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Close" arrow>
                      <IconButton onClick={() => setShoppingModalExpense(null)}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "2fr 1fr 1fr 2fr auto",
                      },
                      gap: 1,
                      alignItems: "center",
                      mb: 2,
                      p: 1.25,
                      borderRadius: 1,
                      border: `1px solid ${isDarkMode ? "#2d3748" : "#e2e8f0"}`,
                      backgroundColor: isDarkMode
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.02)",
                    }}
                  >
                    <TextField
                      label="Item"
                      size="small"
                      value={shoppingModalDraftItem.title}
                      onChange={(e) =>
                        setShoppingModalDraftItem((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                    />
                    <TextField
                      label="Vendor"
                      size="small"
                      value={shoppingModalDraftItem.vendor}
                      onChange={(e) =>
                        setShoppingModalDraftItem((prev) => ({
                          ...prev,
                          vendor: e.target.value,
                        }))
                      }
                    />
                    <TextField
                      label="Price"
                      size="small"
                      value={shoppingModalDraftItem.price}
                      onChange={(e) =>
                        setShoppingModalDraftItem((prev) => ({
                          ...prev,
                          price: e.target.value,
                        }))
                      }
                    />
                    <TextField
                      label="Link"
                      size="small"
                      value={shoppingModalDraftItem.link}
                      onChange={(e) =>
                        setShoppingModalDraftItem((prev) => ({
                          ...prev,
                          link: e.target.value,
                        }))
                      }
                    />
                    <Tooltip title="Add Item" arrow>
                      <span>
                        <IconButton
                          onClick={() => void handleAddItem()}
                          disabled={shoppingItemActionLoadingIds.has(
                            `draft-${listId}`
                          )}
                          sx={{
                            backgroundColor: "#38A169",
                            color: "white",
                            width: 36,
                            height: 36,
                            "&:hover": { backgroundColor: "#2F855A" },
                            "&.Mui-disabled": {
                              opacity: 0.6,
                              color: "white",
                            },
                          }}
                        >
                          {shoppingItemActionLoadingIds.has(
                            `draft-${listId}`
                          ) ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: "white" }}
                            />
                          ) : (
                            <AddIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  {isLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <Typography color="text.secondary">
                        Loading items...
                      </Typography>
                    </Box>
                  ) : visibleItems.length > 0 ? (
                    <Box>
                      <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr
                            style={{
                              borderBottom: `2px solid ${isDarkMode ? "#4a5568" : "#cbd5e0"}`,
                            }}
                          >
                            <th
                              style={{
                                textAlign: "left",
                                padding: "10px 12px",
                                fontWeight: 600,
                                fontSize: "14px",
                                color: isDarkMode ? "#a0aec0" : "#4a5568",
                              }}
                            >
                              Status
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "10px 12px",
                                fontWeight: 600,
                                fontSize: "14px",
                                color: isDarkMode ? "#a0aec0" : "#4a5568",
                              }}
                            >
                              Item
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "10px 12px",
                                fontWeight: 600,
                                fontSize: "14px",
                                color: isDarkMode ? "#a0aec0" : "#4a5568",
                              }}
                            >
                              Vendor
                            </th>
                            <th
                              style={{
                                textAlign: "right",
                                padding: "10px 12px",
                                fontWeight: 600,
                                fontSize: "14px",
                                color: isDarkMode ? "#a0aec0" : "#4a5568",
                              }}
                            >
                              Price
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "10px 12px",
                                fontWeight: 600,
                                fontSize: "14px",
                                color: isDarkMode ? "#a0aec0" : "#4a5568",
                              }}
                            >
                              Requested
                            </th>
                            <th
                              style={{
                                textAlign: "center",
                                padding: "10px 12px",
                                fontWeight: 600,
                                fontSize: "14px",
                                color: isDarkMode ? "#a0aec0" : "#4a5568",
                              }}
                            >
                              Link
                            </th>
                            <th
                              style={{
                                textAlign: "right",
                                padding: "10px 12px",
                                fontWeight: 600,
                                fontSize: "14px",
                                color: isDarkMode ? "#a0aec0" : "#4a5568",
                              }}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleItems.map((item) => {
                            const isActing = shoppingItemActionLoadingIds.has(
                              item.id
                            );
                            const isDeleting =
                              shoppingItemDeleteAnimatingOutIds.has(item.id);
                            return (
                              <tr
                                key={item.id}
                                style={{
                                  borderBottom: `1px solid ${isDarkMode ? "#2d3748" : "#edf2f7"}`,
                                  transition:
                                    "background-color 0.15s, opacity 0.2s, transform 0.2s",
                                  opacity: isDeleting ? 0 : 1,
                                  transform: isDeleting
                                    ? "translateY(-10px)"
                                    : "translateY(0)",
                                  backgroundColor: isDeleting
                                    ? "#ffffff"
                                    : "transparent",
                                }}
                              >
                                <td
                                  style={{
                                    padding: "10px 12px",
                                    fontSize: "12px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      alignItems: "center",
                                    }}
                                  >
                                    {item.isPaid && (
                                      <span
                                        style={{
                                          backgroundColor: "#38A16920",
                                          color: "#38A169",
                                          padding: "2px 6px",
                                          borderRadius: "999px",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Paid
                                      </span>
                                    )}
                                    {item.isSkipped && (
                                      <span
                                        style={{
                                          backgroundColor: "#805AD520",
                                          color: "#805AD5",
                                          padding: "2px 6px",
                                          borderRadius: "999px",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Skipped
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    padding: "10px 12px",
                                    fontSize: "14px",
                                    textDecoration: item.isSkipped
                                      ? "line-through"
                                      : "none",
                                    opacity: item.isPaid ? 0.65 : 1,
                                  }}
                                >
                                  {item.title}
                                </td>
                                <td
                                  style={{
                                    padding: "10px 12px",
                                    fontSize: "14px",
                                    color: isDarkMode ? "#a0aec0" : "#718096",
                                    textDecoration: item.isSkipped
                                      ? "line-through"
                                      : "none",
                                    opacity: item.isPaid ? 0.65 : 1,
                                  }}
                                >
                                  {item.vendor || "-"}
                                </td>
                                <td
                                  style={{
                                    padding: "10px 12px",
                                    fontSize: "14px",
                                    textAlign: "right",
                                    textDecoration: item.isSkipped
                                      ? "line-through"
                                      : "none",
                                    opacity: item.isPaid ? 0.65 : 1,
                                  }}
                                >
                                  {expenseUtils.formatCurrency(item.price)}
                                </td>
                                <td
                                  style={{
                                    padding: "10px 12px",
                                    fontSize: "14px",
                                    color: isDarkMode ? "#a0aec0" : "#718096",
                                    textDecoration: item.isSkipped
                                      ? "line-through"
                                      : "none",
                                    opacity: item.isPaid ? 0.65 : 1,
                                  }}
                                >
                                  {item.dateRequested
                                    ? new Date(
                                        item.dateRequested
                                      ).toLocaleDateString("en-US", {
                                        timeZone: "UTC",
                                      })
                                    : "-"}
                                </td>
                                <td
                                  style={{
                                    padding: "10px 12px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.link ? (
                                    <a
                                      href={item.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: isDarkMode
                                          ? "#63b3ed"
                                          : "#3182CE",
                                      }}
                                    >
                                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                                    </a>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "10px 12px",
                                    textAlign: "right",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      gap: "6px",
                                    }}
                                  >
                                    <Tooltip
                                      title={item.isPaid ? "Unpaid" : "Paid"}
                                      arrow
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={isActing}
                                          onClick={() =>
                                            void handleToggleItemPaid(item)
                                          }
                                          sx={{
                                            backgroundColor: "#38A169",
                                            color: "white",
                                            width: 28,
                                            height: 28,
                                            "&:hover": {
                                              backgroundColor: "#2F855A",
                                            },
                                            "&.Mui-disabled": {
                                              opacity: 0.6,
                                              color: "white",
                                            },
                                          }}
                                        >
                                          {isActing ? (
                                            <CircularProgress
                                              size={14}
                                              sx={{ color: "white" }}
                                            />
                                          ) : (
                                            <CheckCircleIcon
                                              sx={{ fontSize: 16 }}
                                            />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip
                                      title={item.isSkipped ? "Unskip" : "Skip"}
                                      arrow
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={isActing}
                                          onClick={() =>
                                            void handleToggleItemSkipped(item)
                                          }
                                          sx={{
                                            backgroundColor: "#805AD5",
                                            color: "white",
                                            width: 28,
                                            height: 28,
                                            "&:hover": {
                                              backgroundColor: "#6B46C1",
                                            },
                                            "&.Mui-disabled": {
                                              opacity: 0.6,
                                              color: "white",
                                            },
                                          }}
                                        >
                                          {isActing ? (
                                            <CircularProgress
                                              size={14}
                                              sx={{ color: "white" }}
                                            />
                                          ) : (
                                            <BlockIcon sx={{ fontSize: 16 }} />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Delete" arrow>
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={isActing}
                                          onClick={() =>
                                            void handleDeleteItem(item)
                                          }
                                          sx={{
                                            backgroundColor: "#E53E3E",
                                            color: "white",
                                            width: 28,
                                            height: 28,
                                            "&:hover": {
                                              backgroundColor: "#C53030",
                                            },
                                            "&.Mui-disabled": {
                                              opacity: 0.6,
                                              color: "white",
                                            },
                                          }}
                                        >
                                          {isActing ? (
                                            <CircularProgress
                                              size={14}
                                              sx={{ color: "white" }}
                                            />
                                          ) : (
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr
                            style={{
                              borderTop: `2px solid ${isDarkMode ? "#4a5568" : "#cbd5e0"}`,
                            }}
                          >
                            <td
                              style={{
                                padding: "10px 12px",
                                fontWeight: 700,
                                fontSize: "14px",
                              }}
                            >
                              Total ({visibleItems.length} item
                              {visibleItems.length !== 1 ? "s" : ""})
                            </td>
                            <td />
                            <td
                              style={{
                                padding: "10px 12px",
                                fontWeight: 700,
                                fontSize: "14px",
                                textAlign: "right",
                              }}
                            >
                              {expenseUtils.formatCurrency(total)}
                            </td>
                            <td />
                            <td />
                            <td />
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </Box>
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No items in this shopping list.
                    </Typography>
                  )}
                </DialogContent>
              </>
            );
          })()}
      </Dialog>

      <LinkTransactionDialog
        open={Boolean(linkTxnExpense)}
        expense={linkTxnExpense}
        onConfirm={async (transactionId, txn) => {
          const expense = linkTxnExpense;
          if (expense && onLinkTransaction) {
            await onLinkTransaction(expense, transactionId);

            // Compare the linked transaction amount to the expense amount:
            // exact match -> auto mark paid; transaction less than the
            // amount -> ask the user before marking paid.
            if (transactionId && txn && !expense.isPaid) {
              const expenseAmount = getEffectiveAmount(expense);
              const txnAmount = Math.abs(txn.amount);
              const diff = Math.round((expenseAmount - txnAmount) * 100) / 100;
              console.log("[ExpenseList] Linked txn amount check", {
                expenseId: expense.id,
                expenseAmount,
                txnAmount,
                diff,
              });
              if (diff === 0) {
                console.log(
                  "[ExpenseList] Exact amount match — auto-marking paid",
                  { expenseId: expense.id, transactionId }
                );
                await finalizeMarkPaid(
                  expense,
                  txn.date
                    ? new Date(`${txn.date.slice(0, 10)}T12:00:00`)
                    : new Date(),
                  paymentAccount || "TPC Chase",
                  transactionId
                );
              } else if (diff > 0) {
                setLinkPaidPrompt({
                  expense,
                  transactionId,
                  txnAmount,
                  txnDate: txn.date,
                  diff,
                });
              }
            }
          }
          setLinkTxnExpense(null);
        }}
        onCancel={() => setLinkTxnExpense(null)}
      />

      {/* Confirm marking paid when the linked transaction is less than the
          expense amount. */}
      <Dialog
        open={Boolean(linkPaidPrompt)}
        onClose={() => setLinkPaidPrompt(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mark as Paid?</DialogTitle>
        <DialogContent>
          {linkPaidPrompt && (
            <Typography variant="body2">
              The linked transaction (
              {expenseUtils.formatCurrency(linkPaidPrompt.txnAmount)}) is{" "}
              {expenseUtils.formatCurrency(linkPaidPrompt.diff)} less than this
              expense's amount (
              {expenseUtils.formatCurrency(
                getEffectiveAmount(linkPaidPrompt.expense)
              )}
              ). Do you want to mark "{getDisplayName(linkPaidPrompt.expense)}"
              as paid anyway?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLinkPaidPrompt(null)}>No</Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#38A169",
              "&:hover": { backgroundColor: "#2F855A" },
            }}
            onClick={async () => {
              const prompt = linkPaidPrompt;
              setLinkPaidPrompt(null);
              if (!prompt) return;
              console.log(
                "[ExpenseList] User confirmed mark-paid for partial link",
                {
                  expenseId: prompt.expense.id,
                  transactionId: prompt.transactionId,
                  diff: prompt.diff,
                }
              );
              await finalizeMarkPaid(
                prompt.expense,
                prompt.txnDate
                  ? new Date(`${prompt.txnDate.slice(0, 10)}T12:00:00`)
                  : new Date(),
                paymentAccount || "TPC Chase",
                prompt.transactionId
              );
            }}
          >
            Mark as Paid
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(postponeAnchor)}
        anchorEl={postponeAnchor}
        onClose={() => setPostponeAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 1.5, width: 240 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Postpone
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            label="Until"
            InputLabelProps={{ shrink: true }}
            value={postponeDate}
            onChange={(e) => setPostponeDate(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Stack spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={!postponeDate}
              onClick={() => applyPostpone(postponeDate)}
            >
              Postpone until date
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => applyPostpone(null)}
            >
              Postpone indefinitely
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={() => applyPostpone("clear")}
            >
              Un-postpone
            </Button>
          </Stack>
        </Box>
      </Popover>
    </div>
  );
};

export default ExpenseList;
